<script setup>
import { ref, onMounted, computed, watch, onUnmounted, nextTick } from 'vue';
import axios from '@/axiosConfig';
import eventBus from './eventBus.js';
import imageCompression from 'browser-image-compression';
import { marked } from 'marked';
// Add PhotoCache utility
import PhotoCache from '@/utils/PhotoCache';
import {
  buildUnpaidLeasesList,
  getCurrentCyclePeriod,
  getPaymentCycleDate,
  isCountableCyclePayment,
  isDateInCycle,
  isPaymentInCycle,
} from '@/utils/unpaidLeases.js';
import { filterStorageLeases } from '@/utils/storageLeases.js';
import MotdModal from './MotdModal.vue'; // Import MOTD modal component
import PropertySensors from './PropertySensors.vue';
import ServiceContacts from './ServiceContacts.vue';
import ShopInventory from './ShopInventory.vue';
import { useRoute } from 'vue-router';
import { shopInventoryViewFromQuery } from '@/utils/shopInventory';

const propertySummary = ref([]);
const payments = ref([]);
const isLoading = ref(true);
const role = ref(null);

// New variables for property list popups
const showExpiringLeasesPopup = ref(false);
const showResignedLeasesPopup = ref(false);
const showVacantPropertiesPopup = ref(false);
const showCurrentPaymentsPopup = ref(false);
const showEndingLeasesPopup = ref(false); // New state for the "Ending Leases" popup
const expiringLeasesList = ref([]);
const resignedLeasesList = ref([]);
const vacantPropertiesList = ref([]);
const currentPaymentsList = ref([]);
const unpaidLeasesList = ref([]); // New list for unpaid leases
const bouncedPaymentLeases = ref([]); // List for leases with bounced payments in current cycle
const endingLeasesList = ref([]); // New list for the "Ending Leases" popup
const isUploading = ref(false); // New variable to track upload state
const activePaymentsTab = ref('paid'); // Track which tab is active in the payments popup

// Search variables for popups
const popupSearchQuery = ref('');

// Variables for share functionality
const showShareToast = ref(false);
const shareToastMessage = ref('');
const showShareErrorDialog = ref(false);
const shareErrorMessage = ref('');
const shareUrlText = ref(''); // Add this variable to store the share URL for display

// Add variables for lease selection functionality
const selectedLeases = ref([]);
const selectedLeasesTotal = ref(0);
const selectedLeasesBalance = ref(0); // Add variable for total balances

// Unpaid-lease SMS compose dialog (desktop double-tap / mobile long-press)
const showUnpaidSmsDialog = ref(false);
const unpaidSmsLease = ref(null);
const unpaidSmsPhone = ref('');
const unpaidSmsMessage = ref('');
const unpaidSmsError = ref('');
const unpaidSmsLedgerLoading = ref(false);
let unpaidLeaseClickTimeout = null;
let lastUnpaidLeaseTap = { property: null, time: 0 };
const UNPAID_LONG_PRESS_MS = 550;
let unpaidLongPressTimer = null;
let unpaidLongPressMoved = false;
let unpaidSuppressClickAfterLongPress = false;
let unpaidTouchStartPos = null;

// Add error dialog state variables
const showUploadErrorDialog = ref(false);
const uploadErrorMessage = ref('');
const uploadErrorDetails = ref('');

// Photo carousel data
const properties = ref([]);
const filteredProperties = ref([]);
const searchQuery = ref('');
const selectedProperty = ref(null);
const photos = ref([]);
const coverageStats = ref(null);
const isLoadingPhotos = ref(false);
const isEditingPhotos = ref(false);
const photoDialog = ref(false);
const photoFiles = ref([]); // Changed from photoFile to photoFiles array
const photoCaption = ref('');
const photoPreviewUrls = ref([]); // Changed from photoPreviewUrl to photoPreviewUrls array
const viewPhotoDialog = ref(false);
const selectedPhoto = ref(null);
const editCaptionDialog = ref(false);
const editingCaption = ref('');
const confirmDialog = ref(false);
const confirmAction = ref(null);
const confirmMessage = ref('');
const itemToDelete = ref(null);

// New carousel data
const recentPhotos = ref([]);
const isLoadingRecentPhotos = ref(false);
const currentCarouselIndex = ref(0);
const searchIsFocused = ref(false);
const carouselAutoAdvanceTimer = ref(null); // Timer for auto-advancing carousel

// Stats collapse state
const statsCollapsed = ref(false);

// Add variable for deposit sum
const currentCycleDeposits = ref(0);
// Add variable for expected rent
const expectedRent = ref(0);
// Add variable for vacant rent loss
const vacantRentLoss = ref(0);

// Variables for photo zoom functionality
const photoScale = ref(1);
const photoTranslateX = ref(0);
const photoTranslateY = ref(0);
const initialTouchDistance = ref(0);
const initialScale = ref(1);
const lastTouchX = ref(0);
const lastTouchY = ref(0);
const isPanning = ref(false);
const isZooming = ref(false);

// ===== TRASH TRACKING STATE =====
const trashProperties = ref([]);
const trashFilterQuery = ref('');
const trashLoading = ref(false);
const trashCollapsedGroups = ref(new Set());
const trashFocusedCard = ref(null);
const trashShowAlertPopup = ref(false);
const trashAlertPropertyId = ref(null);
const trashAlertAddress = ref('');
const trashAlertMessage = ref('');
const trashAlertSending = ref(false);
const trashAlertResults = ref(null);
const trashAlertFallback = ref(false);
const trashAlertRecipients = ref([]);
const trashAlertRecipientsLoading = ref(false);
const trashAlertRecipientsError = ref('');
const trashAlertDeliveryNote = ref('');
const trashAlertProperty = ref(null);
const trashAlertType = ref('');
const trashAlertTargetBin = ref(null);
const trashSaving = ref(new Set()); // track which properties are saving
const trashStatusFilter = ref(null); // null, 'blank', or 'done'
const trashAlertStandardFooter =
  'Big Property Management<br>[Chat with us](https://chatbot.bigtapapp.com)';
const trashPickupAlertMessage =
  'You now have new trash service through Big Property Management (Arrowaste). <br><br>Please contact the [City of Grand Rapids](https://www.grandrapidsmi.gov/departments/public-works/waste-services/) to have your old trash bin picked up and service discontinued.<br><br>Big Property Management<br>[Chat with us](https://chatbot.bigtapapp.com)';

const trashAlertRenderedPreview = computed(() => {
  const source = String(trashAlertMessage.value || '').trim();
  if (!source) {
    return '<p><em>Preview will appear here.</em></p>';
  }

  return marked.parse(source, {
    gfm: true,
    breaks: true,
  });
});

// Trash computed: filter + group
const trashDayFilterMap = {
  mon: 'pickup_mon',
  tue: 'pickup_tue',
  wed: 'pickup_wed',
  thu: 'pickup_thu',
  fri: 'pickup_fri',
  sat: 'pickup_sat',
  sun: 'pickup_sun',
};

const trashHasDayFilter = computed(() => {
  const q = trashFilterQuery.value.toLowerCase().trim();
  return Object.keys(trashDayFilterMap).some((d) => q.includes(':' + d));
});

const trashHasTextFilter = computed(() => {
  const q = trashFilterQuery.value.toLowerCase().trim();
  if (!q) return false;
  const tokens = q.split(/\s+/);
  return tokens.some((t) => !t.match(/^:\w+$/));
});

const trashFilteredProperties = computed(() => {
  const hasQuery = trashFilterQuery.value.trim();
  const hasStatusFilter = trashStatusFilter.value !== null;

  if (!hasQuery && !hasStatusFilter) return trashProperties.value;

  const q = (trashFilterQuery.value || '').toLowerCase().trim();

  // Extract :day filters
  const dayFilters = [];
  const textParts = [];
  if (hasQuery) {
    for (const token of q.split(/\s+/)) {
      const dayMatch = token.match(/^:(\w+)$/);
      if (dayMatch && trashDayFilterMap[dayMatch[1]]) {
        dayFilters.push(trashDayFilterMap[dayMatch[1]]);
      } else {
        textParts.push(token);
      }
    }
  }

  // Helper: does a property have data defined?
  const hasData = (prop) =>
    (prop.bins && prop.bins.length > 0) ||
    (prop.trash && (prop.trash.provider || prop.trash.container_type));

  return trashProperties.value.filter((p) => {
    // Check status filter (blank/done)
    if (trashStatusFilter.value === 'blank') {
      if (hasData(p)) return false;
    } else if (trashStatusFilter.value === 'done') {
      if (!hasData(p)) return false;
    }

    // Check day filters — check bins first, then fall back to trash
    if (dayFilters.length > 0) {
      const hasDayInBins = (dayCol) => p.bins && p.bins.some((b) => b[dayCol]);
      for (const dayCol of dayFilters) {
        if (!hasDayInBins(dayCol) && (!p.trash || !p.trash[dayCol]))
          return false;
      }
    }

    // Check text filter
    if (textParts.length > 0) {
      const binText = (p.bins || [])
        .map((b) => (b.provider || '') + ' ' + (b.bin_number || ''))
        .join(' ');
      const haystack = (
        p.address +
        ' ' +
        p.tenants.map((t) => t.name).join(' ') +
        ' ' +
        (p.trash?.provider || '') +
        ' ' +
        (p.trash?.bin_number || '') +
        ' ' +
        binText
      ).toLowerCase();
      const segments = textParts
        .join(' ')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return segments.some((seg) => {
        const tokens = seg.split(/\s+/);
        return tokens.every((tok) => haystack.includes(tok));
      });
    }

    return true;
  });
});

const trashGroupedProperties = computed(() => {
  const filtered = trashFilteredProperties.value;
  const all = trashProperties.value;
  const hasText = trashHasTextFilter.value;
  const groups = [];
  const parentMap = {};

  // Pass 1: find parents
  for (const p of filtered) {
    if (p.isParentAddr === 1 && (p.parentId === p.propertyId || !p.parentId)) {
      parentMap[p.propertyId] = { parent: p, children: [] };
      groups.push(parentMap[p.propertyId]);
    }
  }

  // Pass 2: attach children
  for (const p of filtered) {
    if (parentMap[p.propertyId]) continue; // already a parent
    const pid = p.parentId;
    if (pid && parentMap[pid]) {
      if (!parentMap[pid].children.find((x) => x.propertyId === p.propertyId))
        parentMap[pid].children.push(p);
    } else if (pid) {
      // Parent not in filtered set, get from all
      const parentProp = all.find(
        (a) =>
          a.propertyId === pid &&
          a.isParentAddr === 1 &&
          (a.parentId === a.propertyId || !a.parentId),
      );
      if (parentProp) {
        parentMap[pid] = { parent: parentProp, children: [p] };
        groups.push(parentMap[pid]);
        // Only populate all siblings when there's no text search
        if (!hasText) {
          for (const c of all) {
            if (
              c.parentId === pid &&
              c.propertyId !== pid &&
              !parentMap[pid].children.find(
                (x) => x.propertyId === c.propertyId,
              )
            ) {
              parentMap[pid].children.push(c);
            }
          }
        }
      } else {
        groups.push({ parent: p, children: [] });
      }
    } else {
      groups.push({ parent: p, children: [] });
    }
  }

  return groups;
});

const trashReportGroups = computed(() => {
  const all = [...trashProperties.value];
  const groupsByParent = new Map();

  for (const p of all) {
    if (p.isParentAddr === 1 && (p.parentId === p.propertyId || !p.parentId)) {
      groupsByParent.set(p.propertyId, { parent: p, children: [] });
    }
  }

  for (const p of all) {
    const pid = p.parentId;
    if (pid && groupsByParent.has(pid) && p.propertyId !== pid) {
      groupsByParent.get(pid).children.push(p);
      continue;
    }

    const isSelfParent =
      p.isParentAddr === 1 && (p.parentId === p.propertyId || !p.parentId);
    if (!isSelfParent && !groupsByParent.has(p.propertyId)) {
      groupsByParent.set(p.propertyId, { parent: p, children: [] });
    }
  }

  const groups = Array.from(groupsByParent.values());
  groups.sort((a, b) =>
    (a.parent.address || '').localeCompare(b.parent.address || ''),
  );
  for (const group of groups) {
    group.children.sort((a, b) =>
      (a.address || '').localeCompare(b.address || ''),
    );
  }
  return groups;
});

function trashEscapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function trashReportPickupDays(bin) {
  const days = [];
  if (bin.pickup_mon) days.push('Mon');
  if (bin.pickup_tue) days.push('Tue');
  if (bin.pickup_wed) days.push('Wed');
  if (bin.pickup_thu) days.push('Thu');
  if (bin.pickup_fri) days.push('Fri');
  if (bin.pickup_sat) days.push('Sat');
  if (bin.pickup_sun) days.push('Sun');
  return days.length ? days.join(', ') : 'None';
}

function trashReportBinSummary(bin) {
  const parts = [];
  if (bin.provider) parts.push(bin.provider);
  if (bin.bin_number) parts.push(`#${bin.bin_number}`);
  if (bin.container_type) parts.push(bin.container_type);
  if (bin.fee != null && bin.fee !== '') {
    const feeNum = Number(bin.fee);
    if (!Number.isNaN(feeNum)) {
      parts.push(`$${feeNum.toFixed(2)}`);
    }
  }
  parts.push(`Pickup: ${trashReportPickupDays(bin)}`);
  if (bin.notes) parts.push(`Notes: ${bin.notes}`);
  return parts.join(' | ');
}

function trashPrintReport() {
  const groups = trashReportGroups.value;
  if (!groups.length) {
    window.alert('No trash data available to print.');
    return;
  }

  const generatedAt = new Date().toLocaleString();
  const rowsHtml = groups
    .map((group) => {
      return [group.parent, ...group.children]
        .map((property) => {
          const isParent = property.propertyId === group.parent.propertyId;
          const apartmentLabel = isParent
            ? '(Parent)'
            : property.address || 'Unknown';

          const bins = Array.isArray(property.bins) ? property.bins : [];
          const binsHtml = bins.length
            ? `<ul class="tr-print-bin-list">${bins
                .map(
                  (bin) =>
                    `<li>${trashEscapeHtml(trashReportBinSummary(bin))}</li>`,
                )
                .join('')}</ul>`
            : '<span class="tr-print-empty">No bins assigned</span>';

          const rawEnd = property.leaseEndsOn;
          let leaseEndDisplay = '';
          if (rawEnd) {
            const d = new Date(rawEnd);
            if (!isNaN(d.getTime())) {
              const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
              const dd = String(d.getUTCDate()).padStart(2, '0');
              const yy = String(d.getUTCFullYear()).slice(-2);
              leaseEndDisplay = `${mm}/${dd}/${yy}`;
            }
          }

          return `
            <tr>
              <td>${trashEscapeHtml(group.parent.address || 'Unknown')}</td>
              <td>${trashEscapeHtml(apartmentLabel)}</td>
              <td>${trashEscapeHtml(leaseEndDisplay)}</td>
              <td>${binsHtml}</td>
            </tr>
          `;
        })
        .join('');
    })
    .join('');

  const printHtml = `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Trash Assignment Report</title>
        <style>
          @page { size: Letter portrait; margin: 0.55in; }
          body {
            font-family: Arial, sans-serif;
            color: #222;
            margin: 0;
            font-size: 12px;
            line-height: 1.35;
          }
          h1 {
            margin: 0 0 4px;
            font-size: 18px;
          }
          .tr-print-meta {
            margin-bottom: 12px;
            color: #555;
            font-size: 11px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 6px;
            vertical-align: top;
            text-align: left;
          }
          th {
            background: #f3f3f3;
            font-weight: 700;
          }
          .tr-print-bin-list {
            margin: 0;
            padding-left: 16px;
          }
          .tr-print-empty {
            color: #777;
            font-style: italic;
          }
        </style>
      </head>
      <body>
        <h1>Trash Assignment Report</h1>
        <div class="tr-print-meta">Generated: ${trashEscapeHtml(generatedAt)}</div>
        <table>
          <thead>
            <tr>
              <th>Parent</th>
              <th>Apartment</th>
              <th>Lease End</th>
              <th>Assigned Bins</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const printWindow = window.open('', '_blank', 'width=960,height=720');
  if (!printWindow) {
    window.alert('Please allow pop-ups to print the report.');
    return;
  }
  printWindow.document.open();
  printWindow.document.write(printHtml);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 200);
}

async function loadTrashData() {
  trashLoading.value = true;
  try {
    const { data } = await axios.get('/api/trash-tracking/properties', {
      params: { _t: Date.now() },
    });
    trashProperties.value = data;
    // Collapse all groups by default on first load
    if (trashCollapsedGroups.value.size === 0) {
      const collapsed = new Set();
      for (const p of data) {
        if (
          p.isParentAddr === 1 &&
          (p.parentId === p.propertyId || !p.parentId)
        ) {
          collapsed.add(p.propertyId);
        }
      }
      trashCollapsedGroups.value = collapsed;
    }

    // Collapse all bin lists by default on first load
    if (trashBinCollapsed.value.size === 0) {
      const collapsedBins = new Set();
      for (const p of data) {
        if ((p.bins || []).length > 0) {
          collapsedBins.add(p.propertyId);
        }
      }
      trashBinCollapsed.value = collapsedBins;
    }
  } catch (error) {
    console.error('Error loading trash data:', error);
  } finally {
    trashLoading.value = false;
  }
}

function trashToggleCollapse(parentId) {
  const s = new Set(trashCollapsedGroups.value);
  if (s.has(parentId)) s.delete(parentId);
  else s.add(parentId);
  trashCollapsedGroups.value = s;
}

function trashIsCollapsed(parentId) {
  return trashCollapsedGroups.value.has(parentId);
}

function trashCollapseAll() {
  const allParentIds = trashGroupedProperties.value.map(
    (g) => g.parent.propertyId,
  );
  const allCollapsed = allParentIds.every((id) =>
    trashCollapsedGroups.value.has(id),
  );
  if (allCollapsed) {
    trashCollapsedGroups.value = new Set();
  } else {
    trashCollapsedGroups.value = new Set(allParentIds);
  }
}

function trashToggleFocus(propertyId) {
  trashFocusedCard.value =
    trashFocusedCard.value === propertyId ? null : propertyId;
}

function trashIsFocused(propertyId) {
  return trashFocusedCard.value === propertyId;
}

function trashCardCount(group) {
  let count = 0;
  const all = [group.parent, ...group.children];
  for (const p of all) {
    count += (p.bins || []).length;
  }
  return count > 0 ? `(${count})` : '';
}

function getTrashVal(property, field) {
  return property.trash ? property.trash[field] : null;
}

function getTrashFee(property) {
  if (property.trash && property.trash.fee != null) return property.trash.fee;
  return 25;
}

function getTrashPickup(property, day) {
  if (!property.trash) return false;
  return !!property.trash['pickup_' + day];
}

let trashSaveTimers = {};
function trashScheduleSave(property) {
  const pid = property.propertyId;
  if (trashSaveTimers[pid]) clearTimeout(trashSaveTimers[pid]);
  trashSaveTimers[pid] = setTimeout(() => saveTrashData(property), 600);
}

async function saveTrashData(property) {
  const pid = property.propertyId;
  trashSaving.value = new Set([...trashSaving.value, pid]);
  try {
    const t = property.trash || {};
    await axios.put(`/api/trash-tracking/${pid}`, {
      provider: t.provider || null,
      container_type: t.container_type || null,
      container_qty: t.container_qty != null ? t.container_qty : 1,
      fee: t.fee != null ? t.fee : 25,
      pickup_mon: t.pickup_mon ? 1 : 0,
      pickup_tue: t.pickup_tue ? 1 : 0,
      pickup_wed: t.pickup_wed ? 1 : 0,
      pickup_thu: t.pickup_thu ? 1 : 0,
      pickup_fri: t.pickup_fri ? 1 : 0,
      pickup_sat: t.pickup_sat ? 1 : 0,
      pickup_sun: t.pickup_sun ? 1 : 0,
      bin_number: t.bin_number || null,
      notes: t.notes || null,
      shared: t.shared ? 1 : 0,
    });
  } catch (error) {
    console.error('Error saving trash data:', error);
  } finally {
    const s = new Set(trashSaving.value);
    s.delete(pid);
    trashSaving.value = s;
  }
}

function trashSetProvider(property, value) {
  if (!property.trash) property.trash = {};
  property.trash.provider = value;
  trashScheduleSave(property);
}

function trashSetFee(property, value) {
  if (!property.trash) property.trash = {};
  property.trash.fee = parseFloat(value) || 0;
  trashScheduleSave(property);
}

function trashSetType(property, value) {
  if (!property.trash) property.trash = {};
  property.trash.container_type = value;
  trashScheduleSave(property);
}

function getTrashQty(property) {
  if (property.trash && property.trash.container_qty != null)
    return property.trash.container_qty;
  return 1;
}

function trashSetQty(property, value) {
  if (!property.trash) property.trash = {};
  property.trash.container_qty = parseInt(value) || 1;
  trashScheduleSave(property);
}

function trashToggleShared(property) {
  if (!property.trash) property.trash = {};
  property.trash.shared = property.trash.shared ? 0 : 1;
  trashScheduleSave(property);
}

function trashToggleDay(property, day) {
  if (!property.trash) property.trash = {};
  const key = 'pickup_' + day;
  property.trash[key] = property.trash[key] ? 0 : 1;
  trashScheduleSave(property);
}

function trashSetNotes(property, value) {
  if (!property.trash) property.trash = {};
  property.trash.notes = value;
  trashScheduleSave(property);
}

function trashSetBinNumber(property, value) {
  if (!property.trash) property.trash = {};
  property.trash.bin_number = value;
  trashScheduleSave(property);
}

function trashModifiedText(property) {
  if (!property.trash || !property.trash.modifiedByName) return null;
  const d = new Date(property.trash.updated_at);
  const mo = d.getMonth() + 1;
  const day = d.getDate();
  const yr = String(d.getFullYear()).slice(2);
  let hr = d.getHours();
  const ampm = hr >= 12 ? 'pm' : 'am';
  hr = hr % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `modified by: ${property.trash.modifiedByName} on ${mo}/${day}/${yr} at ${hr}:${min}${ampm}`;
}

void getTrashFee;
void getTrashPickup;
void trashSetProvider;
void trashSetFee;
void trashSetType;
void getTrashQty;
void trashSetQty;
void trashToggleDay;
void trashSetNotes;
void trashSetBinNumber;
void trashModifiedText;

// ===== TRASH BIN CRUD =====
const trashBinCollapsed = ref(new Set());
const trashHoveredBinPropertyId = ref(null);

function trashFilteredBins(property) {
  const bins = property.bins || [];
  const q = trashFilterQuery.value.toLowerCase().trim();
  if (!q) return bins;
  // Extract only text tokens (ignore :day filters)
  const textParts = q.split(/\s+/).filter((t) => !t.match(/^:\w+$/));
  if (!textParts.length) return bins;
  const searchText = textParts.join(' ');
  const segments = searchText
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const matched = bins.filter((b) => {
    const haystack = (
      (b.provider || '') +
      ' ' +
      (b.bin_number || '') +
      ' ' +
      (b.container_type || '') +
      ' ' +
      (b.notes || '')
    ).toLowerCase();
    return segments.some((seg) => {
      const tokens = seg.split(/\s+/);
      return tokens.every((tok) => haystack.includes(tok));
    });
  });
  // If no bins matched the text, return all (the property matched on address/tenant)
  return matched.length > 0 ? matched : bins;
}

function trashHasBinMatch(property) {
  const bins = property.bins || [];
  if (!bins.length) return false;
  const filtered = trashFilteredBins(property);
  return filtered.length < bins.length || trashHasTextFilter.value;
}

function trashBinsCollapsed(propertyId) {
  return trashBinCollapsed.value.has(propertyId);
}

function trashToggleBinCollapse(propertyId) {
  const s = new Set(trashBinCollapsed.value);
  if (s.has(propertyId)) s.delete(propertyId);
  else s.add(propertyId);
  trashBinCollapsed.value = s;
}

function trashExpandBins(propertyId) {
  const s = new Set(trashBinCollapsed.value);
  s.delete(propertyId);
  trashBinCollapsed.value = s;
}

function trashCollapseBins(propertyId) {
  const s = new Set(trashBinCollapsed.value);
  s.add(propertyId);
  trashBinCollapsed.value = s;
}

function trashSetHoveredBinProperty(propertyId) {
  trashHoveredBinPropertyId.value = propertyId;
}

function trashClearHoveredBinProperty(propertyId) {
  if (trashHoveredBinPropertyId.value === propertyId) {
    trashHoveredBinPropertyId.value = null;
  }
}

function trashHasInteractiveFocus() {
  const el = document.activeElement;
  if (!el || el === document.body) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    tag === 'BUTTON' ||
    el.isContentEditable
  );
}

function trashGetPropertyBinCount(propertyId) {
  const property = trashProperties.value.find(
    (p) => p.propertyId === propertyId,
  );
  return property?.bins?.length || 0;
}

function trashHandleKeydown(event) {
  if (activeView.value !== 'trash') return;
  if (trashShowAlertPopup.value) return;
  if (trashHasInteractiveFocus()) return;

  const isExpand =
    event.key === 'ArrowRight' || event.code === 'ArrowRight';
  const isCollapse = event.key === 'ArrowLeft' || event.code === 'ArrowLeft';
  if (!isExpand && !isCollapse) return;

  const propertyId = trashHoveredBinPropertyId.value;
  if (!propertyId || trashGetPropertyBinCount(propertyId) === 0) return;

  event.preventDefault();
  if (isExpand) trashExpandBins(propertyId);
  else trashCollapseBins(propertyId);
}

async function trashAddBin(property) {
  try {
    const { data } = await axios.post(
      `/api/trash-tracking/${property.propertyId}/bins`,
    );
    if (!property.bins) property.bins = [];
    property.bins.push({
      id: data.id,
      propertyId: property.propertyId,
      provider: null,
      container_type: null,
      fee: 0,
      pickup_mon: 0,
      pickup_tue: 0,
      pickup_wed: 0,
      pickup_thu: 0,
      pickup_fri: 0,
      pickup_sat: 0,
      pickup_sun: 0,
      bin_number: null,
      notes: null,
      modified_by: null,
      modifiedByName: null,
    });
  } catch (error) {
    console.error('Error adding trash bin:', error);
  }
}

async function trashDeleteBin(property, binId) {
  try {
    await axios.delete(`/api/trash-tracking/bins/${binId}`);
    property.bins = property.bins.filter((b) => b.id !== binId);
  } catch (error) {
    console.error('Error deleting trash bin:', error);
  }
}

let trashBinSaveTimers = {};
function trashBinScheduleSave(bin) {
  const bid = bin.id;
  if (trashBinSaveTimers[bid]) clearTimeout(trashBinSaveTimers[bid]);
  trashBinSaveTimers[bid] = setTimeout(() => saveTrashBin(bin), 600);
}

async function saveTrashBin(bin) {
  trashSaving.value = new Set([...trashSaving.value, bin.propertyId]);
  try {
    await axios.put(`/api/trash-tracking/bins/${bin.id}`, {
      provider: bin.provider || null,
      container_type: bin.container_type || null,
      fee: bin.fee != null ? bin.fee : trashDefaultBinFee(bin.provider),
      pickup_mon: bin.pickup_mon ? 1 : 0,
      pickup_tue: bin.pickup_tue ? 1 : 0,
      pickup_wed: bin.pickup_wed ? 1 : 0,
      pickup_thu: bin.pickup_thu ? 1 : 0,
      pickup_fri: bin.pickup_fri ? 1 : 0,
      pickup_sat: bin.pickup_sat ? 1 : 0,
      pickup_sun: bin.pickup_sun ? 1 : 0,
      bin_number: bin.bin_number || null,
      notes: bin.notes || null,
    });
  } catch (error) {
    console.error('Error saving trash bin:', error);
  } finally {
    const s = new Set(trashSaving.value);
    s.delete(bin.propertyId);
    trashSaving.value = s;
  }
}

function trashDefaultBinFee(provider) {
  return provider === 'Arrowaste' ? 25 : 0;
}

function trashBinSetField(bin, field, value) {
  const priorProvider = bin.provider;
  bin[field] = value;
  if (field === 'provider') {
    const priorDefault = trashDefaultBinFee(priorProvider);
    const currentFee = bin.fee == null ? null : Number(bin.fee);
    if (currentFee == null || currentFee === priorDefault) {
      bin.fee = trashDefaultBinFee(value);
    }
  }
  trashBinScheduleSave(bin);
}

function trashBinToggleDay(bin, day) {
  const key = 'pickup_' + day;
  bin[key] = bin[key] ? 0 : 1;
  trashBinScheduleSave(bin);
}

function trashBinModifiedText(bin) {
  if (!bin.modifiedByName) return null;
  const d = new Date(bin.updated_at);
  const mo = d.getMonth() + 1;
  const day = d.getDate();
  const yr = String(d.getFullYear()).slice(2);
  let hr = d.getHours();
  const ampm = hr >= 12 ? 'pm' : 'am';
  hr = hr % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `modified by: ${bin.modifiedByName} on ${mo}/${day}/${yr} at ${hr}:${min}${ampm}`;
}

async function refreshTrashAlertRecipients() {
  if (!trashAlertPropertyId.value) {
    trashAlertRecipients.value = [];
    trashAlertFallback.value = false;
    trashAlertDeliveryNote.value = '';
    return;
  }

  trashAlertRecipientsLoading.value = true;
  trashAlertRecipientsError.value = '';

  try {
    const { data } = await axios.get('/api/trash-tracking/alert-recipients', {
      params: {
        propertyId: trashAlertPropertyId.value,
        alertType: trashAlertType.value || '',
      },
    });

    trashAlertRecipients.value = data.recipients || [];
    trashAlertFallback.value = !!data.fallback;
    trashAlertDeliveryNote.value = data.deliveryNote || '';
  } catch (error) {
    console.error('Error loading trash alert recipients:', error);
    trashAlertRecipients.value = [];
    trashAlertFallback.value = false;
    trashAlertDeliveryNote.value = '';
    trashAlertRecipientsError.value = 'Unable to load recipient list';
  } finally {
    trashAlertRecipientsLoading.value = false;
  }
}

function trashOpenAlert(property) {
  trashAlertPropertyId.value = property.propertyId;
  trashAlertAddress.value = property.address;
  trashAlertProperty.value = property;
  trashAlertType.value = '';
  trashAlertTargetBin.value = null;
  trashAlertMessage.value = '';
  trashAlertResults.value = null;
  trashAlertFallback.value = false;
  trashAlertRecipients.value = [];
  trashAlertRecipientsError.value = '';
  trashAlertDeliveryNote.value = '';
  trashAlertSending.value = false;
  trashShowAlertPopup.value = true;
  refreshTrashAlertRecipients();
}

function trashPickupDayLabel(row) {
  if (!row) return 'TBD';
  const dayMap = [
    ['pickup_mon', 'Monday'],
    ['pickup_tue', 'Tuesday'],
    ['pickup_wed', 'Wednesday'],
    ['pickup_thu', 'Thursday'],
    ['pickup_fri', 'Friday'],
    ['pickup_sat', 'Saturday'],
    ['pickup_sun', 'Sunday'],
  ];

  const matchedDays = dayMap
    .filter(([key]) => !!row[key])
    .map(([, label]) => label);

  if (!matchedDays.length) return 'TBD';
  return matchedDays.join(', ');
}

function trashMostRecentlyUpdatedRow(property) {
  if (!property) return null;

  const bins = Array.isArray(property.bins) ? property.bins : [];
  if (bins.length > 0) {
    return [...bins].sort((left, right) => {
      const leftTime = new Date(left.updated_at || 0).getTime();
      const rightTime = new Date(right.updated_at || 0).getTime();
      return rightTime - leftTime;
    })[0];
  }

  return property.trash || null;
}

function trashAppendStandardFooter(message) {
  const baseMessage = String(message || '').trim();
  if (!baseMessage) return '';
  if (baseMessage.includes(trashAlertStandardFooter)) return baseMessage;
  return `${baseMessage}\n\n${trashAlertStandardFooter}`;
}

function trashComposeLabeledBinMessage(property = trashAlertProperty.value, bin = null) {
  const targetProperty = property || trashAlertProperty.value;
  if (!targetProperty) return;

  if (!trashShowAlertPopup.value || trashAlertPropertyId.value !== targetProperty.propertyId) {
    trashOpenAlert(targetProperty);
  }

  const latestRow = bin || trashMostRecentlyUpdatedRow(targetProperty);
  trashAlertType.value = 'new-bin';
  trashAlertTargetBin.value = latestRow && latestRow.id ? latestRow : null;

  const vendor = String(latestRow?.provider || '').trim();
  const vendorLabel = vendor ? `${vendor} trash bin` : 'trash bin';
  const pickupDay = trashPickupDayLabel(latestRow);
  const binNumber = String(latestRow?.bin_number || '').trim() || 'TBD';

  const message =
    `Your new ${vendorLabel} is now labelled for your apartment. ` +
    `Your pickup day is **${pickupDay}**. ` +
    `Your bin number is: ${binNumber}. ` +
    'Please bring your cart to the curb the night before.';

  trashAlertMessage.value = trashAppendStandardFooter(message);
  refreshTrashAlertRecipients();
}

function trashOpenPickupAlert(property, bin) {
  const binNumber = String(bin?.bin_number || '').trim() || 'TBD';
  trashOpenAlert(property);
  trashAlertType.value = 'pickup';
  trashAlertTargetBin.value = bin && bin.id ? bin : null;
  trashAlertMessage.value = trashPickupAlertMessage.replace(
    'service discontinued.',
    `service discontinued.<br>Your bin number is: ${binNumber}`,
  );
  refreshTrashAlertRecipients();
}

function trashOpenPublicWorksAlert(property, bin) {
  const address = String(property?.address || '').trim() || 'address not provided';
  const binNumber = String(bin?.bin_number || '').trim() || 'TBD';
  trashOpenAlert(property);
  trashAlertType.value = 'public-works';
  trashAlertTargetBin.value = null;
  trashAlertMessage.value =
    '**City trash bin pickup needed**<br><br>' +
    `Address: ${address}, bin number ${binNumber}<br><br>` +
    'This bin was left behind by a tenant who has moved out.  Please have this bin picked up.<br><br>' +
    'Remaining balance, if any, can be invoiced to Big Property Management, 315 Richard Ter SE, Grand Rapids MI 49506. <br><br>' +
    'Reply to this email if there are any issues.<br><br>' +
    '**Big Property Management**';
  refreshTrashAlertRecipients();
}

function trashNoticeDateLabel() {
  return new Date().toLocaleDateString('en-US');
}

async function trashAppendAlertNoticeToNotes() {
  const bin = trashAlertTargetBin.value;
  if (!bin || !bin.id) return;

  let prefix = '';
  if (trashAlertType.value === 'pickup') {
    prefix = 'pickup notice sent';
  } else if (trashAlertType.value === 'new-bin') {
    prefix = 'new bin notice sent';
  }

  if (!prefix) return;

  const noticeLine = `${prefix} ${trashNoticeDateLabel()}`;
  const existingNotes = String(bin.notes || '').trim();
  bin.notes = existingNotes ? `${existingNotes}\n${noticeLine}` : noticeLine;

  await saveTrashBin(bin);
}

async function trashSendAlert() {
  if (!trashAlertMessage.value.trim()) return;
  trashAlertSending.value = true;
  try {
    const messageWithFooter = trashAppendStandardFooter(
      trashAlertMessage.value,
    );
    trashAlertMessage.value = messageWithFooter;

    const { data } = await axios.post('/api/trash-tracking/alert', {
      propertyId: trashAlertPropertyId.value,
      message: messageWithFooter,
      alertType: trashAlertType.value,
    });
    trashAlertResults.value = data.results;
    trashAlertFallback.value = !!data.fallback;

    const hasSuccessfulSend = Array.isArray(data.results)
      ? data.results.some((result) => result.success)
      : false;

    if (hasSuccessfulSend) {
      await trashAppendAlertNoticeToNotes();
    }
  } catch (error) {
    console.error('Error sending trash alert:', error);
    trashAlertResults.value = [
      { success: false, error: error.message || 'Failed to send' },
    ];
  } finally {
    trashAlertSending.value = false;
  }
}

function trashCloseAlert() {
  trashShowAlertPopup.value = false;
  trashAlertProperty.value = null;
  trashAlertType.value = '';
  trashAlertTargetBin.value = null;
  trashAlertResults.value = null;
  trashAlertFallback.value = false;
  trashAlertRecipients.value = [];
  trashAlertRecipientsError.value = '';
  trashAlertDeliveryNote.value = '';
}

onMounted(async () => {
  eventBus.on('open-unpaid-leases-popup', handleOpenUnpaidLeasesPopup);
  document.addEventListener('keydown', trashHandleKeydown);

  try {
    // Set the document title
    document.title = 'Properties';

    // Parse URL parameters for direct photo opening
    const urlParams = new URLSearchParams(window.location.search);
    const photoIdParam = urlParams.get('photo');
    const directPhotoId = photoIdParam ? parseInt(photoIdParam) : null;

    // Get user role
    const roleResults = await axios.get('/api/user-role');
    role.value = roleResults.data[0]?.role;

    // Only fetch property summary data if user has appropriate role
    if (role.value < 3) {
      // Fetch property summary data
      const propertySummaryResponse = await axios.get(
        '/api/property-lease-summary',
      );
      propertySummary.value = propertySummaryResponse.data;

      // Get current date in America/New_York timezone
      const options = { timeZone: 'America/New_York' };
      const nyTime = new Date().toLocaleString('en-US', options);
      const nyDate = new Date(nyTime);

      // Calculate date range for API request - include data for both current and previous cycles
      const currentMonth = nyDate.getMonth();
      const currentYear = nyDate.getFullYear();

      // Determine start date based on whether we're before or after the 25th
      // We want to go back 2 months if before 25th, or 1 month if after 25th
      // This ensures we get data for the previous cycle regardless of current date
      const startDate = new Date(currentYear, currentMonth - 2, 25);
      const endDate = new Date(nyDate);

      // Format dates for API request
      const startDateStr = formatDateForInput(startDate);
      const endDateStr = formatDateForInput(endDate);

      // console.log(`Fetching payments from ${startDateStr} to ${endDateStr}`);

      // Fetch payments data
      const paymentsResponse = await axios.get('/api/payments', {
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
        },
      });
      payments.value = paymentsResponse.data;
      // console.log(`Loaded ${payments.value.length} payment records`);

      // Fetch deposit data for current cycle
      const depositsResponse = await axios.get('/api/current-cycle-deposits');
      currentCycleDeposits.value = depositsResponse.data.totalDeposits;
      // console.log(
      //   `Total deposits for current cycle: ${currentCycleDeposits.value}`,
      // );

      // Fetch expected rent data
      const expectedRentResponse = await axios.get('/api/expected-rent');
      expectedRent.value = expectedRentResponse.data.totalExpectedRent;
      // console.log(`Total expected rent: ${expectedRent.value}`);

      // Fetch vacant rent loss data
      const vacantRentLossResponse = await axios.get('/api/vacant-rent-loss');
      vacantRentLoss.value = vacantRentLossResponse.data.vacantRentLoss;
      // console.log(`Vacant rent loss: ${vacantRentLoss.value}`);

      // Fetch property photo coverage stats
      await fetchCoverageStats();
    }

    // Fetch properties data
    await fetchProperties();

    // Fetch recent property photos for carousel (all users can see this)
    await fetchRecentPhotos();

    // Start the auto-advance timer for the carousel
    startCarouselAutoAdvance();

    // If a specific photo ID was in the URL, open it directly
    if (directPhotoId) {
      // Check if the photo is in recently loaded photos
      const recentPhoto = recentPhotos.value.find(
        (photo) => photo.id === directPhotoId,
      );

      if (recentPhoto) {
        // Open the photo directly
        openPhotoDialog(recentPhoto);
      } else {
        // Try to fetch the specific photo if not in recent photos
        try {
          // First get the photo metadata
          const photoMetaResponse = await axios.get(
            `/api/property-photo/${directPhotoId}`,
          );
          if (photoMetaResponse.data) {
            const photoData = photoMetaResponse.data;

            // Then fetch the actual image
            const imageResponse = await axios.get(
              `/api/properties/${photoData.propertyId}/photos/${photoData.id}/file`,
              { responseType: 'blob' },
            );

            // Create a blob URL for the image
            const photoBlob = imageResponse.data;
            const photoUrl = URL.createObjectURL(photoBlob);

            // Prepare the photo object
            const photo = {
              ...photoData,
              photoUrl: photoUrl,
            };

            // Open the photo dialog
            openPhotoDialog(photo);
          }
        } catch (err) {
          console.error(`Error fetching specific photo ${directPhotoId}:`, err);
        }
      }
    }

    isLoading.value = false;
  } catch (error) {
    console.error('Error loading data:', error);
    isLoading.value = false;
  }

  // Log user location
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        axios.post('/api/log-location', {
          componentName: 'MPropertySummary',
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        axios.post('/api/log-location', {
          componentName: 'MPropertySummary',
          locationError: error.message,
        });
      },
    );
  }
});

// Helper function to format dates for API calls
function formatDateForInput(date) {
  const adjustedDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000,
  );
  return adjustedDate.toISOString().split('T')[0];
}

// Format date to local format (mm/dd/yy)
function formatDate(dateString) {
  if (dateString === null || dateString === undefined) {
    return '--/--/--';
  }
  const options = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/New_York', // Eastern Time (automatically handles EST/EDT)
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
}

// Format number as percentage
function formatPercent(value, total) {
  if (!total) return '(0%)';
  return '(' + Math.round((value / total) * 100) + '%)';
}

// Format currency
function formatCurrency(amount) {
  if (!amount) return '$0';
  return (
    '$' +
    Math.round(Number(amount))
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  );
}

// Computed properties for statistics
const statistics = computed(() => {
  if (isLoading.value || !propertySummary.value.length) return {};

  const now = new Date();
  const nowTime = now.getTime();
  const thirtyDaysFromNow = new Date(now);
  thirtyDaysFromNow.setDate(now.getDate() + 30);
  const thirtyDaysFromNowTime = thirtyDaysFromNow.getTime();
  const sixtyDaysFromNow = new Date(now);
  sixtyDaysFromNow.setDate(now.getDate() + 60);
  const sixtyDaysFromNowTime = sixtyDaysFromNow.getTime();

  // Format the dates
  const thirtyDaysDate = `${String(thirtyDaysFromNow.getMonth() + 1).padStart(2, '0')}/${String(thirtyDaysFromNow.getDate()).padStart(2, '0')}`;
  const sixtyDaysDate = `${String(sixtyDaysFromNow.getMonth() + 1).padStart(2, '0')}/${String(sixtyDaysFromNow.getDate()).padStart(2, '0')}`;

  // Calculate current date in Eastern Time for payment calculations
  const options = { timeZone: 'America/New_York' };
  const nyTime = new Date().toLocaleString('en-US', options);
  const nyDate = new Date(nyTime);

  // Calculate payment date ranges based on the day of month
  const currentDay = nyDate.getDate();
  const currentMonth = nyDate.getMonth();
  const currentYear = nyDate.getFullYear();

  let prevPeriodStart, prevPeriodEnd, currentPeriodStart, currentPeriodEnd;

  // If today is the 25th or later in the month, current cycle starts on 25th of current month
  // Otherwise current cycle starts on 25th of previous month
  if (currentDay >= 25) {
    // Current cycle: 25th of current month to now
    currentPeriodStart = new Date(currentYear, currentMonth, 25);
    currentPeriodEnd = new Date(currentYear, currentMonth + 1, 24);

    // Previous cycle: 25th of previous month to 24th of current month
    prevPeriodStart = new Date(currentYear, currentMonth - 1, 25);
    prevPeriodEnd = new Date(currentYear, currentMonth, 24);
  } else {
    // Current cycle: 25th of previous month to now
    currentPeriodStart = new Date(currentYear, currentMonth - 1, 25);
    currentPeriodEnd = new Date(currentYear, currentMonth, 24);

    // Previous cycle: 25th of two months ago to 24th of previous month
    prevPeriodStart = new Date(currentYear, currentMonth - 2, 25);
    prevPeriodEnd = new Date(currentYear, currentMonth - 1, 24);
  }

  // Total properties
  const totalProperties = propertySummary.value.length;

  // Occupied properties: startsOn <= now <= endsOn
  const occupied = propertySummary.value.filter((property) => {
    const startsOn = new Date(property.startsOn).getTime();
    const endsOn = new Date(property.endsOn).getTime();
    return startsOn <= nowTime && nowTime <= endsOn;
  }).length;

  // Vacant properties: now < startsOn || now > endsOn
  const vacant = totalProperties - occupied;

  // Fix: Use payments array for payment calculations instead of propertySummary
  // Properties paid between 25th of prev period and 24th of current/prev month
  const paidInPrevPeriod = payments.value.filter(
    (payment) =>
      isCountableCyclePayment(payment) &&
      isDateInCycle(
        getPaymentCycleDate(payment),
        prevPeriodStart,
        prevPeriodEnd,
        prevPeriodEnd,
      ),
  );

  // Count unique addresses/properties that made payments in previous period
  const uniquePrevPeriodAddresses = new Set(
    paidInPrevPeriod.map((payment) => payment.address),
  );
  const uniquePrevPeriodPaymentCount = uniquePrevPeriodAddresses.size;

  // Sum of payments in previous period - use amount directly from payment records
  const paidInPrevPeriodSum = paidInPrevPeriod.reduce((sum, payment) => {
    return sum + parseFloat(payment.amount || 0);
  }, 0);

  // Properties paid from 25th of current period start until now
  const paidInCurrentPeriod = payments.value.filter(
    (payment) =>
      isCountableCyclePayment(payment) &&
      isPaymentInCycle(
        payment,
        currentPeriodStart,
        currentPeriodEnd,
        nyDate,
      ),
  );

  // Count unique addresses/properties that made payments in current period
  const uniqueCurrentPeriodAddresses = new Set(
    paidInCurrentPeriod.map((payment) => payment.address),
  );
  const uniqueCurrentPeriodPaymentCount = uniqueCurrentPeriodAddresses.size;

  // Sum of payments in current period - use amount directly from payment records
  const paidInCurrentPeriodSum = paidInCurrentPeriod.reduce((sum, payment) => {
    return sum + parseFloat(payment.amount || 0);
  }, 0);

  // Leases ending within 30 days
  const leasesEndingIn30Days = propertySummary.value.filter((property) => {
    const endsOn = new Date(property.endsOn).getTime();
    return endsOn > nowTime && endsOn <= thirtyDaysFromNowTime;
  });

  // Leases ending within 31-60 days
  const leasesEndingIn60Days = propertySummary.value.filter((property) => {
    const endsOn = new Date(property.endsOn).getTime();
    return endsOn > thirtyDaysFromNowTime && endsOn <= sixtyDaysFromNowTime;
  });

  // All leases ending within 60 days
  const leasesEndingSoon = [...leasesEndingIn30Days, ...leasesEndingIn60Days];

  // Leases renewing: ending within 60 days AND futureStartsOn is NULL or < now
  const renewingLeases = leasesEndingSoon.filter((property) => {
    return (
      !property.futureStartsOn ||
      new Date(property.futureStartsOn).getTime() < nowTime
    );
  });

  // Count leases renewing in 30 days
  const renewingLeasesIn30Days = leasesEndingIn30Days.filter((property) => {
    return (
      !property.futureStartsOn ||
      new Date(property.futureStartsOn).getTime() < nowTime
    );
  });

  // Count leases renewing in 60 days (31-60 day period)
  const renewingLeasesIn60Days = leasesEndingIn60Days.filter((property) => {
    return (
      !property.futureStartsOn ||
      new Date(property.futureStartsOn).getTime() < nowTime
    );
  });

  // Re-signed leases: futureStartsOn > now and is not null
  const reSignedLeases = propertySummary.value.filter((property) => {
    return (
      property.futureStartsOn &&
      new Date(property.futureStartsOn).getTime() > nowTime
    );
  });

  return {
    totalProperties,
    occupied,
    occupiedPercent: formatPercent(occupied, totalProperties),
    vacant,
    vacantPercent: formatPercent(vacant, totalProperties),
    paidInPrevPeriod: uniquePrevPeriodPaymentCount,
    paidInPrevPeriodPercent: formatPercent(
      uniquePrevPeriodPaymentCount,
      totalProperties,
    ),
    paidInPrevPeriodSum: formatCurrency(paidInPrevPeriodSum),
    paidInCurrentPeriod: uniqueCurrentPeriodPaymentCount,
    paidInCurrentPeriodPercent: formatPercent(
      uniqueCurrentPeriodPaymentCount,
      totalProperties,
    ),
    paidInCurrentPeriodSum: formatCurrency(paidInCurrentPeriodSum),
    leasesEndingSoon: leasesEndingSoon.length,
    leasesEndingSoonPercent: formatPercent(
      leasesEndingSoon.length,
      totalProperties,
    ),
    renewingLeases: renewingLeases.length,
    renewingLeasesPercent: formatPercent(
      renewingLeases.length,
      totalProperties,
    ),
    renewingLeasesIn30Days: renewingLeasesIn30Days.length,
    renewingLeasesIn60Days: renewingLeasesIn60Days.length,
    thirtyDaysDate,
    sixtyDaysDate,
    reSignedLeases: reSignedLeases.length,
    reSignedLeasesPercent: formatPercent(
      reSignedLeases.length,
      totalProperties,
    ),
  };
});

const isMobile = computed(() => window.innerWidth <= 500);
const sendClose = () => eventBus.emit('requestCloseProperties');

// Menu navigation. NavBar passes startView so Tools can open a section directly.
const props = defineProps({
  startView: { type: String, default: 'menu' },
});
const route = useRoute();
const activeView = ref(
  shopInventoryViewFromQuery(route.query) || props.startView || 'menu',
);
watch(
  () => props.startView,
  (view) => {
    if (shopInventoryViewFromQuery(route.query)) return;
    if (view) activeView.value = view;
  },
);
watch(
  () => route.query.view,
  () => {
    const fromLink = shopInventoryViewFromQuery(route.query);
    if (fromLink) activeView.value = fromLink;
  },
);
const propertySensorsRef = ref(null);
const serviceContactsRef = ref(null);
const shopInventoryRef = ref(null);
function goBackToMenu() {
  activeView.value = 'menu';
}
function refreshSensors() {
  propertySensorsRef.value?.reload();
}
function refreshServiceContacts() {
  serviceContactsRef.value?.reload();
}
function addServiceContact() {
  serviceContactsRef.value?.openNew();
}
function refreshShopInventory() {
  shopInventoryRef.value?.reload();
}
function addShopItem() {
  shopInventoryRef.value?.openNew();
}

// Photo carousel methods
// Search and filtering methods
function filterProperties() {
  if (!searchQuery.value) {
    filteredProperties.value = [];
    return;
  }

  // Extract tokens from the search query
  const tokens = searchQuery.value
    .replace(/,/g, ' ')
    .split(' ')
    .map((token) => token.trim())
    .filter((token) => token);

  filteredProperties.value = properties.value.filter((property) => {
    // Apply progressive token matching to the property address
    const fullText = property.propertyAddress.toLowerCase();
    let remainingText = fullText;

    // Check each token sequentially
    for (const token of tokens) {
      const index = remainingText.indexOf(token.toLowerCase());
      if (index === -1) {
        // This token doesn't match the remaining text
        return false;
      }

      // "Remove" the matched part and everything before it from the remaining text
      remainingText = remainingText.substring(index + token.length);
    }

    return true;
  });
}

function clearSearch() {
  searchQuery.value = '';
  selectedProperty.value = null;
  filteredProperties.value = [];
}

// Format address to use "Apt" instead of "#"
function formatAddress(address) {
  if (!address) return '';
  // Replace "#123" pattern with "Apt 123"
  return address.replace(/#(\d+)/g, 'Apt $1');
}

// Term highlighting
function highlightMatch(text, queryInput) {
  if (queryInput) {
    const tokens = queryInput.split(',').map((token) => token.trim());
    const query = tokens.length === 1 ? tokens[0] : tokens[tokens.length - 1];
    const escapedQuery = query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    if (text) {
      return text.replace(regex, '<span class="highlight">$1</span>');
    }
  }
  return text;
}

// Data fetching methods
async function fetchProperties() {
  isLoadingPhotos.value = true;
  try {
    const response = await axios.get('/api/properties-with-photos');
    properties.value = response.data;
    isLoadingPhotos.value = false;
  } catch (error) {
    console.error('Error fetching properties:', error);
    isLoadingPhotos.value = false;
  }
}

async function fetchRecentPhotos() {
  isLoadingRecentPhotos.value = true;
  try {
    // First, check if we have cached photos in localStorage metadata
    if (PhotoCache.hasCachedPhotos()) {
      const cachedPhotoMetadata = PhotoCache.getCachedPhotoMetadata();

      if (cachedPhotoMetadata.length > 0) {
        // console.log('Loading photos from cache');
        // Load cached photos first to avoid network requests
        const loadedPhotos = [];

        for (const photo of cachedPhotoMetadata) {
          try {
            // First try to get the photo blob from IndexedDB cache
            const cachedBlob = await PhotoCache.getPhotoBlob(photo.id);

            if (cachedBlob) {
              // Create a blob URL from the cached blob
              const photoUrl = URL.createObjectURL(cachedBlob);
              // console.log(`Loaded photo ${photo.id} from cache`);

              // Add to loaded photos array
              loadedPhotos.push({
                ...photo,
                photoUrl: photoUrl,
                fromCache: true,
              });
            } else {
              // console.log(`Photo ${photo.id} not found in blob cache`);
              // We'll fetch this from server later if needed
            }
          } catch (err) {
            console.error(`Error loading cached photo ${photo.id}:`, err);
          }
        }

        // Set loaded photos to the view immediately
        if (loadedPhotos.length > 0) {
          recentPhotos.value = loadedPhotos;
        }
      }
    }

    // Fetch photo IDs from server to check for deleted photos and updates
    // console.log('Fetching photo IDs from server to check for deletions');
    const photoIdsResponse = await axios.get('/api/recent-property-photos/ids');
    const serverPhotoIds = photoIdsResponse.data.map((p) => p.id);

    // Check if any cached photos were deleted on the server
    if (PhotoCache.hasCachedPhotos()) {
      const cachedPhotoMetadata = PhotoCache.getCachedPhotoMetadata();
      const cachedPhotoIds = cachedPhotoMetadata.map((p) => p.id);

      // Find photos that are in cache but no longer on server (deleted)
      const deletedPhotoIds = cachedPhotoIds.filter(
        (id) => !serverPhotoIds.includes(id),
      );

      // Remove deleted photos from cache
      if (deletedPhotoIds.length > 0) {
        // console.log( `Found ${deletedPhotoIds.length} photos that were deleted on server, pruning from cache`,);

        // Remove each deleted photo from the cache
        for (const photoId of deletedPhotoIds) {
          await PhotoCache.removePhoto(photoId);

          // Also remove from current view if present
          const photoIndex = recentPhotos.value.findIndex(
            (p) => p.id === photoId,
          );
          if (photoIndex !== -1) {
            // Revoke the object URL before removing
            if (recentPhotos.value[photoIndex].photoUrl) {
              URL.revokeObjectURL(recentPhotos.value[photoIndex].photoUrl);
            }
            recentPhotos.value.splice(photoIndex, 1);
            // console.log(`Removed deleted photo ${photoId} from current view`);
          }
        }
      }

      // Check if cache needs updating for other reasons
      const shouldRefreshCache =
        await checkIfCacheNeedsUpdate(cachedPhotoMetadata);

      // If cache is up to date and we loaded all remaining photos, finish early
      if (
        !shouldRefreshCache &&
        recentPhotos.value.length === serverPhotoIds.length
      ) {
        // console.log('Photo cache is complete and up to date after pruning');
        isLoadingRecentPhotos.value = false;
        return;
      }

      // console.log('Continuing to fetch from server after pruning');
    }

    // Cache is outdated, missing, incomplete, or had deletions - fetch fresh data from server
    // console.log('Fetching photos from server');
    const response = await axios.get('/api/recent-property-photos', {
      params: { limit: PhotoCache.MAX_CACHED_PHOTOS },
    });

    // For each photo, check if we already have it in the view from cache
    // If not, fetch the image data
    const photoPromises = response.data.map(async (photoMeta) => {
      // Check if this photo is already loaded from cache
      const existingPhoto = recentPhotos.value.find(
        (p) => p.id === photoMeta.id,
      );
      if (existingPhoto && existingPhoto.photoUrl) {
        // We already have this photo loaded from cache
        return existingPhoto;
      }

      try {
        // Need to fetch this photo from server
        const imageResponse = await axios.get(
          `/api/properties/${photoMeta.propertyId}/photos/${photoMeta.id}/file`,
          { responseType: 'blob' },
        );

        // Create an object URL from the blob
        const photoBlob = imageResponse.data;
        const photoUrl = URL.createObjectURL(photoBlob);

        // Return the photo with the blob URL
        return {
          id: photoMeta.id,
          propertyId: photoMeta.propertyId,
          caption: photoMeta.caption,
          photoFile: photoMeta.photoFile,
          modifiedOn: photoMeta.modifiedOn,
          propertyAddress: photoMeta.propertyAddress,
          photoUrl: photoUrl,
        };
      } catch (err) {
        console.error(`Error fetching photo ${photoMeta.id}:`, err);
        // Return the photo without a URL if fetching fails
        return {
          id: photoMeta.id,
          propertyId: photoMeta.propertyId,
          caption: photoMeta.caption,
          photoFile: photoMeta.photoFile,
          modifiedOn: photoMeta.modifiedOn,
          propertyAddress: photoMeta.propertyAddress,
          photoUrl: null,
        };
      }
    });

    const newPhotos = await Promise.all(photoPromises);
    recentPhotos.value = newPhotos;

    // Update the cache with the new photos
    await PhotoCache.cachePhotos(newPhotos);
    // console.log('Updated photo cache with server data');
  } catch (error) {
    console.error('Error in photo loading process:', error);
    // If we have cached photos but server request failed, keep showing cached photos
    if (recentPhotos.value.length === 0) {
      recentPhotos.value = [];
    }
  } finally {
    isLoadingRecentPhotos.value = false;
  }
}

// Helper function to check if cache needs updating
async function checkIfCacheNeedsUpdate(cachedPhotoMetadata) {
  try {
    // Make a lightweight request to get the most recent photo timestamp
    // without downloading all the photos
    const response = await axios.get('/api/recent-photos-timestamp');

    if (response.data && response.data.latestTimestamp) {
      const serverLatestDate = new Date(
        response.data.latestTimestamp,
      ).getTime();

      // Get the most recent timestamp from cached photos
      const mostRecentCacheDate = Math.max(
        ...cachedPhotoMetadata.map((p) => new Date(p.modifiedOn).getTime()),
      );

      // If server has newer photos than our cache, we should update
      return serverLatestDate > mostRecentCacheDate;
    }

    // If we can't determine, assume we need to update
    return true;
  } catch (error) {
    console.error('Error checking for cache updates:', error);
    // If request fails, try to work with what we have cached
    return false;
  }
}

async function fetchCoverageStats() {
  try {
    const response = await axios.get('/api/property-photo-coverage');
    if (response.data && response.data.stats) {
      coverageStats.value = response.data.stats;
    }
  } catch (error) {
    console.error('Error fetching property coverage stats:', error);
  }
}

async function fetchPhotosForProperty(propertyId) {
  isLoadingPhotos.value = true;
  try {
    // Step 1: Get the list of photos from the API
    const response = await axios.get(`/api/properties/${propertyId}/photos`);

    // Step 2: For each photo, fetch the actual image data as a blob
    const photoPromises = response.data.map(async (photo) => {
      try {
        // Fetch the image as a blob using our secure endpoint
        const imageResponse = await axios.get(
          `/api/properties/${propertyId}/photos/${photo.id}/file`,
          { responseType: 'blob' },
        );

        // Create an object URL from the blob
        const photoBlob = imageResponse.data;
        const photoUrl = URL.createObjectURL(photoBlob);

        // Return the photo with the blob URL
        return {
          id: photo.id,
          propertyId: photo.propertyId,
          caption: photo.caption,
          photoFile: photo.photoFile,
          modifiedOn: photo.modifiedOn,
          photoUrl: photoUrl,
        };
      } catch (err) {
        console.error(`Error fetching photo ${photo.id}:`, err);
        // Return the photo without a URL if fetching fails
        return {
          id: photo.id,
          propertyId: photo.propertyId,
          caption: photo.caption,
          photoFile: photo.photoFile,
          modifiedOn: photo.modifiedOn,
          photoUrl: null,
        };
      }
    });

    // Wait for all photo fetches to complete
    photos.value = await Promise.all(photoPromises);
    isLoadingPhotos.value = false;
  } catch (error) {
    console.error('Error fetching photos:', error);
    photos.value = [];
    isLoadingPhotos.value = false;
  }
}

// Selection and navigation
function selectProperty(property) {
  selectedProperty.value = property;
  fetchPhotosForProperty(property.id);
}

function backToProperties() {
  selectedProperty.value = null;
  photos.value = [];
  searchQuery.value = '';
  filteredProperties.value = [];
  isEditingPhotos.value = false;
  cleanupBlobUrls();
}

// Edit mode toggle
function toggleEditMode() {
  isEditingPhotos.value = !isEditingPhotos.value;
}

// Dialog management
function openPhotoUploadDialog() {
  photoFiles.value = [];
  photoCaption.value = '';
  photoPreviewUrls.value = [];
  photoDialog.value = true;
}

function openPhotoDialog(photo) {
  selectedPhoto.value = photo;
  viewPhotoDialog.value = true;
}

function closePhotoDialog() {
  // Reset zoom state when closing the dialog
  photoScale.value = 1;
  photoTranslateX.value = 0;
  photoTranslateY.value = 0;
  viewPhotoDialog.value = false;
}

// Touch event handlers for pinch-to-zoom
function handleTouchStart(event) {
  if (event.touches.length === 1) {
    // Single touch - prepare for panning
    isPanning.value = true;
    const touch = event.touches[0];
    lastTouchX.value = touch.clientX;
    lastTouchY.value = touch.clientY;
  } else if (event.touches.length === 2) {
    // Double touch - prepare for zooming
    isZooming.value = true;
    isPanning.value = false;

    // Calculate the initial distance between the two touches
    const touch1 = event.touches[0];
    const touch2 = event.touches[1];
    initialTouchDistance.value = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY,
    );
    initialScale.value = photoScale.value;
  }
}

function handleTouchMove(event) {
  event.preventDefault(); // Prevent browser gestures

  if (isZooming.value && event.touches.length === 2) {
    // Handle zooming - calculate new scale based on change in distance
    const touch1 = event.touches[0];
    const touch2 = event.touches[1];
    const currentDistance = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY,
    );

    // Calculate new scale
    const newScale =
      initialScale.value * (currentDistance / initialTouchDistance.value);

    // Apply limits to the zoom scale
    photoScale.value = Math.min(Math.max(newScale, 1), 5);
  } else if (
    isPanning.value &&
    event.touches.length === 1 &&
    photoScale.value > 1
  ) {
    // Only allow panning when zoomed in
    const touch = event.touches[0];

    // Calculate the distance moved
    const deltaX = touch.clientX - lastTouchX.value;
    const deltaY = touch.clientY - lastTouchY.value;

    // Update the translation values - scale the deltas by the current zoom level
    photoTranslateX.value += deltaX;
    photoTranslateY.value += deltaY;

    // Update the last touch position
    lastTouchX.value = touch.clientX;
    lastTouchY.value = touch.clientY;
  }
}

function handleTouchEnd() {
  // Reset the touch states
  isPanning.value = false;
  isZooming.value = false;
}

function handleDoubleTap(event) {
  if (photoScale.value > 1) {
    // If already zoomed in, reset to normal
    photoScale.value = 1;
    photoTranslateX.value = 0;
    photoTranslateY.value = 0;
  } else {
    // Zoom in to 2x at the tap location
    photoScale.value = 2;

    // Calculate tap position relative to the image center
    const img = event.target;
    const rect = img.getBoundingClientRect();
    const tapX = event.clientX - rect.left - rect.width / 2;
    const tapY = event.clientY - rect.top - rect.height / 2;

    // Center the zoomed view on the tap location
    photoTranslateX.value = -tapX;
    photoTranslateY.value = -tapY;
  }
}

function cancelEditCaption() {
  editCaptionDialog.value = false;
}

async function updatePhotoCaption() {
  if (!selectedPhoto.value) return;

  try {
    await axios.put(`/api/property-photos/${selectedPhoto.value.id}/caption`, {
      caption: editingCaption.value,
    });

    // Update the caption in the local photo object
    selectedPhoto.value.caption = editingCaption.value;
    // Update the modifiedOn date to current timestamp
    selectedPhoto.value.modifiedOn = new Date().toISOString();

    // Also update in the photos array
    const photoIndex = photos.value.findIndex(
      (p) => p.id === selectedPhoto.value.id,
    );
    if (photoIndex !== -1) {
      photos.value[photoIndex].caption = editingCaption.value;
      photos.value[photoIndex].modifiedOn = selectedPhoto.value.modifiedOn;
    }

    // Update in the recentPhotos array if present
    const recentPhotoIndex = recentPhotos.value.findIndex(
      (p) => p.id === selectedPhoto.value.id,
    );
    if (recentPhotoIndex !== -1) {
      recentPhotos.value[recentPhotoIndex].caption = editingCaption.value;
      recentPhotos.value[recentPhotoIndex].modifiedOn =
        selectedPhoto.value.modifiedOn;

      // Update the cache with modified photo
      await PhotoCache.cachePhotos(recentPhotos.value);
      // console.log('Updated photo cache after caption change');
    }

    editCaptionDialog.value = false;
  } catch (error) {
    console.error('Error updating photo caption:', error);
    alert('Error updating caption. Please try again.');
  }
}

function handlePhotoSelect(event) {
  const files = Array.from(event.target.files);
  if (files.length) {
    const validFiles = [];

    files.forEach((file) => {
      // Check if file is an image
      if (!file.type.match('image.*')) {
        alert('Please select image files only');
        return;
      }

      // All files are accepted now, we'll compress large ones later
      validFiles.push(file);
    });

    photoFiles.value = validFiles;

    // Generate preview URLs for selected files
    photoPreviewUrls.value = photoFiles.value.map((file) =>
      URL.createObjectURL(file),
    );
  }
}

async function uploadPhoto() {
  if (!photoFiles.value.length || !selectedProperty.value) return;

  try {
    // Show loading state
    isUploading.value = true;
    isLoadingPhotos.value = true;

    // Process and upload each photo sequentially
    for (const file of photoFiles.value) {
      // Always use compression and conversion to ensure proper MIME type, especially for iOS devices
      let fileToUpload = file;

      // console.log( `Original file: ${file.name} (${formatFileSize(file.size)}), type: ${file.type}`,);

      // Options for image compression/conversion
      const options = {
        maxSizeMB: 1.5, // Max file size in MB
        maxWidthOrHeight: 1920, // Maximum width or height
        useWebWorker: true, // Use web workers for better performance
        fileType: 'image/jpeg', // Always convert to JPEG format
      };

      try {
        // Always compress/convert to ensure proper file type
        const compressedFile = await imageCompression(file, options);

        // Create a new File object with the correct MIME type and extension
        const fileName =
          (file.name || 'photo').replace(/\.[^/.]+$/, '') + '.jpg';
        fileToUpload = new File([compressedFile], fileName, {
          type: 'image/jpeg',
          lastModified: new Date().getTime(),
        });

        // console.log( `Converted file: ${fileToUpload.name} (${formatFileSize(fileToUpload.size)}), type: ${fileToUpload.type}`,);
      } catch (compressionError) {
        console.error('Error compressing image:', compressionError);
        // Continue with original file if compression fails
      }

      // Split file into chunks of 950KB (just under 1MB NGINX limit)
      const CHUNK_SIZE = 950 * 1024; // 950KB in bytes
      const totalChunks = Math.ceil(fileToUpload.size / CHUNK_SIZE);

      // If file is small enough to send in one request, use the original method
      if (totalChunks === 1) {
        // console.log('File small enough to upload directly');

        const formData = new FormData();
        formData.append('photo', fileToUpload);
        formData.append('caption', photoCaption.value);

        try {
          // Upload this individual photo
          await axios.post(
            `/api/properties/${selectedProperty.value.id}/photos`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );
        } catch (error) {
          console.error('Upload error:', error);
          throw new Error(
            `Upload failed: ${error.response?.data?.error || error.message}`,
          );
        }
      } else {
        // Otherwise, use chunked upload
        // console.log(`Using chunked upload: ${totalChunks} chunks`);

        // Generate a unique upload ID
        const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

        // Start by informing the server we're beginning a chunked upload
        const initResponse = await axios.post('/api/properties/chunked/init', {
          fileName: fileToUpload.name,
          fileType: fileToUpload.type,
          totalChunks: totalChunks,
          totalSize: fileToUpload.size,
          uploadId: uploadId,
          propertyId: selectedProperty.value.id,
          caption: photoCaption.value,
        });

        if (initResponse.status !== 200) {
          throw new Error('Failed to initialize chunked upload');
        }

        // Process each chunk
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, fileToUpload.size);
          const chunk = fileToUpload.slice(start, end);

          const chunkFormData = new FormData();
          chunkFormData.append('chunk', chunk, 'chunk');
          chunkFormData.append('chunkIndex', chunkIndex.toString());
          chunkFormData.append('uploadId', uploadId);

          // console.log(`Uploading chunk ${chunkIndex + 1}/${totalChunks}`);

          const chunkResponse = await axios.post(
            '/api/properties/chunked/upload',
            chunkFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );

          if (chunkResponse.status !== 200) {
            throw new Error(`Failed to upload chunk ${chunkIndex + 1}`);
          }
        }

        // Finalize the upload - Convert propertyId to string to prevent the type error
        const finalizeResponse = await axios.post(
          '/api/properties/chunked/finalize',
          {
            uploadId,
            propertyId: String(selectedProperty.value.id), // Explicitly convert to string
          },
        );

        if (finalizeResponse.status !== 201) {
          throw new Error('Failed to finalize chunked upload');
        }
      }
    }

    // After all photos are uploaded, refresh the photo list
    await fetchPhotosForProperty(selectedProperty.value.id);

    // Update the photo count in the properties array
    const propertyIndex = properties.value.findIndex(
      (p) => p.id === selectedProperty.value.id,
    );
    if (propertyIndex !== -1) {
      // Increment the photoCount for this property
      properties.value[propertyIndex].photoCount += photoFiles.value.length;

      // If this property is in the filtered list, update that too
      const filteredIndex = filteredProperties.value.findIndex(
        (p) => p.id === selectedProperty.value.id,
      );
      if (filteredIndex !== -1) {
        filteredProperties.value[filteredIndex].photoCount +=
          photoFiles.value.length;
      }
    }

    // Refresh recent photos to include the newly uploaded ones
    await fetchRecentPhotos();

    // Reset carousel index to show the new photos
    currentCarouselIndex.value = 0;

    photoDialog.value = false;
    isLoadingPhotos.value = false;
    isUploading.value = false;
  } catch (error) {
    console.error('Error processing or uploading photo:', error);
    isLoadingPhotos.value = false;
    isUploading.value = false;

    // Show detailed error dialog instead of simple alert
    uploadErrorMessage.value = 'Error uploading photo';
    uploadErrorDetails.value = error.response
      ? `${error.response.status}: ${error.response.data.error || error.response.data.message || JSON.stringify(error.response.data)}`
      : error.message || 'Unknown error occurred';
    showUploadErrorDialog.value = true;
  }
}

async function confirmDelete() {
  try {
    if (confirmAction.value === 'deletePhoto' && itemToDelete.value) {
      await axios.delete(`/api/property-photos/${itemToDelete.value.id}`);
      await fetchPhotosForProperty(selectedProperty.value.id);

      // If the deleted photo is currently being viewed, close the viewer
      if (
        viewPhotoDialog.value &&
        selectedPhoto.value &&
        selectedPhoto.value.id === itemToDelete.value.id
      ) {
        closePhotoDialog();
      }

      // Update the photo count in the properties array
      const propertyIndex = properties.value.findIndex(
        (p) => p.id === selectedProperty.value.id,
      );
      if (
        propertyIndex !== -1 &&
        properties.value[propertyIndex].photoCount > 0
      ) {
        // Decrement the photoCount for this property
        properties.value[propertyIndex].photoCount -= 1;

        // If this property is in the filtered list, update that too
        const filteredIndex = filteredProperties.value.findIndex(
          (p) => p.id === selectedProperty.value.id,
        );
        if (
          filteredIndex !== -1 &&
          filteredProperties.value[filteredIndex].photoCount > 0
        ) {
          filteredProperties.value[filteredIndex].photoCount -= 1;
        }
      }

      // Remove the deleted photo from recentPhotos array if it exists there
      const recentPhotoIndex = recentPhotos.value.findIndex(
        (p) => p.id === itemToDelete.value.id,
      );
      if (recentPhotoIndex !== -1) {
        // Revoke the object URL before removing from array
        if (recentPhotos.value[recentPhotoIndex].photoUrl) {
          URL.revokeObjectURL(recentPhotos.value[recentPhotoIndex].photoUrl);
        }
        // Remove from the array
        recentPhotos.value.splice(recentPhotoIndex, 1);

        // Update the carousel index if needed
        if (recentPhotos.value.length > 0) {
          currentCarouselIndex.value = Math.min(
            currentCarouselIndex.value,
            recentPhotos.value.length - 1,
          );
        }

        // Also remove from IndexedDB cache
        try {
          await PhotoCache.removePhoto(itemToDelete.value.id);
          // console.log(`Removed photo ${itemToDelete.value.id} from cache`);
        } catch (cacheError) {
          console.error('Error removing photo from cache:', cacheError);
        }
      }
    }

    confirmDialog.value = false;
  } catch (error) {
    console.error('Error deleting item:', error);
    alert('Error deleting item. Please try again.');
  }
}

// New share functionality
function sharePhoto() {
  if (!selectedPhoto.value) return;

  try {
    // Check if we're in a browser environment with window
    if (typeof window === 'undefined') {
      throw new Error('Not in browser environment');
    }

    // Create the URL for sharing and store it for the template
    const baseUrl = window.location.origin;
    shareUrlText.value = `${baseUrl}/mproperties?photo=${selectedPhoto.value.id}`;

    // Check if clipboard API is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(shareUrlText.value)
        .then(() => {
          // Show success toast
          shareToastMessage.value = 'URL copied to clipboard!';
          showShareToast.value = true;

          // Hide toast after 3 seconds
          setTimeout(() => {
            showShareToast.value = false;
          }, 3000);
        })
        .catch((err) => {
          console.error('Could not copy text: ', err);
          // Show error dialog instead of alert
          shareErrorMessage.value =
            'Clipboard access is not available in non-HTTPS environments. Please copy this URL manually:';
          showShareErrorDialog.value = true;
        });
    } else {
      // Clipboard API not available
      // console.log('Clipboard API not available, showing manual copy dialog');
      shareErrorMessage.value =
        'Clipboard access is not available in this browser. Please copy this URL manually:';
      showShareErrorDialog.value = true;
    }
  } catch (error) {
    console.error('Error sharing photo:', error);
    // Show error dialog instead of alert
    shareErrorMessage.value =
      'Unable to share photo. This feature requires a secure context (HTTPS) in production.';
    showShareErrorDialog.value = true;
  }
}

// Clean up blob URLs
function cleanupBlobUrls() {
  // Revoke all object URLs from photos
  if (photos.value && photos.value.length) {
    photos.value.forEach((photo) => {
      if (photo.photoUrl && typeof photo.photoUrl === 'string') {
        URL.revokeObjectURL(photo.photoUrl);
      }
    });
  }

  // Clean up any preview URLs
  if (photoPreviewUrls.value.length) {
    photoPreviewUrls.value.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    photoPreviewUrls.value = [];
  }
}

// Function to handle confirming photo deletion
function confirmDeletePhoto(photo, event) {
  // Stop event propagation to prevent opening the photo preview
  if (event) {
    event.stopPropagation();
  }

  confirmMessage.value = 'Are you sure you want to delete this photo?';
  confirmAction.value = 'deletePhoto';
  itemToDelete.value = photo;
  confirmDialog.value = true;
}

// Toggle stats visibility
function toggleStats() {
  statsCollapsed.value = !statsCollapsed.value;
}

// Start auto-advance for carousel
function startCarouselAutoAdvance() {
  stopCarouselAutoAdvance(); // Ensure no duplicate timers
  carouselAutoAdvanceTimer.value = setInterval(() => {
    currentCarouselIndex.value =
      (currentCarouselIndex.value + 1) % recentPhotos.value.length;
  }, 15000); // Advance every 15 seconds
}

// Stop auto-advance for carousel
function stopCarouselAutoAdvance() {
  if (carouselAutoAdvanceTimer.value) {
    clearInterval(carouselAutoAdvanceTimer.value);
    carouselAutoAdvanceTimer.value = null;
  }
}

// Manual navigation functions for carousel
function prevCarouselPhoto(event) {
  event.stopPropagation();
  currentCarouselIndex.value =
    (currentCarouselIndex.value - 1 + recentPhotos.value.length) %
    recentPhotos.value.length;

  // Reset the auto-advance timer when manually navigating
  startCarouselAutoAdvance();
}

function nextCarouselPhoto(event) {
  event.stopPropagation();
  currentCarouselIndex.value =
    (currentCarouselIndex.value + 1) % recentPhotos.value.length;

  // Reset the auto-advance timer when manually navigating
  startCarouselAutoAdvance();
}

// Clean up on unmount
onUnmounted(() => {
  eventBus.off('open-unpaid-leases-popup', handleOpenUnpaidLeasesPopup);
  document.removeEventListener('keydown', trashHandleKeydown);

  // Clear the auto-advance timer
  stopCarouselAutoAdvance();

  // Clean up blob URLs
  cleanupBlobUrls();

  // Clean up any selected photo blob URL
  if (selectedPhoto.value && selectedPhoto.value.photoUrl) {
    URL.revokeObjectURL(selectedPhoto.value.photoUrl);
  }

  // Clean up recent photos blob URLs
  recentPhotos.value.forEach((photo) => {
    if (photo.photoUrl) {
      URL.revokeObjectURL(photo.photoUrl);
    }
  });
});

watch(
  () => isLoading.value,
  (newVal) => {
    if (newVal === false) {
      fetchProperties();
    }
  },
);

const trashFilterRef = ref(null);

watch(activeView, (newVal) => {
  if (newVal === 'trash') {
    if (trashProperties.value.length === 0) {
      loadTrashData();
    }
    nextTick(() => {
      trashFilterRef.value?.focus();
    });
  }
  if (newVal === 'laundry') {
    if (laundryProperties.value.length === 0) {
      loadLaundryData();
    }
    nextTick(() => {
      laundryFilterRef.value?.focus();
    });
  }
  if (newVal === 'cleaners') {
    if (cleaningProperties.value.length === 0) {
      loadCleaningData();
    }
    nextTick(() => {
      cleaningFilterRef.value?.focus();
    });
  }
  if (newVal === 'storage') {
    if (storageLeases.value.length === 0) {
      loadStorageLeases();
    }
    nextTick(() => {
      storageFilterRef.value?.focus();
    });
  }
});

// ===== LAUNDRY TRACKING STATE =====
const laundryProperties = ref([]);
const laundryFilterQuery = ref('');
const laundryLoading = ref(false);
const laundryCollapsedGroups = ref(new Set());
const laundryFocusedCard = ref(null);
const laundrySaving = ref(new Set());
const laundryStatusFilter = ref(null);
const laundryFilterRef = ref(null);
const laundryCollapsedMachines = ref(new Set());
const laundryModelSerialToggle = ref({});
const laundryInputRefs = {};
const setLaundryInputRef = (machineId) => (el) => {
  if (el) laundryInputRefs[machineId] = el;
};
const toggleLaundryModelSerial = (machineId) => {
  laundryModelSerialToggle.value[machineId] =
    laundryModelSerialToggle.value[machineId] === 'serial' ? 'model' : 'serial';
  nextTick(() => {
    const input = laundryInputRefs[machineId];
    if (input) {
      input.focus();
      input.select();
    }
  });
};
const laundryTabToggle = (machineId, e) => {
  if (laundryModelSerialToggle.value[machineId] !== 'serial') {
    e.preventDefault();
    toggleLaundryModelSerial(machineId);
  }
};
const laundryShowAlertPopup = ref(false);
const laundryAlertPropertyId = ref(null);
const laundryAlertAddress = ref('');
const laundryAlertMessage = ref('');
const laundryAlertSending = ref(false);
const laundryAlertResults = ref(null);
const laundryAlertFallback = ref(false);

const laundryFilteredProperties = computed(() => {
  const hasQuery = laundryFilterQuery.value.trim();
  const hasStatusFilter = laundryStatusFilter.value !== null;

  if (!hasQuery && !hasStatusFilter) return laundryProperties.value;

  const q = (laundryFilterQuery.value || '').toLowerCase().trim();
  const textParts = hasQuery ? q.split(/\s+/) : [];

  const hasData = (prop) => prop.machines && prop.machines.length > 0;

  return laundryProperties.value.filter((p) => {
    if (laundryStatusFilter.value === 'blank') {
      if (hasData(p)) return false;
    } else if (laundryStatusFilter.value === 'done') {
      if (!hasData(p)) return false;
    }

    if (textParts.length > 0) {
      const haystack = (
        p.address +
        ' ' +
        p.tenants.map((t) => t.name).join(' ') +
        ' ' +
        (p.machines || [])
          .map(
            (m) =>
              (m.pixie_id || '') + ' ' + (m.make || '') + ' ' + (m.type || ''),
          )
          .join(' ')
      ).toLowerCase();
      const segments = textParts
        .join(' ')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return segments.some((seg) => {
        const tokens = seg.split(/\s+/);
        return tokens.every((tok) => haystack.includes(tok));
      });
    }

    return true;
  });
});

const laundryGroupedProperties = computed(() => {
  const filtered = laundryFilteredProperties.value;
  const all = laundryProperties.value;
  const groups = [];
  const parentMap = {};

  for (const p of filtered) {
    if (p.isParentAddr === 1 && (p.parentId === p.propertyId || !p.parentId)) {
      parentMap[p.propertyId] = { parent: p, children: [] };
      groups.push(parentMap[p.propertyId]);
    }
  }

  for (const p of filtered) {
    if (parentMap[p.propertyId]) continue;
    const pid = p.parentId;
    if (pid && parentMap[pid]) {
      if (!parentMap[pid].children.find((x) => x.propertyId === p.propertyId))
        parentMap[pid].children.push(p);
    } else if (pid) {
      const parentProp = all.find(
        (a) =>
          a.propertyId === pid &&
          a.isParentAddr === 1 &&
          (a.parentId === a.propertyId || !a.parentId),
      );
      if (parentProp) {
        parentMap[pid] = { parent: parentProp, children: [p] };
        groups.push(parentMap[pid]);
        for (const c of all) {
          if (
            c.parentId === pid &&
            c.propertyId !== pid &&
            !parentMap[pid].children.find((x) => x.propertyId === c.propertyId)
          ) {
            parentMap[pid].children.push(c);
          }
        }
      } else {
        groups.push({ parent: p, children: [] });
      }
    } else {
      groups.push({ parent: p, children: [] });
    }
  }

  return groups;
});

async function loadLaundryData() {
  laundryLoading.value = true;
  try {
    const { data } = await axios.get('/api/laundry/properties', {
      params: { _t: Date.now() },
    });
    laundryProperties.value = data;
    if (laundryCollapsedGroups.value.size === 0) {
      const collapsed = new Set();
      for (const p of data) {
        if (
          p.isParentAddr === 1 &&
          (p.parentId === p.propertyId || !p.parentId)
        ) {
          collapsed.add(p.propertyId);
        }
      }
      laundryCollapsedGroups.value = collapsed;
    }
  } catch (error) {
    console.error('Error loading laundry data:', error);
  } finally {
    laundryLoading.value = false;
  }
}

function laundryToggleCollapse(parentId) {
  const s = new Set(laundryCollapsedGroups.value);
  if (s.has(parentId)) s.delete(parentId);
  else s.add(parentId);
  laundryCollapsedGroups.value = s;
}

function laundryIsCollapsed(parentId) {
  return laundryCollapsedGroups.value.has(parentId);
}

function laundryCollapseAll() {
  const allParentIds = laundryGroupedProperties.value.map(
    (g) => g.parent.propertyId,
  );
  const allCollapsed = allParentIds.every((id) =>
    laundryCollapsedGroups.value.has(id),
  );
  if (allCollapsed) {
    laundryCollapsedGroups.value = new Set();
  } else {
    laundryCollapsedGroups.value = new Set(allParentIds);
  }
}

function laundryToggleFocus(propertyId) {
  const wasFocused = laundryFocusedCard.value === propertyId;
  laundryFocusedCard.value = wasFocused ? null : propertyId;
  const s = new Set(laundryCollapsedMachines.value);
  if (wasFocused) s.add(propertyId);
  else s.delete(propertyId);
  laundryCollapsedMachines.value = s;
}

function laundryIsFocused(propertyId) {
  return laundryFocusedCard.value === propertyId;
}

function laundryToggleMachineCollapse(propertyId) {
  const s = new Set(laundryCollapsedMachines.value);
  if (s.has(propertyId)) s.delete(propertyId);
  else s.add(propertyId);
  laundryCollapsedMachines.value = s;
}

function laundryMachinesCollapsed(propertyId) {
  return laundryCollapsedMachines.value.has(propertyId);
}

function laundryMachineCounts(property) {
  const machines = property.machines || [];
  if (!machines.length) return '';
  const w = machines.filter((m) => m.type === 'Washer').length;
  const d = machines.filter((m) => m.type === 'Dryer').length;
  const c = machines.filter((m) => m.type === 'Combo').length;
  const parts = [];
  if (w) parts.push(`${w}W`);
  if (d) parts.push(`${d}D`);
  if (c) parts.push(`${c}C`);
  return parts.length ? `(${parts.join(', ')})` : '';
}

function laundryIsShared(property) {
  return property.laundryShared && property.laundryShared.shared;
}

async function laundryToggleShared(property) {
  if (!property.laundryShared) property.laundryShared = { shared: 0 };
  property.laundryShared.shared = property.laundryShared.shared ? 0 : 1;
  try {
    await axios.put(`/api/laundry/shared/${property.propertyId}`, {
      shared: property.laundryShared.shared,
    });
  } catch (error) {
    console.error('Error toggling laundry shared:', error);
  }
}

async function laundryAddMachine(property, type) {
  try {
    const { data } = await axios.post('/api/laundry/machines', {
      propertyId: property.propertyId,
      type: type || null,
    });
    if (!property.machines) property.machines = [];
    const newMachine = {
      id: data.id,
      propertyId: property.propertyId,
      type: type || null,
      make: null,
      model_number: null,
      age: null,
      condition_rating: null,
      fuel_type: 'Electric',
      load_type: 'Top',
      pmt: null,
      voltage: '120V',
      pixie_id: null,
      shinepay_id: null,
      coin_pickup_date: null,
      notes: null,
      modified_by: null,
      modifiedByName: null,
    };
    property.machines.push(newMachine);
    nextTick(() => {
      const input = laundryInputRefs[newMachine.id];
      if (input) {
        input.focus();
      }
    });
  } catch (error) {
    console.error('Error adding laundry machine:', error);
  }
}

async function laundryDeleteMachine(property, machineId) {
  try {
    await axios.delete(`/api/laundry/machines/${machineId}`);
    property.machines = property.machines.filter((m) => m.id !== machineId);
  } catch (error) {
    console.error('Error deleting laundry machine:', error);
  }
}

let laundrySaveTimers = {};
function laundryScheduleSave(machine) {
  const mid = machine.id;
  if (laundrySaveTimers[mid]) clearTimeout(laundrySaveTimers[mid]);
  laundrySaveTimers[mid] = setTimeout(() => saveLaundryMachine(machine), 600);
}

async function saveLaundryMachine(machine) {
  laundrySaving.value = new Set([...laundrySaving.value, machine.propertyId]);
  try {
    await axios.put(`/api/laundry/machines/${machine.id}`, {
      type: machine.type || null,
      cost: machine.cost != null ? machine.cost : null,
      make: machine.make || null,
      model_number: machine.model_number || null,
      serial_number: machine.serial_number || null,
      age: machine.age != null ? machine.age : null,
      condition_rating:
        machine.condition_rating != null ? machine.condition_rating : null,
      fuel_type: machine.fuel_type || 'Electric',
      load_type: machine.load_type || 'Top',
      pmt: machine.pmt || null,
      voltage: machine.voltage || '120V',
      pixie_id: machine.pixie_id || null,
      shinepay_id: machine.shinepay_id || null,
      coin_pickup_date: machine.coin_pickup_date || null,
      notes: machine.notes || null,
    });
  } catch (error) {
    console.error('Error saving laundry machine:', error);
  } finally {
    const s = new Set(laundrySaving.value);
    s.delete(machine.propertyId);
    laundrySaving.value = s;
  }
}

function laundrySetField(machine, field, value) {
  machine[field] = value;
  laundryScheduleSave(machine);
}

function laundryCoinPickup(machine) {
  const today = new Date().toISOString().slice(0, 10);
  machine.coin_pickup_date = today;
  laundryScheduleSave(machine);
}

function laundrySetRating(machine, rating) {
  machine.condition_rating =
    machine.condition_rating === rating ? null : rating;
  laundryScheduleSave(machine);
}

function laundryModifiedText(machine) {
  if (!machine.modifiedByName) return null;
  const d = new Date(machine.updated_at);
  const mo = d.getMonth() + 1;
  const day = d.getDate();
  const yr = String(d.getFullYear()).slice(2);
  let hr = d.getHours();
  const ampm = hr >= 12 ? 'pm' : 'am';
  hr = hr % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `modified by: ${machine.modifiedByName} on ${mo}/${day}/${yr} at ${hr}:${min}${ampm}`;
}

function laundryMachineBorderClass(machine) {
  if (machine.type === 'Washer') return 'lm-border-washer';
  if (machine.type === 'Dryer') return 'lm-border-dryer';
  return 'lm-border-combo';
}

function laundryOpenAlert(property) {
  laundryAlertPropertyId.value = property.propertyId;
  laundryAlertAddress.value = property.address;
  laundryAlertMessage.value = '';
  laundryAlertResults.value = null;
  laundryAlertFallback.value = false;
  laundryAlertSending.value = false;
  laundryShowAlertPopup.value = true;
}

async function laundrySendAlert() {
  if (!laundryAlertMessage.value.trim()) return;
  laundryAlertSending.value = true;
  try {
    const { data } = await axios.post('/api/laundry/alert', {
      propertyId: laundryAlertPropertyId.value,
      message: laundryAlertMessage.value,
    });
    laundryAlertResults.value = data.results;
    laundryAlertFallback.value = !!data.fallback;
  } catch (error) {
    console.error('Error sending laundry alert:', error);
    laundryAlertResults.value = [
      { success: false, error: error.message || 'Failed to send' },
    ];
  } finally {
    laundryAlertSending.value = false;
  }
}

function laundryCloseAlert() {
  laundryShowAlertPopup.value = false;
  laundryAlertResults.value = null;
  laundryAlertFallback.value = false;
}

// ===== CLEANING TRACKING STATE =====
const cleaningProperties = ref([]);
const cleaningContractors = ref([]);
const cleaningFilterQuery = ref('');
const cleaningLoading = ref(false);
const cleaningCollapsedGroups = ref(new Set());
const cleaningFocusedCard = ref(null);
const cleaningSaving = ref(new Set());
const cleaningFilterRef = ref(null);
const cleaningCollapsedLocations = ref(new Set());
const cleaningShowAlertPopup = ref(false);
const cleaningAlertPropertyId = ref(null);
const cleaningAlertAddress = ref('');
const cleaningAlertMessage = ref('');
const cleaningAlertSending = ref(false);
const cleaningAlertResults = ref(null);
const cleaningAlertFallback = ref(false);

const cleaningFilteredProperties = computed(() => {
  const q = cleaningFilterQuery.value.trim().toLowerCase();
  if (!q) return cleaningProperties.value;
  return cleaningProperties.value.filter((p) => {
    if (p.address && p.address.toLowerCase().includes(q)) return true;
    if (p.tenants && p.tenants.some((t) => t.name.toLowerCase().includes(q)))
      return true;
    return false;
  });
});

const cleaningGroupedProperties = computed(() => {
  const filtered = cleaningFilteredProperties.value;
  const all = cleaningProperties.value;
  const groups = [];
  const parentMap = {};

  for (const p of filtered) {
    if (p.isParentAddr === 1 && (p.parentId === p.propertyId || !p.parentId)) {
      parentMap[p.propertyId] = { parent: p, children: [] };
      groups.push(parentMap[p.propertyId]);
    }
  }

  for (const p of filtered) {
    if (parentMap[p.propertyId]) continue;
    const pid = p.parentId;
    if (pid && parentMap[pid]) {
      if (!parentMap[pid].children.find((x) => x.propertyId === p.propertyId))
        parentMap[pid].children.push(p);
    } else if (pid) {
      const parentProp = all.find(
        (a) =>
          a.propertyId === pid &&
          a.isParentAddr === 1 &&
          (a.parentId === a.propertyId || !a.parentId),
      );
      if (parentProp) {
        parentMap[pid] = { parent: parentProp, children: [p] };
        groups.push(parentMap[pid]);
        for (const c of all) {
          if (
            c.parentId === pid &&
            c.propertyId !== pid &&
            !parentMap[pid].children.find((x) => x.propertyId === c.propertyId)
          ) {
            parentMap[pid].children.push(c);
          }
        }
      } else {
        groups.push({ parent: p, children: [] });
      }
    } else {
      groups.push({ parent: p, children: [] });
    }
  }

  return groups;
});

async function loadCleaningData() {
  cleaningLoading.value = true;
  try {
    const { data } = await axios.get('/api/cleaning/properties', {
      params: { _t: Date.now() },
    });
    cleaningProperties.value = data.properties;
    cleaningContractors.value = data.contractors;
    if (cleaningCollapsedGroups.value.size === 0) {
      const collapsed = new Set();
      for (const p of data.properties) {
        if (
          p.isParentAddr === 1 &&
          (p.parentId === p.propertyId || !p.parentId)
        ) {
          collapsed.add(p.propertyId);
        }
      }
      cleaningCollapsedGroups.value = collapsed;
    }
  } catch (error) {
    console.error('Error loading cleaning data:', error);
  } finally {
    cleaningLoading.value = false;
  }
}

// ===== STORAGE LOCKER LIST =====
const storageLeases = ref([]);
const storageFilterQuery = ref('');
const storageLoading = ref(false);
const storageFilterRef = ref(null);
const storageRenewingId = ref(null);
const storageRenewError = ref({});
const storageAddOpen = ref(false);
const storageAddQuery = ref('');
const storageAddProperty = ref(null);
const storagePropertyMatches = ref([]);
const storageAddLocker = ref('');
const storageAddFee = ref('');
const storageAddError = ref('');
const storageAddSaving = ref(false);
const storageEditingId = ref(null);
const storageAddCard = ref(null);
let storagePropertyTimer = null;

const filteredStorageLeases = computed(() =>
  filterStorageLeases(storageLeases.value, storageFilterQuery.value),
);

async function loadStorageLeases({ quiet = false } = {}) {
  if (!quiet) storageLoading.value = true;
  try {
    const { data } = await axios.get('/api/storage-leases', {
      params: { _t: Date.now() },
    });
    storageLeases.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error loading storage leases:', error);
  } finally {
    if (!quiet) storageLoading.value = false;
  }
}

function openStorageAdd() {
  storageAddOpen.value = true;
  storageEditingId.value = null;
  storageAddError.value = '';
  storageAddQuery.value = '';
  storageAddProperty.value = null;
  storagePropertyMatches.value = [];
  storageAddLocker.value = '';
  storageAddFee.value = '';
}

function editUnassignedStorage(row) {
  if (!row?.unassigned || !row.storageId) return;
  storageAddOpen.value = true;
  storageEditingId.value = row.storageId;
  storageAddProperty.value = {
    id: row.propertyId,
    display: row.address || '',
  };
  storageAddQuery.value = row.address || '';
  storagePropertyMatches.value = [];
  storageAddLocker.value = row.locker || '';
  storageAddFee.value = String(row.fee || '').replace(/[$,]/g, '');
  storageAddError.value = '';
  nextTick(() => {
    storageAddCard.value?.scrollIntoView?.({ block: 'nearest' });
  });
}

function searchStorageProperties() {
  storageAddProperty.value = null;
  if (storagePropertyTimer) clearTimeout(storagePropertyTimer);
  const query = storageAddQuery.value.trim();
  if (!query) {
    storagePropertyMatches.value = [];
    return;
  }
  storagePropertyTimer = setTimeout(async () => {
    try {
      const { data } = await axios.get('/api/properties', {
        params: { query, includeHouses: '1' },
      });
      storagePropertyMatches.value = Array.isArray(data) ? data.slice(0, 8) : [];
    } catch (error) {
      console.error('Error searching properties for storage:', error);
      storagePropertyMatches.value = [];
    }
  }, 250);
}

function selectStorageProperty(property) {
  storageAddProperty.value = property;
  storageAddQuery.value = property.display || '';
  storagePropertyMatches.value = [];
}

async function saveUnassignedStorage() {
  if (storageAddSaving.value) return;
  storageAddError.value = '';
  if (!storageAddProperty.value?.id) {
    storageAddError.value = 'Choose a property';
    return;
  }
  storageAddSaving.value = true;
  try {
    const payload = {
      propertyId: storageAddProperty.value.id,
      locker: storageAddLocker.value,
      fee: storageAddFee.value,
    };
    const request = storageEditingId.value
      ? axios.put(`/api/storage-units/${storageEditingId.value}`, payload)
      : axios.post('/api/storage-units', payload);
    const { data } = await request;
    if (Array.isArray(data?.leases)) {
      storageLeases.value = data.leases;
    } else {
      await loadStorageLeases({ quiet: true });
    }
    storageAddOpen.value = false;
  } catch (error) {
    storageAddError.value =
      error.response?.data?.error ||
      (storageEditingId.value
        ? 'Could not update storage'
        : 'Could not add storage');
  } finally {
    storageAddSaving.value = false;
  }
}

async function renewStorageLease(row) {
  if (!row?.leaseId || storageRenewingId.value) return;
  storageRenewingId.value = row.leaseId;
  storageRenewError.value = {
    ...storageRenewError.value,
    [row.leaseId]: '',
  };
  try {
    const { data } = await axios.post(
      `/api/storage-leases/${row.leaseId}/renew`,
    );
    if (Array.isArray(data?.leases)) {
      storageLeases.value = data.leases;
    } else {
      await loadStorageLeases({ quiet: true });
    }
  } catch (error) {
    const message =
      error.response?.data?.error || 'Could not copy storage to the current lease';
    storageRenewError.value = {
      ...storageRenewError.value,
      [row.leaseId]: message,
    };
  } finally {
    storageRenewingId.value = null;
  }
}

function cleaningToggleCollapse(parentId) {
  const s = new Set(cleaningCollapsedGroups.value);
  if (s.has(parentId)) s.delete(parentId);
  else s.add(parentId);
  cleaningCollapsedGroups.value = s;
}

function cleaningIsCollapsed(parentId) {
  return cleaningCollapsedGroups.value.has(parentId);
}

function cleaningCollapseAll() {
  const allParentIds = cleaningGroupedProperties.value.map(
    (g) => g.parent.propertyId,
  );
  const allCollapsed = allParentIds.every((id) =>
    cleaningCollapsedGroups.value.has(id),
  );
  if (allCollapsed) {
    cleaningCollapsedGroups.value = new Set();
  } else {
    cleaningCollapsedGroups.value = new Set(allParentIds);
  }
}

function cleaningToggleFocus(propertyId) {
  cleaningFocusedCard.value =
    cleaningFocusedCard.value === propertyId ? null : propertyId;
}

function cleaningIsFocused(propertyId) {
  return cleaningFocusedCard.value === propertyId;
}

function cleaningToggleLocationCollapse(propertyId) {
  const s = new Set(cleaningCollapsedLocations.value);
  if (s.has(propertyId)) s.delete(propertyId);
  else s.add(propertyId);
  cleaningCollapsedLocations.value = s;
}

function cleaningLocationsCollapsed(propertyId) {
  return cleaningCollapsedLocations.value.has(propertyId);
}

function cleaningLocationCount(property) {
  const count = (property.locations || []).length;
  return count > 0 ? `(${count})` : '';
}

function cleaningCardCount(group) {
  let count = 0;
  const all = [group.parent, ...group.children];
  for (const p of all) {
    count += (p.locations || []).length;
  }
  return count > 0 ? `(${count})` : '';
}

async function cleaningAddLocation(property) {
  try {
    const { data } = await axios.post('/api/cleaning/locations', {
      propertyId: property.propertyId,
    });
    if (!property.locations) property.locations = [];
    property.locations.push({
      id: data.id,
      propertyId: property.propertyId,
      location_type: null,
      clean_every_weeks: null,
      condition_rating: null,
      notes: null,
      last_cleaned_on: null,
      last_cleaned_by: null,
      cleanedByName: null,
      modified_by: null,
      modifiedByName: null,
    });
  } catch (error) {
    console.error('Error adding cleaning location:', error);
  }
}

async function cleaningDeleteLocation(property, locationId) {
  try {
    await axios.delete(`/api/cleaning/locations/${locationId}`);
    property.locations = property.locations.filter(
      (loc) => loc.id !== locationId,
    );
  } catch (error) {
    console.error('Error deleting cleaning location:', error);
  }
}

let cleaningSaveTimers = {};
function cleaningScheduleSave(location) {
  const lid = location.id;
  if (cleaningSaveTimers[lid]) clearTimeout(cleaningSaveTimers[lid]);
  cleaningSaveTimers[lid] = setTimeout(
    () => saveCleaningLocation(location),
    600,
  );
}

async function saveCleaningLocation(location) {
  cleaningSaving.value = new Set([
    ...cleaningSaving.value,
    location.propertyId,
  ]);
  try {
    await axios.put(`/api/cleaning/locations/${location.id}`, {
      location_type: location.location_type || null,
      clean_every_weeks:
        location.clean_every_weeks != null ? location.clean_every_weeks : null,
      condition_rating:
        location.condition_rating != null ? location.condition_rating : null,
      notes: location.notes || null,
      last_cleaned_on: location.last_cleaned_on || null,
      last_cleaned_by:
        location.last_cleaned_by != null ? location.last_cleaned_by : null,
    });
  } catch (error) {
    console.error('Error saving cleaning location:', error);
  } finally {
    const s = new Set(cleaningSaving.value);
    s.delete(location.propertyId);
    cleaningSaving.value = s;
  }
}

function cleaningSetField(location, field, value) {
  location[field] = value;
  cleaningScheduleSave(location);
}

function cleaningSetRating(location, rating) {
  location.condition_rating =
    location.condition_rating === rating ? null : rating;
  cleaningScheduleSave(location);
}

function cleaningSetCleanedToday(location) {
  location.last_cleaned_on = new Date().toISOString().slice(0, 10);
  cleaningScheduleSave(location);
}

function cleaningSetContractor(location, peopleId) {
  location.last_cleaned_by = peopleId ? parseInt(peopleId) : null;
  const contractor = cleaningContractors.value.find(
    (c) => c.id === location.last_cleaned_by,
  );
  location.cleanedByName = contractor ? contractor.fullName : null;
  cleaningScheduleSave(location);
}

function cleaningOpenAlert(property) {
  cleaningAlertPropertyId.value = property.propertyId;
  cleaningAlertAddress.value = property.address;
  cleaningAlertMessage.value = '';
  cleaningAlertResults.value = null;
  cleaningAlertFallback.value = false;
  cleaningAlertSending.value = false;
  cleaningShowAlertPopup.value = true;
}

async function cleaningSendAlert() {
  if (!cleaningAlertMessage.value.trim()) return;
  cleaningAlertSending.value = true;
  try {
    const { data } = await axios.post('/api/cleaning/alert', {
      propertyId: cleaningAlertPropertyId.value,
      message: cleaningAlertMessage.value,
    });
    cleaningAlertResults.value = data.results;
    cleaningAlertFallback.value = !!data.fallback;
  } catch (error) {
    console.error('Error sending cleaning alert:', error);
    cleaningAlertResults.value = [
      { success: false, error: error.message || 'Failed to send' },
    ];
  } finally {
    cleaningAlertSending.value = false;
  }
}

function cleaningCloseAlert() {
  cleaningShowAlertPopup.value = false;
  cleaningAlertResults.value = null;
  cleaningAlertFallback.value = false;
}

function cleaningModifiedText(location) {
  if (!location.modifiedByName) return null;
  const d = new Date(location.updated_at);
  const mo = d.getMonth() + 1;
  const day = d.getDate();
  const yr = String(d.getFullYear()).slice(2);
  let hr = d.getHours();
  const ampm = hr >= 12 ? 'pm' : 'am';
  hr = hr % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `modified by: ${location.modifiedByName} on ${mo}/${day}/${yr} at ${hr}:${min}${ampm}`;
}

// Page refresh function - updated to re-fetch data instead of reloading page
async function refreshPage() {
  try {
    isLoading.value = true;
    isLoadingRecentPhotos.value = true; // Add this to show loading state for photos

    // Only fetch property summary data if user has appropriate role
    if (role.value < 3) {
      // Fetch property summary data
      const propertySummaryResponse = await axios.get(
        '/api/property-lease-summary',
      );
      propertySummary.value = propertySummaryResponse.data;

      // Get current date in America/New_York timezone
      const options = { timeZone: 'America/New_York' };
      const nyTime = new Date().toLocaleString('en-US', options);
      const nyDate = new Date(nyTime);

      // Calculate date range for API request - include data for both current and previous cycles
      const currentMonth = nyDate.getMonth();
      const currentYear = nyDate.getFullYear();

      // Determine start date based on whether we're before or after the 25th
      // We want to go back 2 months if before 25th, or 1 month if after 25th
      // This ensures we get data for the previous cycle regardless of current date
      const startDate = new Date(currentYear, currentMonth - 2, 25);
      const endDate = new Date(nyDate);

      // Format dates for API request
      const startDateStr = formatDateForInput(startDate);
      const endDateStr = formatDateForInput(endDate);

      // console.log(`Fetching payments from ${startDateStr} to ${endDateStr}`);

      // Fetch payments data
      const paymentsResponse = await axios.get('/api/payments', {
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
        },
      });
      payments.value = paymentsResponse.data;
      // console.log(`Loaded ${payments.value.length} payment records`);

      // Fetch property photo coverage stats
      await fetchCoverageStats();
    }

    // Force photo cache update - clear cached photo metadata to ensure fresh data
    localStorage.removeItem(PhotoCache.PHOTO_CACHE_KEY);
    localStorage.removeItem(PhotoCache.PHOTO_CACHE_TIMESTAMP_KEY);

    // Clean up existing photo blob URLs
    recentPhotos.value.forEach((photo) => {
      if (photo.photoUrl) {
        URL.revokeObjectURL(photo.photoUrl);
      }
    });

    // Reset the photos array
    recentPhotos.value = [];

    // Fetch properties data
    await fetchProperties();

    // Fetch fresh recent property photos for carousel (all users can see this)
    await fetchRecentPhotos();

    // Restart the carousel if it was running
    startCarouselAutoAdvance();

    // Reset carousel index to first photo
    currentCarouselIndex.value = 0;

    isLoading.value = false;
    isLoadingRecentPhotos.value = false;
  } catch (error) {
    console.error('Error refreshing data:', error);
    isLoading.value = false;
    isLoadingRecentPhotos.value = false;
  }
}

// Add these functions to prepare and populate the popup lists
function showEndingLeasesDetails() {
  // Get current date and 60 days from now
  const now = new Date();
  const nowTime = now.getTime();
  const sixtyDaysFromNow = new Date(now);
  sixtyDaysFromNow.setDate(now.getDate() + 60);
  const sixtyDaysFromNowTime = sixtyDaysFromNow.getTime();

  // Filter leases that are ending within 60 days (regardless of renewal status)
  const leasesEndingSoon = propertySummary.value.filter((property) => {
    const endsOn = new Date(property.endsOn).getTime();
    return endsOn > nowTime && endsOn <= sixtyDaysFromNowTime;
  });

  // Sort by end date (closest first)
  leasesEndingSoon.sort((a, b) => new Date(a.endsOn) - new Date(b.endsOn));

  // Format each property for display
  endingLeasesList.value = leasesEndingSoon.map((property) => ({
    address: property.address,
    endsOn: formatDate(property.endsOn),
    futureStartsOn: property.futureStartsOn
      ? formatDate(property.futureStartsOn)
      : '',
  }));

  // Show the popup
  showEndingLeasesPopup.value = true;
}

function showExpiringLeasesDetails() {
  // Get current date and 60 days from now
  const now = new Date();
  const nowTime = now.getTime();
  const sixtyDaysFromNow = new Date(now);
  sixtyDaysFromNow.setDate(now.getDate() + 60);
  const sixtyDaysFromNowTime = sixtyDaysFromNow.getTime();

  // Filter leases that are ending within 60 days AND don't have a future starts on
  const leasesEndingSoon = propertySummary.value.filter((property) => {
    const endsOn = new Date(property.endsOn).getTime();
    return (
      endsOn > nowTime &&
      endsOn <= sixtyDaysFromNowTime &&
      (!property.futureStartsOn ||
        new Date(property.futureStartsOn).getTime() < nowTime)
    );
  });

  // Sort by end date (closest first)
  leasesEndingSoon.sort((a, b) => new Date(a.endsOn) - new Date(b.endsOn));

  // Format each property for display
  expiringLeasesList.value = leasesEndingSoon.map((property) => ({
    address: property.address,
    endsOn: formatDate(property.endsOn),
  }));

  // Show the popup
  showExpiringLeasesPopup.value = true;
}

function showResignedLeasesDetails() {
  const now = new Date();
  const nowTime = now.getTime();

  // Get properties with future start date
  const resignedLeases = propertySummary.value.filter((property) => {
    return (
      property.futureStartsOn &&
      new Date(property.futureStartsOn).getTime() > nowTime
    );
  });

  // Sort by future start date
  resignedLeases.sort(
    (a, b) => new Date(a.futureStartsOn) - new Date(b.futureStartsOn),
  );

  // Format each property for display
  resignedLeasesList.value = resignedLeases.map((property) => ({
    address: property.address,
    futureStartsOn: formatDate(property.futureStartsOn),
  }));

  // Show the popup
  showResignedLeasesPopup.value = true;
}

function showVacantPropertiesDetails() {
  // Get current date
  const now = new Date();
  const nowTime = now.getTime();

  // Filter properties that are vacant (not within an active lease period)
  const vacantProperties = propertySummary.value.filter((property) => {
    const startsOn = new Date(property.startsOn).getTime();
    const endsOn = new Date(property.endsOn).getTime();
    return startsOn > nowTime || endsOn < nowTime;
  });

  // Sort by address
  vacantProperties.sort((a, b) => a.address.localeCompare(b.address));

  // Format each property for display
  vacantPropertiesList.value = vacantProperties.map((property) => ({
    address: property.address,
    endsOn: formatDate(property.endsOn),
    status:
      new Date(property.startsOn).getTime() > nowTime ? 'Upcoming' : 'Expired',
  }));

  // Show the popup
  showVacantPropertiesPopup.value = true;
}

// Add methods for lease selection in unpaid leases popup
function toggleLeaseSelection(lease) {
  const index = selectedLeases.value.findIndex(
    (l) => l.property === lease.property,
  );
  if (index === -1) {
    // Add to selected leases
    selectedLeases.value.push(lease);
  } else {
    // Remove from selected leases
    selectedLeases.value.splice(index, 1);
  }
  // Calculate total of selected leases
  calculateSelectedLeasesTotal();
  // Save selected leases to localStorage
  saveSelectedLeasesToLocalStorage();
}

function getFirstPhone(phones) {
  if (!phones) return '';
  return String(phones)
    .split(',')
    .map((p) => p.trim())
    .find((p) => p.length > 0) || '';
}

function buildUnpaidLeaseSmsText(lease) {
  const balance =
    lease.balanceOwedValue > 0
      ? lease.balanceOwed
      : (() => {
          const raw =
            parseFloat(String(lease.balance || '0').replace(/[$,]/g, '')) || 0;
          return (
            '$' +
            Math.round(Math.abs(raw))
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          );
        })();
  const property = lease.property || 'your property';
  return (
    `You are currently carrying a balance of ${balance} on your rent for ${property}. ` +
    `Your recent payment attempt failed to process. Please make a payment via cashiers check ` +
    `or money order and place in the dropbox at 315 Richard Terrace.`
  );
}

function openUnpaidLeaseSmsDialog(lease) {
  unpaidSmsLease.value = lease;
  unpaidSmsPhone.value = getFirstPhone(lease.phones);
  unpaidSmsMessage.value = buildUnpaidLeaseSmsText(lease);
  unpaidSmsError.value = unpaidSmsPhone.value
    ? ''
    : 'No phone number on file for this tenant.';
  showUnpaidSmsDialog.value = true;
}

function closeUnpaidSmsDialog() {
  showUnpaidSmsDialog.value = false;
  unpaidSmsLease.value = null;
  unpaidSmsPhone.value = '';
  unpaidSmsMessage.value = '';
  unpaidSmsError.value = '';
}

function sendUnpaidLeaseSms() {
  const phone = (unpaidSmsPhone.value || '').trim();
  const message = (unpaidSmsMessage.value || '').trim();
  if (!phone) {
    unpaidSmsError.value = 'No phone number on file for this tenant.';
    return;
  }
  if (!message) {
    unpaidSmsError.value = 'Message cannot be empty.';
    return;
  }
  unpaidSmsError.value = '';
  const encodedMessage = encodeURIComponent(message);
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const smsLink = isIOS
    ? `sms:${phone}&body=${encodedMessage}`
    : `sms:${phone}?body=${encodedMessage}`;
  window.location.href = smsLink;
}

/** Append a one-time tenant-app ledger deep link to the SMS body. */
async function appendLedgerLinkToSms() {
  if (!unpaidSmsLease.value || unpaidSmsLedgerLoading.value) return;
  unpaidSmsLedgerLoading.value = true;
  unpaidSmsError.value = '';
  try {
    const lease = unpaidSmsLease.value;
    const response = await axios.post('/api/tenant-temp-auth-keys', {
      destination: 'ledger',
      peopleId: lease.peopleId || undefined,
      leaseId: lease.leaseId || undefined,
      phone: unpaidSmsPhone.value || undefined,
    });
    const url = response.data?.url;
    if (!url) {
      unpaidSmsError.value = 'Could not create ledger link.';
      return;
    }
    const block = `\n\nView your rent ledger:\n\n${url}`;
    const current = unpaidSmsMessage.value || '';
    if (current.includes(url) || current.includes('View your rent ledger:')) {
      // Replace previous ledger link block if staff clicks again (new key)
      unpaidSmsMessage.value = current.replace(
        /\n\nView your rent ledger:(?:\n|<br\s*\/?>)*\s*\S+/gi,
        block,
      );
      if (!unpaidSmsMessage.value.includes(url)) {
        unpaidSmsMessage.value = current.trimEnd() + block;
      }
    } else {
      unpaidSmsMessage.value = current.trimEnd() + block;
    }
  } catch (error) {
    console.error('Error creating tenant temp auth key:', error);
    unpaidSmsError.value =
      error.response?.data?.error ||
      'Failed to create ledger link. Try again.';
  } finally {
    unpaidSmsLedgerLoading.value = false;
  }
}

function clearUnpaidLongPressTimer() {
  if (unpaidLongPressTimer) {
    clearTimeout(unpaidLongPressTimer);
    unpaidLongPressTimer = null;
  }
}

/** Mobile: long-press a selected row to open SMS (does not toggle selection). */
function handleUnpaidLeaseTouchStart(lease, event) {
  if (!isMobile.value) return;
  unpaidLongPressMoved = false;
  unpaidSuppressClickAfterLongPress = false;
  const touch = event.touches && event.touches[0];
  unpaidTouchStartPos = touch
    ? { x: touch.clientX, y: touch.clientY }
    : null;
  clearUnpaidLongPressTimer();
  unpaidLongPressTimer = setTimeout(() => {
    unpaidLongPressTimer = null;
    if (unpaidLongPressMoved) return;
    // Only open SMS when the row is already selected
    if (!isLeaseSelected(lease)) return;
    unpaidSuppressClickAfterLongPress = true;
    if (unpaidLeaseClickTimeout) {
      clearTimeout(unpaidLeaseClickTimeout);
      unpaidLeaseClickTimeout = null;
    }
    lastUnpaidLeaseTap = { property: null, time: 0 };
    openUnpaidLeaseSmsDialog(lease);
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try {
        navigator.vibrate(25);
      } catch (_) {
        /* ignore */
      }
    }
  }, UNPAID_LONG_PRESS_MS);
}

function handleUnpaidLeaseTouchMove(event) {
  if (!isMobile.value || !unpaidTouchStartPos) return;
  const touch = event.touches && event.touches[0];
  if (!touch) return;
  const dx = Math.abs(touch.clientX - unpaidTouchStartPos.x);
  const dy = Math.abs(touch.clientY - unpaidTouchStartPos.y);
  if (dx > 12 || dy > 12) {
    unpaidLongPressMoved = true;
    clearUnpaidLongPressTimer();
  }
}

function handleUnpaidLeaseTouchEnd() {
  clearUnpaidLongPressTimer();
  unpaidTouchStartPos = null;
}

function handleUnpaidLeaseContextMenu(event) {
  // Avoid the native callout after a long-press on mobile
  if (isMobile.value && event) {
    event.preventDefault();
  }
}

/** Single tap selects; double-tap opens SMS (desktop). Mobile SMS: long-press selected. */
function handleUnpaidLeaseRowClick(lease) {
  if (unpaidSuppressClickAfterLongPress) {
    unpaidSuppressClickAfterLongPress = false;
    return;
  }

  const now = Date.now();
  // Desktop double-click path via synthetic double-tap (kept for non-mobile)
  if (
    !isMobile.value &&
    lastUnpaidLeaseTap.property === lease.property &&
    now - lastUnpaidLeaseTap.time < 350
  ) {
    if (unpaidLeaseClickTimeout) {
      clearTimeout(unpaidLeaseClickTimeout);
      unpaidLeaseClickTimeout = null;
    }
    lastUnpaidLeaseTap = { property: null, time: 0 };
    openUnpaidLeaseSmsDialog(lease);
    return;
  }

  lastUnpaidLeaseTap = { property: lease.property, time: now };
  if (unpaidLeaseClickTimeout) {
    clearTimeout(unpaidLeaseClickTimeout);
  }
  // On mobile, select immediately (no double-tap delay)
  if (isMobile.value) {
    toggleLeaseSelection(lease);
    unpaidLeaseClickTimeout = null;
    return;
  }
  unpaidLeaseClickTimeout = setTimeout(() => {
    toggleLeaseSelection(lease);
    unpaidLeaseClickTimeout = null;
  }, 300);
}

function handleUnpaidLeaseRowDblClick(lease, event) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  if (isMobile.value) return; // mobile uses long-press instead
  if (unpaidLeaseClickTimeout) {
    clearTimeout(unpaidLeaseClickTimeout);
    unpaidLeaseClickTimeout = null;
  }
  lastUnpaidLeaseTap = { property: null, time: 0 };
  openUnpaidLeaseSmsDialog(lease);
}

function isLeaseSelected(lease) {
  return selectedLeases.value.some((l) => l.property === lease.property);
}

function calculateSelectedLeasesTotal() {
  selectedLeasesTotal.value = selectedLeases.value.reduce((total, lease) => {
    // Convert currency string to number (remove $ and commas)
    const amount = parseFloat(lease.amount.replace(/[$,]/g, ''));
    return total + amount;
  }, 0);

  selectedLeasesBalance.value = selectedLeases.value.reduce((total, lease) => {
    // Convert balance string to number (remove $ and commas)
    const balance = parseFloat(lease.balance.replace(/[$,]/g, ''));
    return total + balance;
  }, 0);
}

function clearLeaseSelection() {
  selectedLeases.value = [];
  selectedLeasesTotal.value = 0;
  selectedLeasesBalance.value = 0;
  // Clear saved selection in localStorage
  localStorage.removeItem('selectedLeaseIds');
}

function saveSelectedLeasesToLocalStorage() {
  // Save the property addresses of selected leases to localStorage
  const selectedLeaseIds = selectedLeases.value.map((lease) => lease.property);
  localStorage.setItem('selectedLeaseIds', JSON.stringify(selectedLeaseIds));
}

function loadSelectedLeasesFromLocalStorage() {
  // Get saved lease IDs from localStorage
  const savedLeaseIds = localStorage.getItem('selectedLeaseIds');
  if (savedLeaseIds) {
    try {
      const leaseIds = JSON.parse(savedLeaseIds);
      // Find the leases in the unpaidLeasesList
      selectedLeases.value = unpaidLeasesList.value.filter((lease) =>
        leaseIds.includes(lease.property),
      );
      calculateSelectedLeasesTotal();
    } catch (e) {
      console.error('Error loading selected leases from localStorage:', e);
    }
  }
}

function showCurrentPaymentsDetails(initialTab = 'paid') {
  activePaymentsTab.value = initialTab;

  const { currentPeriodStart, currentPeriodEnd, nyDate } =
    getCurrentCyclePeriod();

  // Get payments from current cycle (effectiveDate when set, else paidOn)
  const currentPeriodPayments = payments.value.filter(
    (payment) =>
      isCountableCyclePayment(payment) &&
      isPaymentInCycle(
        payment,
        currentPeriodStart,
        currentPeriodEnd,
        nyDate,
      ),
  );

  // Sort by cycle date in descending order (most recent first)
  currentPeriodPayments.sort(
    (a, b) =>
      new Date(getPaymentCycleDate(b)) - new Date(getPaymentCycleDate(a)),
  );

  // Format payment data for display
  currentPaymentsList.value = currentPeriodPayments.map((payment) => {
    const cycleDate = getPaymentCycleDate(payment);
    const hasDistinctEffectiveDate =
      payment.effectiveDate &&
      payment.effectiveDate !== '' &&
      payment.paidOn !== payment.effectiveDate;
    return {
      property:
        parseFloat(payment.amount) === 20.0
          ? 'Application Fee'
          : payment.address || 'Unknown Property',
      tenant: payment.fullName || 'Unknown Tenant',
      transactionNumber: payment.transactionNumber || 'N/A',
      amount: formatCurrency(payment.amount),
      date: hasDistinctEffectiveDate
        ? `${formatDate(payment.paidOn)} eff. ${formatDate(cycleDate)}`
        : formatDate(cycleDate),
    };
  });

  // Format dates for API request to get bounced payments
  const startDateStr = formatDateForInput(currentPeriodStart);
  const endDateStr = formatDateForInput(currentPeriodEnd);

  // Fetch bounced payments for the current cycle
  axios
    .get('/api/bounced-payments-current-cycle', {
      params: {
        startDate: startDateStr,
        endDate: endDateStr,
      },
    })
    .then((response) => {
      bouncedPaymentLeases.value = response.data;
      console.log(
        `Loaded ${bouncedPaymentLeases.value.length} bounced payment records`,
      );

      // After getting bounced payments, populate the unpaid leases
      populateUnpaidLeases(currentPeriodStart, currentPeriodEnd, nyDate);

      // Load the previously selected leases from localStorage
      loadSelectedLeasesFromLocalStorage();
    })
    .catch((error) => {
      console.error('Error loading bounced payments:', error);
      // Continue with unpaid leases even if bounced payments fetch fails
      populateUnpaidLeases(currentPeriodStart, currentPeriodEnd, nyDate);
      loadSelectedLeasesFromLocalStorage();
    });

  // Show the popup
  showCurrentPaymentsPopup.value = true;
}

function populateUnpaidLeases(
  currentPeriodStart,
  currentPeriodEnd,
  currentDate,
) {
  unpaidLeasesList.value = buildUnpaidLeasesList({
    propertySummary: propertySummary.value,
    payments: payments.value,
    bouncedPaymentLeases: bouncedPaymentLeases.value,
    currentPeriodStart,
    currentPeriodEnd,
    currentDate,
  });
}

function handleOpenUnpaidLeasesPopup() {
  showCurrentPaymentsDetails('unpaid');
}

// Helper function to calculate the due date (typically the 1st of the month)
function calculateDueDate(currentPeriodStart) {
  // Find the 1st of the next month after the cycle start
  const dueDate = new Date(currentPeriodStart);
  dueDate.setDate(1);
  dueDate.setMonth(dueDate.getMonth() + 1);
  return formatDate(dueDate);
}

// Popup search filtering functions
function clearPopupSearch() {
  popupSearchQuery.value = '';
}

// Computed properties for filtered popup lists
const filteredEndingLeasesList = computed(() => {
  if (!popupSearchQuery.value) return endingLeasesList.value;
  const query = popupSearchQuery.value.toLowerCase();
  return endingLeasesList.value.filter(
    (lease) =>
      lease.address.toLowerCase().includes(query) ||
      lease.endsOn.includes(query) ||
      (lease.futureStartsOn && lease.futureStartsOn.includes(query)),
  );
});

const filteredExpiringLeasesList = computed(() => {
  if (!popupSearchQuery.value) return expiringLeasesList.value;
  const query = popupSearchQuery.value.toLowerCase();
  return expiringLeasesList.value.filter(
    (lease) =>
      lease.address.toLowerCase().includes(query) ||
      lease.endsOn.includes(query),
  );
});

const filteredResignedLeasesList = computed(() => {
  if (!popupSearchQuery.value) return resignedLeasesList.value;
  const query = popupSearchQuery.value.toLowerCase();
  return resignedLeasesList.value.filter(
    (lease) =>
      lease.address.toLowerCase().includes(query) ||
      lease.futureStartsOn.includes(query),
  );
});

const filteredVacantPropertiesList = computed(() => {
  if (!popupSearchQuery.value) return vacantPropertiesList.value;
  const query = popupSearchQuery.value.toLowerCase();
  return vacantPropertiesList.value.filter(
    (property) =>
      property.address.toLowerCase().includes(query) ||
      property.endsOn.includes(query) ||
      property.status.toLowerCase().includes(query),
  );
});

const filteredCurrentPaymentsList = computed(() => {
  if (!popupSearchQuery.value) return currentPaymentsList.value;
  const query = popupSearchQuery.value.toLowerCase();
  return currentPaymentsList.value.filter(
    (payment) =>
      payment.property.toLowerCase().includes(query) ||
      payment.tenant.toLowerCase().includes(query) ||
      payment.transactionNumber.toLowerCase().includes(query) ||
      payment.amount.includes(query) ||
      payment.date.includes(query),
  );
});

const filteredUnpaidLeasesList = computed(() => {
  if (!popupSearchQuery.value) return unpaidLeasesList.value;
  const query = popupSearchQuery.value.toLowerCase();
  return unpaidLeasesList.value.filter(
    (lease) =>
      lease.property.toLowerCase().includes(query) ||
      lease.tenant.toLowerCase().includes(query) ||
      lease.amount.includes(query) ||
      lease.dueDate.includes(query) ||
      lease.balance.includes(query),
  );
});

function closePopups() {
  showExpiringLeasesPopup.value = false;
  showResignedLeasesPopup.value = false;
  showVacantPropertiesPopup.value = false;
  showCurrentPaymentsPopup.value = false;
  showEndingLeasesPopup.value = false;
  closeUnpaidSmsDialog();
  // Clear search when closing popups
  popupSearchQuery.value = '';
}
</script>

<template>
  <div class="content-wrapper">
    <!-- MOTD Modal Component -->
    <MotdModal :route="$route.path" />

    <!-- ===== MAIN MENU VIEW ===== -->
    <div v-if="activeView === 'menu'" class="tools-menu">
      <div class="title-bar">
        <span style="flex: 1">Property Tools</span>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>
      <div class="menu-container">
        <button class="menu-button" @click="activeView = 'property-summary'">
          <i class="fas fa-building menu-icon"></i>
          <span>Property Summary</span>
        </button>
        <button
          class="menu-button"
          @click="$router.push('/lockcodes')"
        >
          <i class="fas fa-lock menu-icon"></i>
          <span>Lock Codes</span>
        </button>
        <button class="menu-button" @click="activeView = 'sensors'">
          <i class="fas fa-thermometer-half menu-icon"></i>
          <span>Sensors</span>
        </button>
        <button class="menu-button" @click="activeView = 'storage'">
          <i class="fas fa-box menu-icon"></i>
          <span>Storage</span>
        </button>
        <button class="menu-button" @click="activeView = 'trash'">
          <i class="fas fa-trash-alt menu-icon"></i>
          <span>Trash</span>
        </button>
        <button class="menu-button" @click="activeView = 'laundry'">
          <i class="fas fa-tshirt menu-icon"></i>
          <span>Laundry</span>
        </button>
        <button class="menu-button" @click="activeView = 'cleaners'">
          <i class="fas fa-broom menu-icon"></i>
          <span>Cleaners</span>
        </button>
        <button class="menu-button" @click="activeView = 'landscaping'">
          <i class="fas fa-leaf menu-icon"></i>
          <span>Landscaping</span>
        </button>
        <button class="menu-button" @click="activeView = 'snow-removal'">
          <i class="fas fa-snowflake menu-icon"></i>
          <span>Snow Removal</span>
        </button>
        <button class="menu-button" @click="activeView = 'service-contacts'">
          <i class="fas fa-address-book menu-icon"></i>
          <span>Service Contacts</span>
        </button>
        <button class="menu-button" @click="activeView = 'shop-inventory'">
          <i class="fas fa-boxes menu-icon"></i>
          <span>Shop Inventory</span>
        </button>
      </div>
    </div>

    <!-- ===== SENSORS VIEW ===== -->
    <div v-if="activeView === 'sensors'" class="tr-view sensor-view">
      <div class="title-bar">
        <button class="back-btn" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span style="flex: 1">Sensors</span>
        <button class="home" title="Refresh" @click="refreshSensors">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>
      <PropertySensors ref="propertySensorsRef" />
    </div>

    <div v-if="activeView === 'service-contacts'" class="tr-view sensor-view">
      <div class="title-bar">
        <button class="home" title="Add" @click="addServiceContact">
          <i class="fas fa-plus"></i>
        </button>
        <span style="flex: 1">Service Contacts</span>
        <button class="home" title="Back" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <button class="home" title="Refresh" @click="refreshServiceContacts">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>
      <ServiceContacts ref="serviceContactsRef" />
    </div>

    <div v-if="activeView === 'shop-inventory'" class="tr-view sensor-view">
      <div class="title-bar">
        <button class="home" title="Add" @click="addShopItem">
          <i class="fas fa-plus"></i>
        </button>
        <span style="flex: 1">Shop Inventory</span>
        <button class="home" title="Back" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <button class="home" title="Refresh" @click="refreshShopInventory">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>
      <ShopInventory ref="shopInventoryRef" />
    </div>

    <!-- ===== PROPERTY SUMMARY VIEW ===== -->
    <div v-if="activeView === 'property-summary'">
      <div class="title-bar">
        <button class="back-btn" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span style="flex: 1">Property Summary</span>
        <button @click="refreshPage" class="home">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>

      <div class="content">
        <div v-if="isLoading" class="loading">
          <i class="fas fa-spinner fa-spin"></i>
          <div>Loading property data...</div>
        </div>

        <div v-else>
          <!-- Stats Container with collapse/expand functionality -->
          <div class="stats-section" v-if="role < 3">
            <!-- Stats container with transition for smooth animation -->
            <transition name="collapse">
              <div v-if="!statsCollapsed" class="stats-container">
                <!-- Occupancy Stats -->
                <div
                  class="stat-tile"
                  @click="showVacantPropertiesDetails"
                  style="cursor: pointer"
                >
                  <div class="stat-header">Occupancy</div>
                  <div class="stat-content">
                    <div class="stat-row">
                      <span class="stat-label">Occupied:</span>
                      <span class="stat-value">{{ statistics.occupied }}</span>
                      <span class="stat-percent">{{
                        statistics.occupiedPercent
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Vacant:</span>
                      <span class="stat-value">{{ statistics.vacant }}</span>
                      <span class="stat-percent">{{
                        statistics.vacantPercent
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Total:</span>
                      <span class="stat-value">{{
                        statistics.totalProperties
                      }}</span>
                      <span class="stat-percent">(100%)</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Rent Exp:</span>
                      <span class="stat-value stat-value-total" colspan="2">{{
                        formatCurrency(expectedRent)
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Loss:</span>
                      <span class="stat-value stat-value-total" colspan="2">{{
                        formatCurrency(vacantRentLoss)
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Previous Payment Period Stats -->
                <div class="stat-tile">
                  <div class="stat-header">Rent, Previous Cycle</div>
                  <div class="stat-content">
                    <div class="stat-row">
                      <span class="stat-label">Payments:</span>
                      <span class="stat-value">{{
                        statistics.paidInPrevPeriod
                      }}</span>
                      <span class="stat-percent">{{
                        statistics.paidInPrevPeriodPercent
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Total:</span>
                      <span class="stat-value stat-value-total" colspan="2">{{
                        statistics.paidInPrevPeriodSum
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label small"
                        >25th month - 24th month</span
                      >
                    </div>
                  </div>
                </div>

                <!-- Current Payment Period Stats -->
                <div
                  class="stat-tile"
                  @click="showCurrentPaymentsDetails"
                  style="cursor: pointer"
                >
                  <div class="stat-header">Rent, Current Cycle</div>
                  <div class="stat-content">
                    <div class="stat-row">
                      <span class="stat-label">Payments:</span>
                      <span class="stat-value">{{
                        statistics.paidInCurrentPeriod
                      }}</span>
                      <span class="stat-percent">{{
                        statistics.paidInCurrentPeriodPercent
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Rcvd:</span>
                      <span class="stat-value stat-value-total" colspan="2">{{
                        statistics.paidInCurrentPeriodSum
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Deposited:</span>
                      <span class="stat-value stat-value-total" colspan="2">{{
                        formatCurrency(currentCycleDeposits)
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Remaining:</span>
                      <span class="stat-value stat-value-total" colspan="2">{{
                        formatCurrency(
                          expectedRent -
                            parseFloat(
                              statistics.paidInCurrentPeriodSum.replace(
                                /[^0-9.-]+/g,
                                '',
                              ),
                            ),
                        )
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label small">25th of month to now</span>
                    </div>
                  </div>
                </div>

                <!-- Leases Ending Soon Stats -->
                <div
                  class="stat-tile"
                  @click="showEndingLeasesDetails"
                  style="cursor: pointer"
                >
                  <div class="stat-header">All Leases ending in 60d</div>
                  <div class="stat-content">
                    <div class="stat-row">
                      <span class="stat-label">Count:</span>
                      <span class="stat-value">{{
                        statistics.leasesEndingSoon
                      }}</span>
                      <span class="stat-percent">{{
                        statistics.leasesEndingSoonPercent
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label small"
                        >All endings, renewing or not</span
                      >
                    </div>
                  </div>
                </div>

                <!-- Renewing Leases Stats -->
                <div
                  class="stat-tile"
                  @click="showExpiringLeasesDetails"
                  style="cursor: pointer"
                >
                  <div class="stat-header">Exp. Leases (no resign)</div>
                  <div class="stat-content">
                    <div class="stat-row">
                      <span class="stat-label">Exp. in 30d:</span>
                      <span class="stat-value">{{
                        statistics.renewingLeasesIn30Days
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Exp. in 60d:</span>
                      <span class="stat-value">{{
                        statistics.renewingLeasesIn60Days
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Total:</span>
                      <span class="stat-value">{{
                        statistics.renewingLeases
                      }}</span>
                      <span class="stat-percent">{{
                        statistics.renewingLeasesPercent
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label small"
                        >Expiring within 60d ({{ statistics.sixtyDaysDate }})
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Re-signed Leases Stats -->
                <div
                  class="stat-tile"
                  @click="showResignedLeasesDetails"
                  style="cursor: pointer"
                >
                  <div class="stat-header">Re-signed Leases</div>
                  <div class="stat-content">
                    <div class="stat-row">
                      <span class="stat-label">Count:</span>
                      <span class="stat-value">{{
                        statistics.reSignedLeases
                      }}</span>
                      <span class="stat-percent">{{
                        statistics.reSignedLeasesPercent
                      }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label small">Resigned Leases</span>
                    </div>
                  </div>
                </div>
              </div>
            </transition>

            <!-- Show stats bar (appears when stats are collapsed) -->
            <transition name="fade">
              <div
                v-if="statsCollapsed"
                class="show-stats-bar"
                @click="toggleStats"
              >
                Show property stats
              </div>
            </transition>

            <!-- Toggle caret button - Only show when stats are visible -->
            <div
              v-if="!statsCollapsed"
              class="stats-toggle-btn"
              @click="toggleStats"
            >
              <i class="fas fa-caret-up"></i>
            </div>
          </div>

          <!-- Property Photos Section -->
          <div
            class="property-photos-section"
            :class="{ 'no-stats': role >= 3 }"
          >
            <!-- Property photo coverage progress bar -->
            <div class="property-coverage-bar" v-if="coverageStats">
              <div class="coverage-label">
                <span class="section-title-inline">Photos</span> -
                {{ coverageStats.withPhotos }} of
                {{ coverageStats.total }} properties ({{
                  coverageStats.percentage
                }}%)
              </div>
              <div class="progress-bar">
                <div
                  class="progress-bar-fill"
                  :style="{ width: coverageStats.percentage + '%' }"
                ></div>
              </div>
            </div>

            <!-- Property Search View -->
            <div
              class="property-photos-content"
              :class="{
                'content-blurred': photoDialog,
                'stats-collapsed': statsCollapsed,
              }"
            >
              <div v-if="!selectedProperty">
                <div class="search-bar">
                  <input
                    ref="searchInput"
                    v-model="searchQuery"
                    @input="filterProperties"
                    @focus="
                      searchIsFocused = true;
                      statsCollapsed = true;
                    "
                    @blur="searchIsFocused = false"
                    placeholder="Search address"
                  />
                  <button class="clear-search" @click="clearSearch">
                    <i class="fas fa-backspace"></i>
                  </button>
                </div>

                <!-- Empty state with recent photos carousel -->
                <div
                  v-if="
                    !searchQuery &&
                    !filteredProperties.length &&
                    !searchIsFocused
                  "
                  class="recent-photos-container"
                >
                  <!-- Show loading indicator while loading recent photos -->
                  <div v-if="isLoadingRecentPhotos" class="loading-indicator">
                    <i class="fas fa-spinner fa-spin"></i> Loading recent
                    photos...
                  </div>

                  <!-- Display carousel when photos are loaded -->
                  <div
                    v-else-if="recentPhotos.length > 0"
                    class="carousel-container"
                    @mouseenter="stopCarouselAutoAdvance"
                    @mouseleave="startCarouselAutoAdvance"
                  >
                    <div class="carousel-title">Recent Property Photos</div>

                    <div class="carousel-content">
                      <div
                        v-for="(photo, index) in recentPhotos"
                        :key="photo.id"
                        class="carousel-item"
                        :class="{ active: index === currentCarouselIndex }"
                      >
                        <img
                          :src="photo.photoUrl"
                          :alt="photo.caption || 'Property photo'"
                          @click="
                            openPhotoDialog(recentPhotos[currentCarouselIndex])
                          "
                        />

                        <div class="carousel-item-details">
                          <div
                            v-if="photo.caption"
                            class="carousel-item-caption"
                          >
                            {{ photo.caption }}
                          </div>

                          <div class="carousel-nav-container">
                            <button
                              v-if="recentPhotos.length > 1"
                              class="carousel-nav-bottom carousel-prev-bottom"
                              @click.stop.prevent="prevCarouselPhoto($event)"
                            >
                              <i class="fas fa-chevron-left"></i>
                            </button>

                            <div class="carousel-item-property">
                              {{ formatAddress(photo.propertyAddress) }}
                            </div>

                            <button
                              v-if="recentPhotos.length > 1"
                              class="carousel-nav-bottom carousel-next-bottom"
                              @click.stop.prevent="nextCarouselPhoto($event)"
                            >
                              <i class="fas fa-chevron-right"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Carousel Dots -->
                    <div v-if="recentPhotos.length > 1" class="carousel-dots">
                      <span
                        v-for="(_, index) in recentPhotos"
                        :key="index"
                        class="carousel-dot"
                        :class="{ active: index === currentCarouselIndex }"
                        @click="currentCarouselIndex = index"
                      ></span>
                    </div>
                  </div>

                  <!-- Show message if no recent photos -->
                  <div v-else class="no-recent-photos">
                    No property photos have been uploaded yet.
                  </div>
                </div>

                <div v-show="filteredProperties.length" class="match-label">
                  Matches:
                </div>
                <div v-if="isLoadingPhotos" class="loading-indicator">
                  <i class="fas fa-spinner fa-spin"></i> Loading property
                  data...
                </div>
                <ul v-else-if="filteredProperties.length">
                  <li
                    v-for="property in filteredProperties.slice(0, 8)"
                    :key="property.id"
                    @click="selectProperty(property)"
                  >
                    <div class="property-info">
                      <div
                        v-html="
                          highlightMatch(
                            formatAddress(property.propertyAddress),
                            searchQuery,
                          )
                        "
                      ></div>
                    </div>

                    <span v-if="property.photoCount > 0" class="badge">
                      {{ property.photoCount }}
                    </span>
                  </li>
                </ul>
                <div
                  v-else-if="searchQuery && !filteredProperties.length"
                  class="no-results"
                >
                  No properties found matching "{{ searchQuery }}"
                </div>
              </div>

              <!-- Property photos list -->
              <div v-if="selectedProperty" class="property-details">
                <div class="back-button-2" @click="backToProperties">
                  <i class="fas fa-arrow-left"></i> Back to Search
                </div>
                <h3>{{ formatAddress(selectedProperty.propertyAddress) }}</h3>

                <button class="add-button" @click="openPhotoUploadDialog">
                  <i class="fas fa-plus"></i> Add Photo
                </button>

                <div v-if="photos.length === 0" class="no-photos">
                  No photos found for this property.
                </div>

                <div v-else class="photo-gallery">
                  <div
                    class="gallery-item"
                    v-for="photo in photos"
                    :key="photo.id"
                    @click="openPhotoDialog(photo)"
                  >
                    <img
                      :src="photo.photoUrl"
                      :alt="photo.caption || 'Property photo'"
                    />
                    <div v-if="photo.caption" class="gallery-caption">
                      {{ photo.caption }}
                    </div>
                    <button
                      class="delete-photo-button"
                      v-if="isEditingPhotos"
                      @click="confirmDeletePhoto(photo, $event)"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>

                <div class="edit-mode-toggle">
                  <button class="toggle-button" @click="toggleEditMode()">
                    {{ isEditingPhotos ? 'Done Editing' : 'Edit Photos' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Photo Upload Dialog -->
      <div
        v-if="photoDialog"
        class="dialog-overlay"
        @click="photoDialog = false"
      >
        <div class="dialog-content" @click.stop>
          <div class="dialog-header">Upload Photo</div>
          <div class="dialog-body">
            <div class="upload-instructions">
              Select images from your device to upload:
            </div>
            <div class="upload-options">
              <label class="upload-option">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  @change="handlePhotoSelect"
                  class="hidden-input"
                />
                <div class="option-button">
                  <i class="fas fa-images"></i>
                  <div>Gallery</div>
                </div>
              </label>
              <label class="upload-option">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  @change="handlePhotoSelect"
                  class="hidden-input"
                />
                <div class="option-button">
                  <i class="fas fa-camera"></i>
                  <div>Camera</div>
                </div>
              </label>
            </div>
            <div class="preview-container" v-if="photoPreviewUrls.length">
              <div
                v-for="(url, index) in photoPreviewUrls"
                :key="index"
                class="photo-preview-item"
              >
                <img :src="url" class="photo-preview" />
              </div>
            </div>
            <input
              v-model="photoCaption"
              placeholder="Caption (optional)"
              class="caption-input"
            />
          </div>
          <div class="dialog-actions">
            <button @click="photoDialog = false" :disabled="isUploading">
              Cancel
            </button>
            <button
              class="confirm-button"
              :disabled="!photoFiles.length || isUploading"
              @click="uploadPhoto"
            >
              <span v-if="isUploading">
                <i class="fas fa-spinner fa-spin"></i> Uploading...
              </span>
              <span v-else>Upload</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Photo View Dialog -->
      <div
        v-if="viewPhotoDialog"
        class="photo-viewer"
        @click="closePhotoDialog"
      >
        <div
          class="photo-viewer-content"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
          @dblclick="handleDoubleTap"
        >
          <button class="share-fullscreen-button" @click.stop="sharePhoto">
            <i class="fas fa-share-alt"></i>
          </button>
          <img
            v-if="selectedPhoto"
            :src="selectedPhoto.photoUrl"
            :alt="selectedPhoto.caption || 'Property photo'"
            class="full-size-photo"
            :style="{
              transform: `scale(${photoScale}) translate(${photoTranslateX}px, ${photoTranslateY}px)`,
            }"
          />
          <button
            class="delete-fullscreen-button"
            @click.stop="confirmDeletePhoto(selectedPhoto, $event)"
          >
            <i class="fas fa-trash"></i>
          </button>
          <div class="photo-viewer-actions">
            <div class="photo-viewer-caption" v-if="selectedPhoto">
              {{ selectedPhoto.caption || 'No caption' }}
              <button
                class="edit-caption-button"
                @click.stop="openEditCaptionDialog"
              >
                <i class="fas fa-pencil-alt"></i>
              </button>
            </div>
          </div>
          <div class="photo-viewer-close">tap to close</div>
        </div>
      </div>

      <!-- Edit Caption Dialog -->
      <div v-if="editCaptionDialog" class="dialog-overlay" @click.stop>
        <div class="dialog-content" @click.stop>
          <div class="dialog-header">Edit Caption</div>
          <div class="dialog-body">
            <textarea
              v-model="editingCaption"
              placeholder="Enter caption"
              class="caption-input"
            ></textarea>
          </div>
          <div class="dialog-actions">
            <button @click="cancelEditCaption">Cancel</button>
            <button class="confirm-button" @click="updatePhotoCaption">
              Save
            </button>
          </div>
        </div>
      </div>

      <!-- Confirm Delete Dialog -->
      <div
        v-if="confirmDialog"
        class="dialog-overlay"
        @click="confirmDialog = false"
      >
        <div class="dialog-content" @click.stop>
          <div class="dialog-header">Confirm Delete</div>
          <div class="dialog-body">
            {{ confirmMessage }}
          </div>
          <div class="dialog-actions">
            <button
              @click="confirmDialog = false"
              class="confirm-dialog-button cancel-button"
            >
              Cancel
            </button>
            <button
              @click="confirmDelete"
              class="confirm-dialog-button delete-confirm-button"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Expiring Leases Popup -->
      <div
        v-if="showExpiringLeasesPopup"
        class="popup-overlay"
        @click="closePopups"
      >
        <div class="popup-content" @click.stop>
          <div class="popup-header">Expiring Leases (no re-sign)</div>
          <div class="popup-body">
            <div class="popup-search-bar">
              <input
                v-model="popupSearchQuery"
                placeholder="Search by address or date..."
                class="popup-search-input"
              />
              <button
                v-if="popupSearchQuery"
                @click="clearPopupSearch"
                class="popup-clear-search"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="property-list">
              <div
                v-for="lease in filteredExpiringLeasesList"
                :key="lease.address"
                class="property-item"
              >
                <div class="property-address">{{ lease.address }}</div>
                <div class="property-date">{{ lease.endsOn }}</div>
              </div>
            </div>
          </div>
          <div class="popup-footer">
            <button @click="closePopups">Close</button>
          </div>
        </div>
      </div>

      <!-- Resigned Leases Popup -->
      <div
        v-if="showResignedLeasesPopup"
        class="popup-overlay"
        @click="closePopups"
      >
        <div class="popup-content" @click.stop>
          <div class="popup-header">Re-signing Leases</div>
          <div class="popup-body">
            <div class="popup-search-bar">
              <input
                v-model="popupSearchQuery"
                placeholder="Search by address or date..."
                class="popup-search-input"
              />
              <button
                v-if="popupSearchQuery"
                @click="clearPopupSearch"
                class="popup-clear-search"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="property-list">
              <div
                v-for="lease in filteredResignedLeasesList"
                :key="lease.address"
                class="property-item"
              >
                <div class="property-address">{{ lease.address }}</div>
                <div class="property-date">{{ lease.futureStartsOn }}</div>
              </div>
            </div>
          </div>
          <div class="popup-footer">
            <button @click="closePopups">Close</button>
          </div>
        </div>
      </div>

      <!-- Vacant Properties Popup -->
      <div
        v-if="showVacantPropertiesPopup"
        class="popup-overlay"
        @click="closePopups"
      >
        <div class="popup-content" @click.stop>
          <div class="popup-header">Vacant Properties or Expired Leases</div>
          <div class="popup-body">
            <div class="popup-search-bar">
              <input
                v-model="popupSearchQuery"
                placeholder="Search by address, date, or status..."
                class="popup-search-input"
              />
              <button
                v-if="popupSearchQuery"
                @click="clearPopupSearch"
                class="popup-clear-search"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="property-list">
              <div
                v-for="property in filteredVacantPropertiesList"
                :key="property.address"
                class="property-item"
              >
                <div class="property-address">{{ property.address }}</div>
                <div class="property-date">{{ property.endsOn }}</div>
                <div class="property-status">{{ property.status }}</div>
              </div>
            </div>
          </div>
          <div class="popup-footer">
            <button @click="closePopups">Close</button>
          </div>
        </div>
      </div>

      <!-- Current Payments Popup -->
      <div
        v-if="showCurrentPaymentsPopup"
        class="popup-overlay"
        @click="closePopups"
      >
        <div class="popup-content payments-popup" @click.stop>
          <div class="popup-header">Current Cycle Payments</div>

          <!-- Tab Navigation -->
          <div class="payment-tabs">
            <button
              :class="['tab-button', { active: activePaymentsTab === 'paid' }]"
              @click="activePaymentsTab = 'paid'"
            >
              Paid Leases ({{ filteredCurrentPaymentsList.length }})
            </button>
            <button
              :class="[
                'tab-button',
                { active: activePaymentsTab === 'unpaid' },
              ]"
              @click="activePaymentsTab = 'unpaid'"
            >
              Unpaid Leases ({{ filteredUnpaidLeasesList.length }})
            </button>
          </div>

          <div class="popup-body">
            <!-- Search Bar (above tabs content) -->
            <div class="popup-search-bar">
              <input
                v-model="popupSearchQuery"
                placeholder="Search by name, address, amount, or date..."
                class="popup-search-input"
              />
              <button
                v-if="popupSearchQuery"
                @click="clearPopupSearch"
                class="popup-clear-search"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Paid Leases Tab Content -->
            <div
              v-if="activePaymentsTab === 'paid'"
              class="property-list payment-list"
            >
              <div
                v-for="(payment, index) in filteredCurrentPaymentsList"
                :key="index"
                class="property-item payment-item"
              >
                <div class="payment-info">
                  <div class="payment-property">{{ payment.property }}</div>
                  <div class="payment-tenant">{{ payment.tenant }}</div>
                </div>
                <div class="payment-details">
                  <div class="payment-date">{{ payment.date }}</div>
                  <div class="payment-amount">{{ payment.amount }}</div>
                  <div class="payment-transaction">
                    {{ payment.transactionNumber }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Unpaid Leases Tab Content -->
            <div
              v-else-if="activePaymentsTab === 'unpaid'"
              class="unpaid-tab-content"
            >
              <!-- Selected Leases Summary Bar - MOVED OUTSIDE of scrollable area -->
              <div class="selected-leases-summary-bar">
                <div class="summary-total">
                  <div>
                    Selected Total: {{ formatCurrency(selectedLeasesTotal) }}
                  </div>
                  <div class="balance-label">
                    Total Balances: {{ formatCurrency(selectedLeasesBalance) }}
                  </div>
                </div>
                <button
                  class="clear-selection-button"
                  @click="clearLeaseSelection"
                >
                  Unselect All
                </button>
              </div>

              <!-- Scrollable list area -->
              <div class="property-list payment-list">
                <div
                  v-for="(lease, index) in filteredUnpaidLeasesList"
                  :key="index"
                  class="property-item payment-item unpaid-item"
                  :class="{ selected: isLeaseSelected(lease) }"
                  :title="
                    isMobile
                      ? 'Tap to select · Long-press selected name to text'
                      : 'Click to select · Double-click to text tenant'
                  "
                  @click="handleUnpaidLeaseRowClick(lease)"
                  @dblclick="handleUnpaidLeaseRowDblClick(lease, $event)"
                  @touchstart.passive="handleUnpaidLeaseTouchStart(lease, $event)"
                  @touchmove.passive="handleUnpaidLeaseTouchMove($event)"
                  @touchend="handleUnpaidLeaseTouchEnd"
                  @touchcancel="handleUnpaidLeaseTouchEnd"
                  @contextmenu="handleUnpaidLeaseContextMenu($event)"
                >
                  <div class="payment-info">
                    <div class="payment-property">
                      <i
                        v-if="lease.bouncedPayment"
                        class="fas fa-exclamation-triangle payment-warning-icon"
                        :title="`Payment of ${lease.bounceAmount} bounced on ${lease.bounceDate}`"
                      >
                      </i>
                      {{ lease.property }}
                    </div>
                    <div class="payment-tenant">{{ lease.tenant }}</div>
                  </div>
                  <div class="payment-details">
                    <div class="payment-date">Due: {{ lease.dueDate }}</div>
                    <div class="payment-amount">{{ lease.amount }}</div>
                    <div class="payment-transaction">
                      {{ lease.balance }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="filteredUnpaidLeasesList.length === 0"
                  class="no-unpaid"
                >
                  No matching unpaid leases found.
                </div>
              </div>
            </div>
          </div>

          <div class="popup-footer">
            <button @click="closePopups">Close</button>
          </div>
        </div>
      </div>

      <!-- Unpaid lease → SMS compose (double-tap row) -->
      <div
        v-if="showUnpaidSmsDialog"
        class="popup-overlay unpaid-sms-overlay"
        @click="closeUnpaidSmsDialog"
      >
        <div class="popup-content unpaid-sms-popup" @click.stop>
          <div class="popup-header">Text Tenant</div>
          <div class="popup-body unpaid-sms-body">
            <div class="unpaid-sms-meta" v-if="unpaidSmsLease">
              <div>
                <strong>Tenant:</strong> {{ unpaidSmsLease.tenant }}
              </div>
              <div>
                <strong>Property:</strong> {{ unpaidSmsLease.property }}
              </div>
            </div>
            <label class="unpaid-sms-label" for="unpaid-sms-phone">Phone</label>
            <input
              id="unpaid-sms-phone"
              v-model="unpaidSmsPhone"
              class="unpaid-sms-phone"
              type="tel"
              placeholder="Phone number"
              autocomplete="tel"
            />
            <label class="unpaid-sms-label" for="unpaid-sms-message"
              >Message</label
            >
            <textarea
              id="unpaid-sms-message"
              v-model="unpaidSmsMessage"
              class="unpaid-sms-message"
              rows="8"
              placeholder="SMS message"
            ></textarea>
            <div v-if="unpaidSmsError" class="unpaid-sms-error">
              {{ unpaidSmsError }}
            </div>
          </div>
          <div class="popup-footer unpaid-sms-footer">
            <button
              type="button"
              class="ledger-link-button"
              :disabled="unpaidSmsLedgerLoading"
              title="Append a one-time rent ledger link"
              @click="appendLedgerLinkToSms"
            >
              <span v-if="unpaidSmsLedgerLoading">Working…</span>
              <span v-else
                >Ledger <i class="fas fa-external-link-alt"></i
              ></span>
            </button>
            <div class="unpaid-sms-footer-actions">
              <button @click="closeUnpaidSmsDialog">Cancel</button>
              <button class="confirm-button" @click="sendUnpaidLeaseSms">
                Send SMS
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Ending Leases Popup -->
      <div
        v-if="showEndingLeasesPopup"
        class="popup-overlay"
        @click="closePopups"
      >
        <div class="popup-content" @click.stop>
          <div class="popup-header">All Leases Ending in 60 Days</div>
          <div class="popup-body">
            <div class="popup-search-bar">
              <input
                v-model="popupSearchQuery"
                placeholder="Search by address or date..."
                class="popup-search-input"
              />
              <button
                v-if="popupSearchQuery"
                @click="clearPopupSearch"
                class="popup-clear-search"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="property-list">
              <!-- Column headers -->
              <div class="property-item header">
                <div class="property-address">Property</div>
                <div class="property-date-label">Ends On</div>
                <div class="property-date-label">Future Start</div>
              </div>
              <div
                v-for="lease in filteredEndingLeasesList"
                :key="lease.address"
                class="property-item"
              >
                <div class="property-address">{{ lease.address }}</div>
                <div class="property-date">{{ lease.endsOn }}</div>
                <div class="property-date">{{ lease.futureStartsOn }}</div>
              </div>
            </div>
          </div>
          <div class="popup-footer">
            <button @click="closePopups">Close</button>
          </div>
        </div>
      </div>

      <!-- Share Toast -->
      <div v-if="showShareToast" class="share-toast">
        {{ shareToastMessage }}
      </div>

      <!-- Share Error Dialog -->
      <div
        v-if="showShareErrorDialog"
        class="dialog-overlay"
        @click="showShareErrorDialog = false"
      >
        <div class="dialog-content" @click.stop>
          <div class="dialog-header">Share Error</div>
          <div class="dialog-body">
            <p>{{ shareErrorMessage }}</p>
            <p v-if="selectedPhoto" class="share-url">
              <strong>URL:</strong> {{ shareUrlText }}
            </p>
            <p class="share-tip">
              In development mode, clipboard access is restricted. On production
              with HTTPS, this feature will work automatically.
            </p>
          </div>
          <div class="dialog-actions">
            <button @click="showShareErrorDialog = false">Close</button>
          </div>
        </div>
      </div>

      <!-- Upload Error Dialog -->
      <div
        v-if="showUploadErrorDialog"
        class="dialog-overlay"
        @click="showUploadErrorDialog = false"
      >
        <div class="dialog-content" @click.stop>
          <div class="dialog-header">Upload Error</div>
          <div class="dialog-body">
            <p>{{ uploadErrorMessage }}</p>
            <p v-if="uploadErrorDetails" class="error-details">
              <strong>Details:</strong> {{ uploadErrorDetails }}
            </p>
          </div>
          <div class="dialog-actions">
            <button @click="showUploadErrorDialog = false">Close</button>
          </div>
        </div>
      </div>
    </div>
    <!-- end property-summary view -->

    <!-- ===== TRASH VIEW ===== -->
    <div v-if="activeView === 'trash'" class="tr-view">
      <!-- Alert Popup -->
      <div
        v-if="trashShowAlertPopup"
        class="tr-alert-overlay"
        @click="trashCloseAlert"
      ></div>
      <div v-if="trashShowAlertPopup" class="tr-alert-popup">
        <h4><i class="fas fa-envelope"></i> Trash Alert</h4>
        <div class="tr-alert-context">{{ trashAlertAddress }}</div>
        <div v-if="!trashAlertResults">
          <textarea
            v-model="trashAlertMessage"
            class="tr-alert-textarea"
            placeholder="Enter message to send to tenant(s)..."
            rows="4"
          ></textarea>
          <div class="tr-alert-actions">
            <button class="tr-cancel-btn" @click="trashCloseAlert">
              Cancel
            </button>
            <button
              class="tr-send-btn"
              @click="trashSendAlert"
              :disabled="trashAlertSending || !trashAlertMessage.trim()"
            >
              <i v-if="trashAlertSending" class="fas fa-spinner fa-spin"></i>
              <span v-else>Send</span>
            </button>
          </div>
          <div class="tr-alert-preview">
            <div class="tr-alert-preview-label">Preview</div>
            <div class="tr-alert-preview-body" v-html="trashAlertRenderedPreview"></div>
          </div>
          <div class="tr-alert-recipients">
            <div class="tr-alert-recipients-label">Recipients</div>
            <div
              v-if="trashAlertRecipientsLoading"
              class="tr-alert-recipients-status"
            >
              Loading recipients...
            </div>
            <div
              v-else-if="trashAlertRecipientsError"
              class="tr-alert-recipients-error"
            >
              {{ trashAlertRecipientsError }}
            </div>
            <div
              v-if="!trashAlertRecipientsLoading && trashAlertFallback"
              class="tr-alert-fallback"
            >
              <i class="fas fa-info-circle"></i>
              No tenant emails found — message will be sent to:
            </div>
            <div
              v-if="!trashAlertRecipientsLoading && trashAlertDeliveryNote"
              class="tr-alert-delivery-note"
            >
              {{ trashAlertDeliveryNote }}
            </div>
            <ul
              v-if="!trashAlertRecipientsLoading && trashAlertRecipients.length"
              class="tr-alert-recipient-list"
            >
              <li
                v-for="(recipient, index) in trashAlertRecipients"
                :key="`${recipient.email}-${index}`"
              >
                <span v-if="recipient.name">{{ recipient.name }} — </span
                >{{ recipient.email }}
              </li>
            </ul>
          </div>
        </div>
        <div v-else class="tr-alert-results">
          <div v-if="trashAlertFallback" class="tr-alert-fallback">
            <i class="fas fa-info-circle"></i> Message will be sent to
            communications
          </div>
          <div
            v-for="(r, i) in trashAlertResults"
            :key="i"
            :class="r.success ? 'tr-result-ok' : 'tr-result-fail'"
          >
            <i :class="r.success ? 'fas fa-check' : 'fas fa-times'"></i>
            {{ r.name || r.email || 'Unknown' }}
            <span v-if="!r.success"> — {{ r.error }}</span>
          </div>
          <button
            class="tr-send-btn"
            @click="trashCloseAlert"
            style="margin-top: 10px"
          >
            Close
          </button>
        </div>
      </div>

      <div class="title-bar">
        <button class="back-btn" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span style="flex: 1">Trash</span>
        <button class="home" @click="loadTrashData" title="Refresh">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>

      <div class="tr-content">
        <!-- Filter bar -->
        <div class="tr-filter-bar">
          <input
            ref="trashFilterRef"
            v-model="trashFilterQuery"
            type="text"
            class="tr-filter-input"
            placeholder="Search properties..."
          />
          <button
            v-if="trashFilterQuery"
            class="tr-clear-btn"
            @click="
              trashFilterQuery = '';
              trashFilterRef?.focus();
            "
          >
            <i class="fas fa-backspace"></i>
          </button>
        </div>

        <!-- Button row -->
        <div class="tr-button-row">
          <button
            class="tr-action-btn"
            :class="{ 'tr-action-btn-active': trashStatusFilter === 'blank' }"
            @click="
              trashStatusFilter = trashStatusFilter === 'blank' ? null : 'blank'
            "
          >
            Show Blank
          </button>
          <button
            class="tr-action-btn"
            :class="{ 'tr-action-btn-active': trashStatusFilter === 'done' }"
            @click="
              trashStatusFilter = trashStatusFilter === 'done' ? null : 'done'
            "
          >
            Show Done
          </button>
          <button class="tr-action-btn" @click="trashCollapseAll">
            <i class="fas fa-compress-alt"></i> Collapse
          </button>
          <button
            class="tr-action-btn tr-print-btn"
            title="Print trash report"
            @click="trashPrintReport"
          >
            <i class="fas fa-print"></i>
          </button>
        </div>

        <!-- Loading -->
        <div v-if="trashLoading" class="tr-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading...
        </div>

        <!-- Card list -->
        <div v-else class="tr-card-list">
          <div v-if="trashGroupedProperties.length === 0" class="tr-empty">
            <i class="fas fa-trash-alt"></i>
            <div>No properties found</div>
          </div>

          <div
            v-for="group in trashGroupedProperties"
            :key="group.parent.propertyId"
            class="tr-group"
          >
            <!-- Parent card -->
            <div
              class="tr-card"
              :class="{
                'tr-card-focused': trashIsFocused(group.parent.propertyId),
              }"
              @click="trashToggleFocus(group.parent.propertyId)"
            >
              <div class="tr-card-header">
                <div class="tr-card-address">
                  {{ group.parent.address }}
                  <span v-if="trashCardCount(group)" class="lm-machine-counts">
                    {{ trashCardCount(group) }}
                  </span>
                  <i
                    v-if="trashSaving.has(group.parent.propertyId)"
                    class="fas fa-circle-notch fa-spin tr-saving-icon"
                  ></i>
                </div>
                <button
                  v-if="group.children.length > 0"
                  class="tr-collapse-btn"
                  @click.stop="trashToggleCollapse(group.parent.propertyId)"
                >
                  <i
                    :class="
                      trashIsCollapsed(group.parent.propertyId)
                        ? 'fas fa-chevron-right'
                        : 'fas fa-chevron-down'
                    "
                  ></i>
                  <span class="tr-child-count">{{
                    group.children.length
                  }}</span>
                </button>
              </div>

              <!-- Tenants -->
              <div
                v-if="group.parent.isRentable && group.parent.tenants.length"
                class="tr-tenants"
              >
                <span
                  v-for="(t, ti) in group.parent.tenants"
                  :key="ti"
                  class="tr-tenant-name"
                  :class="{ 'tr-tenant-vacant': t.name === 'Vacant' }"
                  >{{ t.name }}</span
                >
              </div>

              <!-- Trash details (shown when focused, day-filtered, or bin-matched) -->
              <div
                v-if="
                  trashIsFocused(group.parent.propertyId) ||
                  trashHasDayFilter ||
                  trashHasBinMatch(group.parent)
                "
                class="tr-card-body"
                @click.stop
              >
                <!-- Bins header with + button -->
                <div
                  class="tr-bins-header"
                  @mouseenter="
                    trashSetHoveredBinProperty(group.parent.propertyId)
                  "
                  @mouseleave="
                    trashClearHoveredBinProperty(group.parent.propertyId)
                  "
                >
                  <span class="tr-bins-title"
                    >Bins ({{ trashFilteredBins(group.parent).length }})</span
                  >
                  <button
                    class="lm-add-btn"
                    title="Add bin"
                    @click.stop="trashAddBin(group.parent)"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                  <button
                    v-if="(group.parent.bins || []).length > 0"
                    class="tr-collapse-btn"
                    @click.stop="
                      trashToggleBinCollapse(group.parent.propertyId)
                    "
                  >
                    <i
                      :class="
                        trashBinsCollapsed(group.parent.propertyId)
                          ? 'fas fa-chevron-right'
                          : 'fas fa-chevron-down'
                      "
                    ></i>
                  </button>
                </div>

                <!-- Bin cards -->
                <div
                  v-show="!trashBinsCollapsed(group.parent.propertyId)"
                  class="tr-bins-list"
                >
                  <div
                    v-for="(bin, bi) in trashFilteredBins(group.parent)"
                    :key="bin.id"
                    class="tr-bin-card"
                    @mouseenter="
                      trashSetHoveredBinProperty(group.parent.propertyId)
                    "
                    @mouseleave="
                      trashClearHoveredBinProperty(group.parent.propertyId)
                    "
                  >
                    <div class="tr-bin-card-header">
                      <span class="tr-bin-label">Bin {{ bi + 1 }}</span>
                      <div class="tr-bin-actions">
                            <button
                              v-if="bin.provider === 'Arrowaste'"
                              class="tr-bin-pickup-btn"
                              title="Compose label alert"
                              @click.stop="trashComposeLabeledBinMessage(group.parent, bin)"
                            >
                              arrowaste
                            </button>
                            <button
                              v-if="bin.provider === 'City of Grand Rapids'"
                              class="tr-bin-pickup-btn"
                              title="Open public works alert"
                              @click.stop="trashOpenPublicWorksAlert(group.parent, bin)"
                            >
                              public works
                            </button>
                        <button
                          class="tr-bin-pickup-btn"
                          title="Open pickup alert"
                              @click.stop="trashOpenPickupAlert(group.parent, bin)"
                        >
                          pickup
                        </button>
                        <button
                          class="tr-bin-delete-btn"
                          title="Delete bin"
                          @click.stop="trashDeleteBin(group.parent, bin.id)"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                    <div class="tr-field-row">
                      <label class="tr-label">Provider</label>
                      <select
                        class="tr-select"
                        :value="bin.provider || ''"
                        @change="
                          trashBinSetField(bin, 'provider', $event.target.value)
                        "
                      >
                        <option value="">— Select —</option>
                        <option value="Arrowaste">Arrowaste</option>
                        <option value="Republic">Republic</option>
                        <option value="City of Grand Rapids">
                          City of Grand Rapids
                        </option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div class="tr-field-row">
                      <label class="tr-label">Fee</label>
                      <input
                        type="number"
                        class="tr-fee-input"
                        :value="bin.fee != null ? bin.fee : trashDefaultBinFee(bin.provider)"
                        @change="
                          trashBinSetField(
                            bin,
                            'fee',
                            parseFloat($event.target.value) || 0,
                          )
                        "
                      />
                      <select
                        class="tr-select"
                        :value="bin.container_type || ''"
                        @change="
                          trashBinSetField(
                            bin,
                            'container_type',
                            $event.target.value,
                          )
                        "
                      >
                        <option value="">— Type —</option>
                        <option value="Dumpster">Dumpster</option>
                        <option value="Large Cart">Large Cart</option>
                        <option value="Small Cart">Small Cart</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div class="tr-field-row">
                      <label class="tr-label">Pickup</label>
                      <div class="tr-days-row">
                        <label
                          v-for="d in [
                            'mon',
                            'tue',
                            'wed',
                            'thu',
                            'fri',
                            'sat',
                            'sun',
                          ]"
                          :key="d"
                          class="tr-day-label"
                        >
                          <span class="tr-day-text">{{
                            d.charAt(0).toUpperCase() + d.slice(1)
                          }}</span>
                          <input
                            type="checkbox"
                            :checked="bin['pickup_' + d]"
                            @change="trashBinToggleDay(bin, d)"
                          />
                        </label>
                        <input
                          type="text"
                          class="tr-bin-number"
                          :value="bin.bin_number || ''"
                          @input="
                            trashBinSetField(
                              bin,
                              'bin_number',
                              $event.target.value,
                            )
                          "
                          placeholder="bin number"
                        />
                      </div>
                    </div>
                    <div class="tr-field-row">
                      <label class="tr-label">Notes</label>
                      <textarea
                        class="tr-notes"
                        rows="2"
                        :value="bin.notes || ''"
                        @input="
                          trashBinSetField(bin, 'notes', $event.target.value)
                        "
                        placeholder="Notes..."
                      ></textarea>
                    </div>
                    <div v-if="trashBinModifiedText(bin)" class="tr-modified">
                      {{ trashBinModifiedText(bin) }}
                    </div>
                  </div>
                </div>

                <div class="tr-field-row">
                  <button
                    class="tr-alert-btn"
                    @click="trashOpenAlert(group.parent)"
                  >
                    <i class="fas fa-envelope"></i> Alert Tenant
                  </button>
                </div>
              </div>
            </div>

            <!-- Children -->
            <div
              v-show="
                !trashIsCollapsed(group.parent.propertyId) ||
                trashHasDayFilter ||
                trashHasTextFilter
              "
              class="tr-children-wrapper"
            >
              <div
                v-for="child in group.children"
                :key="child.propertyId"
                class="tr-card tr-card-child"
                :class="{
                  'tr-card-focused': trashIsFocused(child.propertyId),
                }"
                @click="trashToggleFocus(child.propertyId)"
              >
                <div class="tr-card-header">
                  <div class="tr-card-address">
                    {{ child.address }}
                    <i
                      v-if="trashSaving.has(child.propertyId)"
                      class="fas fa-circle-notch fa-spin tr-saving-icon"
                    ></i>
                  </div>
                </div>

                <div v-if="child.tenants.length" class="tr-tenants">
                  <span
                    v-for="(t, ti) in child.tenants"
                    :key="ti"
                    class="tr-tenant-name"
                    :class="{ 'tr-tenant-vacant': t.name === 'Vacant' }"
                    >{{ t.name }}</span
                  >
                </div>

                <div
                  v-if="
                    trashIsFocused(child.propertyId) ||
                    trashHasDayFilter ||
                    trashHasTextFilter ||
                    trashHasBinMatch(child)
                  "
                  class="tr-card-body"
                  @click.stop
                >
                  <div class="tr-field-row">
                    <label class="tr-shared-label">
                      <input
                        type="checkbox"
                        :checked="getTrashVal(child, 'shared')"
                        @change="trashToggleShared(child)"
                      />
                      Shared with parent
                    </label>
                  </div>
                  <template v-if="!getTrashVal(child, 'shared')">
                    <!-- Bins header with + button -->
                    <div
                      class="tr-bins-header"
                      @mouseenter="trashSetHoveredBinProperty(child.propertyId)"
                      @mouseleave="
                        trashClearHoveredBinProperty(child.propertyId)
                      "
                    >
                      <span class="tr-bins-title"
                        >Bins ({{ trashFilteredBins(child).length }})</span
                      >
                      <button
                        class="lm-add-btn"
                        title="Add bin"
                        @click.stop="trashAddBin(child)"
                      >
                        <i class="fas fa-plus"></i>
                      </button>
                      <button
                        v-if="(child.bins || []).length > 0"
                        class="tr-collapse-btn"
                        @click.stop="trashToggleBinCollapse(child.propertyId)"
                      >
                        <i
                          :class="
                            trashBinsCollapsed(child.propertyId)
                              ? 'fas fa-chevron-right'
                              : 'fas fa-chevron-down'
                          "
                        ></i>
                      </button>
                    </div>

                    <!-- Bin cards -->
                    <div
                      v-show="!trashBinsCollapsed(child.propertyId)"
                      class="tr-bins-list"
                    >
                      <div
                        v-for="(bin, bi) in trashFilteredBins(child)"
                        :key="bin.id"
                        class="tr-bin-card"
                        @mouseenter="trashSetHoveredBinProperty(child.propertyId)"
                        @mouseleave="
                          trashClearHoveredBinProperty(child.propertyId)
                        "
                      >
                        <div class="tr-bin-card-header">
                          <span class="tr-bin-label">Bin {{ bi + 1 }}</span>
                          <div class="tr-bin-actions">
                            <button
                              v-if="bin.provider === 'Arrowaste'"
                              class="tr-bin-pickup-btn"
                              title="Compose label alert"
                              @click.stop="trashComposeLabeledBinMessage(child, bin)"
                            >
                              arrowaste
                            </button>
                            <button
                              v-if="bin.provider === 'City of Grand Rapids'"
                              class="tr-bin-pickup-btn"
                              title="Open public works alert"
                              @click.stop="trashOpenPublicWorksAlert(child, bin)"
                            >
                              public works
                            </button>
                            <button
                              class="tr-bin-pickup-btn"
                              title="Open pickup alert"
                              @click.stop="trashOpenPickupAlert(child, bin)"
                            >
                              pickup
                            </button>
                            <button
                              class="tr-bin-delete-btn"
                              title="Delete bin"
                              @click.stop="trashDeleteBin(child, bin.id)"
                            >
                              <i class="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                        <div class="tr-field-row">
                          <label class="tr-label">Provider</label>
                          <select
                            class="tr-select"
                            :value="bin.provider || ''"
                            @change="
                              trashBinSetField(
                                bin,
                                'provider',
                                $event.target.value,
                              )
                            "
                          >
                            <option value="">— Select —</option>
                            <option value="Arrowaste">Arrowaste</option>
                            <option value="Republic">Republic</option>
                            <option value="City of Grand Rapids">
                              City of Grand Rapids
                            </option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div class="tr-field-row">
                          <label class="tr-label">Fee</label>
                          <input
                            type="number"
                            class="tr-fee-input"
                            :value="bin.fee != null ? bin.fee : trashDefaultBinFee(bin.provider)"
                            @change="
                              trashBinSetField(
                                bin,
                                'fee',
                                parseFloat($event.target.value) || 0,
                              )
                            "
                          />
                          <select
                            class="tr-select"
                            :value="bin.container_type || ''"
                            @change="
                              trashBinSetField(
                                bin,
                                'container_type',
                                $event.target.value,
                              )
                            "
                          >
                            <option value="">— Type —</option>
                            <option value="Dumpster">Dumpster</option>
                            <option value="Large Cart">Large Cart</option>
                            <option value="Small Cart">Small Cart</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div class="tr-field-row">
                          <label class="tr-label">Pickup</label>
                          <div class="tr-days-row">
                            <label
                              v-for="d in [
                                'mon',
                                'tue',
                                'wed',
                                'thu',
                                'fri',
                                'sat',
                                'sun',
                              ]"
                              :key="d"
                              class="tr-day-label"
                            >
                              <span class="tr-day-text">{{
                                d.charAt(0).toUpperCase() + d.slice(1)
                              }}</span>
                              <input
                                type="checkbox"
                                :checked="bin['pickup_' + d]"
                                @change="trashBinToggleDay(bin, d)"
                              />
                            </label>
                            <input
                              type="text"
                              class="tr-bin-number"
                              :value="bin.bin_number || ''"
                              @input="
                                trashBinSetField(
                                  bin,
                                  'bin_number',
                                  $event.target.value,
                                )
                              "
                              placeholder="bin number"
                            />
                          </div>
                        </div>
                        <div class="tr-field-row">
                          <label class="tr-label">Notes</label>
                          <textarea
                            class="tr-notes"
                            rows="2"
                            :value="bin.notes || ''"
                            @input="
                              trashBinSetField(
                                bin,
                                'notes',
                                $event.target.value,
                              )
                            "
                            placeholder="Notes..."
                          ></textarea>
                        </div>
                        <div
                          v-if="trashBinModifiedText(bin)"
                          class="tr-modified"
                        >
                          {{ trashBinModifiedText(bin) }}
                        </div>
                      </div>
                    </div>

                    <div class="tr-field-row">
                      <button
                        class="tr-alert-btn"
                        @click="trashOpenAlert(child)"
                      >
                        <i class="fas fa-envelope"></i> Alert Tenant
                      </button>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== LAUNDRY VIEW ===== -->
    <div v-if="activeView === 'laundry'" class="lm-view">
      <!-- Alert Popup -->
      <div
        v-if="laundryShowAlertPopup"
        class="tr-alert-overlay"
        @click="laundryCloseAlert"
      ></div>
      <div v-if="laundryShowAlertPopup" class="tr-alert-popup">
        <h4><i class="fas fa-envelope"></i> Laundry Alert</h4>
        <div class="tr-alert-context">{{ laundryAlertAddress }}</div>
        <div v-if="!laundryAlertResults">
          <textarea
            v-model="laundryAlertMessage"
            class="tr-alert-textarea"
            placeholder="Enter message to send to tenant(s)..."
            rows="4"
          ></textarea>
          <div class="tr-alert-actions">
            <button class="tr-cancel-btn" @click="laundryCloseAlert">
              Cancel
            </button>
            <button
              class="tr-send-btn"
              @click="laundrySendAlert"
              :disabled="laundryAlertSending || !laundryAlertMessage.trim()"
            >
              <i v-if="laundryAlertSending" class="fas fa-spinner fa-spin"></i>
              <span v-else>Send</span>
            </button>
          </div>
        </div>
        <div v-else class="tr-alert-results">
          <div v-if="laundryAlertFallback" class="tr-alert-fallback">
            <i class="fas fa-info-circle"></i> Message will be sent to
            communications
          </div>
          <div
            v-for="(r, i) in laundryAlertResults"
            :key="i"
            :class="r.success ? 'tr-result-ok' : 'tr-result-fail'"
          >
            <i :class="r.success ? 'fas fa-check' : 'fas fa-times'"></i>
            {{ r.name || r.email || 'Unknown' }}
            <span v-if="!r.success"> — {{ r.error }}</span>
          </div>
          <button
            class="tr-send-btn"
            @click="laundryCloseAlert"
            style="margin-top: 10px"
          >
            Close
          </button>
        </div>
      </div>

      <div class="title-bar">
        <button class="back-btn" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span style="flex: 1">Laundry</span>
        <button class="home" @click="loadLaundryData" title="Refresh">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>

      <div class="tr-content">
        <!-- Filter bar -->
        <div class="tr-filter-bar">
          <input
            ref="laundryFilterRef"
            v-model="laundryFilterQuery"
            type="text"
            class="tr-filter-input"
            placeholder="Search properties..."
          />
          <button
            v-if="laundryFilterQuery"
            class="tr-clear-btn"
            @click="
              laundryFilterQuery = '';
              laundryFilterRef?.focus();
            "
          >
            <i class="fas fa-backspace"></i>
          </button>
        </div>

        <!-- Button row -->
        <div class="tr-button-row">
          <button
            class="tr-action-btn"
            :class="{
              'tr-action-btn-active': laundryStatusFilter === 'blank',
            }"
            @click="
              laundryStatusFilter =
                laundryStatusFilter === 'blank' ? null : 'blank'
            "
          >
            Show Blank
          </button>
          <button
            class="tr-action-btn"
            :class="{
              'tr-action-btn-active': laundryStatusFilter === 'done',
            }"
            @click="
              laundryStatusFilter =
                laundryStatusFilter === 'done' ? null : 'done'
            "
          >
            Show Done
          </button>
          <button class="tr-action-btn" @click="laundryCollapseAll">
            <i class="fas fa-compress-alt"></i> Collapse
          </button>
        </div>

        <!-- Loading -->
        <div v-if="laundryLoading" class="tr-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading...
        </div>

        <!-- Card list -->
        <div v-else class="tr-card-list">
          <div v-if="laundryGroupedProperties.length === 0" class="tr-empty">
            <i class="fas fa-tshirt"></i>
            <div>No properties found</div>
          </div>

          <div
            v-for="group in laundryGroupedProperties"
            :key="group.parent.propertyId"
            class="tr-group"
          >
            <!-- Parent card -->
            <div
              class="tr-card"
              :class="{
                'tr-card-focused': laundryIsFocused(group.parent.propertyId),
              }"
              @click="laundryToggleFocus(group.parent.propertyId)"
            >
              <div class="tr-card-header">
                <div class="tr-card-address">
                  {{ group.parent.address }}
                  <span
                    v-if="laundryMachineCounts(group.parent)"
                    class="lm-machine-counts"
                  >
                    {{ laundryMachineCounts(group.parent) }}
                  </span>
                  <i
                    v-if="laundrySaving.has(group.parent.propertyId)"
                    class="fas fa-circle-notch fa-spin tr-saving-icon"
                  ></i>
                </div>
                <button
                  v-if="
                    laundryIsFocused(group.parent.propertyId) &&
                    (group.parent.machines || []).length > 0
                  "
                  class="lm-collapse-machines-btn"
                  title="Collapse machines"
                  @click.stop="
                    laundryToggleMachineCollapse(group.parent.propertyId)
                  "
                >
                  <i
                    :class="
                      laundryMachinesCollapsed(group.parent.propertyId)
                        ? 'fas fa-chevron-right'
                        : 'fas fa-chevron-down'
                    "
                  ></i>
                </button>
                <button
                  v-if="laundryIsFocused(group.parent.propertyId)"
                  class="lm-add-btn"
                  title="Add machine"
                  @click.stop="laundryAddMachine(group.parent, 'Washer')"
                >
                  <i class="fas fa-plus"></i>
                </button>
                <button
                  v-if="group.children.length > 0"
                  class="tr-collapse-btn"
                  @click.stop="laundryToggleCollapse(group.parent.propertyId)"
                >
                  <i
                    :class="
                      laundryIsCollapsed(group.parent.propertyId)
                        ? 'fas fa-chevron-right'
                        : 'fas fa-chevron-down'
                    "
                  ></i>
                  <span class="tr-child-count">{{
                    group.children.length
                  }}</span>
                </button>
              </div>

              <!-- Tenants -->
              <div
                v-if="group.parent.isRentable && group.parent.tenants.length"
                class="tr-tenants"
              >
                <span
                  v-for="(t, ti) in group.parent.tenants"
                  :key="ti"
                  class="tr-tenant-name"
                  :class="{ 'tr-tenant-vacant': t.name === 'Vacant' }"
                  >{{ t.name }}</span
                >
              </div>

              <!-- Machines for this parent (shown when focused and not collapsed) -->
              <div
                v-if="
                  laundryIsFocused(group.parent.propertyId) &&
                  !laundryMachinesCollapsed(group.parent.propertyId)
                "
                class="lm-machines-wrapper"
                @click.stop
              >
                <div
                  v-for="machine in group.parent.machines || []"
                  :key="machine.id"
                  class="lm-machine-card"
                  :class="laundryMachineBorderClass(machine)"
                >
                  <div class="lm-machine-header">
                    <select
                      class="lm-select-sm"
                      :value="machine.type || ''"
                      @change="
                        laundrySetField(machine, 'type', $event.target.value)
                      "
                    >
                      <option value="">— Type —</option>
                      <option value="Washer">Washer</option>
                      <option value="Dryer">Dryer</option>
                      <option value="Combo">Combo</option>
                    </select>
                    <input
                      type="number"
                      class="lm-input-cost"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      :value="machine.cost != null ? machine.cost : ''"
                      @change="
                        laundrySetField(
                          machine,
                          'cost',
                          $event.target.value
                            ? parseFloat($event.target.value)
                            : null,
                        )
                      "
                    />
                    <select
                      class="lm-select-sm"
                      :value="machine.make || ''"
                      @change="
                        laundrySetField(machine, 'make', $event.target.value)
                      "
                    >
                      <option value="">— Make —</option>
                      <option value="Whirlpool">Whirlpool</option>
                      <option value="Maytag">Maytag</option>
                      <option value="Speed Queen">Speed Queen</option>
                      <option value="Amana">Amana</option>
                      <option value="Samsung">Samsung</option>
                      <option value="LG">LG</option>
                      <option value="Roper">Roper</option>
                      <option value="ElectroLux">ElectroLux</option>
                      <option value="GE Commercial">GE Commercial</option>
                      <option value="Frigidaire">Frigidaire</option>
                      <option value="Other">Other</option>
                    </select>
                    <button
                      class="lm-delete-btn"
                      title="Remove machine"
                      @click="laundryDeleteMachine(group.parent, machine.id)"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                  <div class="lm-machine-row">
                    <div class="lm-model-serial-wrapper">
                      <input
                        :ref="setLaundryInputRef(machine.id)"
                        type="text"
                        class="lm-input-sm"
                        :placeholder="
                          laundryModelSerialToggle[machine.id] === 'serial'
                            ? 'Serial #'
                            : 'Model #'
                        "
                        :value="
                          laundryModelSerialToggle[machine.id] === 'serial'
                            ? machine.serial_number || ''
                            : machine.model_number || ''
                        "
                        @change="
                          laundrySetField(
                            machine,
                            laundryModelSerialToggle[machine.id] === 'serial'
                              ? 'serial_number'
                              : 'model_number',
                            $event.target.value,
                          )
                        "
                        @keydown.tab="laundryTabToggle(machine.id, $event)"
                      />
                      <button
                        class="lm-model-serial-toggle"
                        :title="
                          laundryModelSerialToggle[machine.id] === 'serial'
                            ? 'Show Model #'
                            : 'Show Serial #'
                        "
                        @click="toggleLaundryModelSerial(machine.id)"
                      >
                        {{
                          laundryModelSerialToggle[machine.id] === 'serial'
                            ? 'S#'
                            : 'M#'
                        }}
                      </button>
                    </div>
                    <input
                      type="number"
                      class="lm-input-xs"
                      placeholder="Age"
                      min="0"
                      :value="machine.age != null ? machine.age : ''"
                      @change="
                        laundrySetField(
                          machine,
                          'age',
                          $event.target.value
                            ? parseInt($event.target.value)
                            : null,
                        )
                      "
                    />
                    <span class="lm-stars">
                      <i
                        v-for="s in 5"
                        :key="s"
                        class="fas fa-star"
                        :class="{
                          'lm-star-active': machine.condition_rating >= s,
                        }"
                        @click="laundrySetRating(machine, s)"
                      ></i>
                    </span>
                  </div>
                  <div class="lm-machine-row">
                    <div class="lm-toggle-group">
                      <button
                        class="lm-toggle-btn"
                        :class="{
                          'lm-toggle-active': machine.fuel_type === 'Gas',
                        }"
                        @click="laundrySetField(machine, 'fuel_type', 'Gas')"
                      >
                        Gas
                      </button>
                      <button
                        class="lm-toggle-btn"
                        :class="{
                          'lm-toggle-active': machine.fuel_type === 'Electric',
                        }"
                        @click="
                          laundrySetField(machine, 'fuel_type', 'Electric')
                        "
                      >
                        Elec
                      </button>
                    </div>
                    <div class="lm-toggle-group">
                      <button
                        class="lm-toggle-btn"
                        :class="{
                          'lm-toggle-active': machine.load_type === 'Top',
                        }"
                        @click="laundrySetField(machine, 'load_type', 'Top')"
                      >
                        Top
                      </button>
                      <button
                        class="lm-toggle-btn"
                        :class="{
                          'lm-toggle-active': machine.load_type === 'Front',
                        }"
                        @click="laundrySetField(machine, 'load_type', 'Front')"
                      >
                        Front
                      </button>
                    </div>
                    <select
                      class="lm-select-xs"
                      :value="machine.pmt || ''"
                      @change="
                        laundrySetField(machine, 'pmt', $event.target.value)
                      "
                    >
                      <option value="">— Pmt —</option>
                      <option value="Coin">Coin</option>
                      <option value="App">App</option>
                      <option value="Both">Both</option>
                      <option value="None">None</option>
                    </select>
                    <select
                      class="lm-select-xs"
                      :value="machine.voltage || '120V'"
                      @change="
                        laundrySetField(machine, 'voltage', $event.target.value)
                      "
                    >
                      <option value="120V">120V</option>
                      <option value="240V">240V</option>
                    </select>
                  </div>
                  <div class="lm-machine-row">
                    <input
                      type="text"
                      class="lm-input-sm"
                      placeholder="PixieId"
                      :value="machine.pixie_id || ''"
                      @change="
                        laundrySetField(
                          machine,
                          'pixie_id',
                          $event.target.value,
                        )
                      "
                    />
                    <input
                      type="text"
                      class="lm-input-sm"
                      placeholder="ShinePayId"
                      :value="machine.shinepay_id || ''"
                      @change="
                        laundrySetField(
                          machine,
                          'shinepay_id',
                          $event.target.value,
                        )
                      "
                    />
                    <input
                      type="date"
                      class="lm-input-date"
                      :value="machine.coin_pickup_date || ''"
                      @change="
                        laundrySetField(
                          machine,
                          'coin_pickup_date',
                          $event.target.value,
                        )
                      "
                    />
                    <button
                      class="lm-coin-btn"
                      title="Set coin pickup to today"
                      @click="laundryCoinPickup(machine)"
                    >
                      <i class="fas fa-coins"></i>
                    </button>
                  </div>
                  <div class="lm-machine-row">
                    <textarea
                      class="tr-notes"
                      rows="2"
                      placeholder="Notes..."
                      :value="machine.notes || ''"
                      @input="
                        laundrySetField(machine, 'notes', $event.target.value)
                      "
                    ></textarea>
                  </div>
                  <div class="lm-machine-row lm-machine-footer">
                    <button
                      class="tr-alert-btn"
                      @click="laundryOpenAlert(group.parent)"
                    >
                      <i class="fas fa-envelope"></i> Alert
                    </button>
                    <span
                      v-if="laundryModifiedText(machine)"
                      class="tr-modified"
                    >
                      {{ laundryModifiedText(machine) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Children (apartments) -->
            <div
              v-show="!laundryIsCollapsed(group.parent.propertyId)"
              class="tr-children-wrapper"
            >
              <div
                v-for="child in group.children"
                :key="child.propertyId"
                class="tr-card tr-card-child"
                :class="{
                  'tr-card-focused': laundryIsFocused(child.propertyId),
                }"
                @click="laundryToggleFocus(child.propertyId)"
              >
                <div class="tr-card-header">
                  <div class="tr-card-address">
                    {{ child.address }}
                    <span
                      v-if="laundryMachineCounts(child)"
                      class="lm-machine-counts"
                    >
                      {{ laundryMachineCounts(child) }}
                    </span>
                    <i
                      v-if="laundrySaving.has(child.propertyId)"
                      class="fas fa-circle-notch fa-spin tr-saving-icon"
                    ></i>
                  </div>
                  <button
                    v-if="
                      laundryIsFocused(child.propertyId) &&
                      !laundryIsShared(child) &&
                      (child.machines || []).length > 0
                    "
                    class="lm-collapse-machines-btn"
                    title="Collapse machines"
                    @click.stop="laundryToggleMachineCollapse(child.propertyId)"
                  >
                    <i
                      :class="
                        laundryMachinesCollapsed(child.propertyId)
                          ? 'fas fa-chevron-right'
                          : 'fas fa-chevron-down'
                      "
                    ></i>
                  </button>
                  <button
                    v-if="
                      laundryIsFocused(child.propertyId) &&
                      !laundryIsShared(child)
                    "
                    class="lm-add-btn"
                    title="Add machine"
                    @click.stop="laundryAddMachine(child, 'Washer')"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                </div>

                <div v-if="child.tenants.length" class="tr-tenants">
                  <span
                    v-for="(t, ti) in child.tenants"
                    :key="ti"
                    class="tr-tenant-name"
                    :class="{ 'tr-tenant-vacant': t.name === 'Vacant' }"
                    >{{ t.name }}</span
                  >
                </div>

                <div
                  v-if="laundryIsFocused(child.propertyId)"
                  class="tr-card-body"
                  @click.stop
                >
                  <div class="tr-field-row">
                    <label class="tr-shared-label">
                      <input
                        type="checkbox"
                        :checked="laundryIsShared(child)"
                        @change="laundryToggleShared(child)"
                      />
                      Shared with parent
                    </label>
                  </div>
                  <template
                    v-if="
                      !laundryIsShared(child) &&
                      !laundryMachinesCollapsed(child.propertyId)
                    "
                  >
                    <div class="lm-machines-wrapper">
                      <div
                        v-for="machine in child.machines || []"
                        :key="machine.id"
                        class="lm-machine-card"
                        :class="laundryMachineBorderClass(machine)"
                      >
                        <div class="lm-machine-header">
                          <select
                            class="lm-select-sm"
                            :value="machine.type || ''"
                            @change="
                              laundrySetField(
                                machine,
                                'type',
                                $event.target.value,
                              )
                            "
                          >
                            <option value="">— Type —</option>
                            <option value="Washer">Washer</option>
                            <option value="Dryer">Dryer</option>
                            <option value="Combo">Combo</option>
                          </select>
                          <input
                            type="number"
                            class="lm-input-cost"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            :value="machine.cost != null ? machine.cost : ''"
                            @change="
                              laundrySetField(
                                machine,
                                'cost',
                                $event.target.value
                                  ? parseFloat($event.target.value)
                                  : null,
                              )
                            "
                          />
                          <select
                            class="lm-select-sm"
                            :value="machine.make || ''"
                            @change="
                              laundrySetField(
                                machine,
                                'make',
                                $event.target.value,
                              )
                            "
                          >
                            <option value="">— Make —</option>
                            <option value="Whirlpool">Whirlpool</option>
                            <option value="Maytag">Maytag</option>
                            <option value="Speed Queen">Speed Queen</option>
                            <option value="Amana">Amana</option>
                            <option value="Samsung">Samsung</option>
                            <option value="LG">LG</option>
                            <option value="Roper">Roper</option>
                            <option value="ElectroLux">ElectroLux</option>
                            <option value="GE Commercial">GE Commercial</option>
                            <option value="Frigidaire">Frigidaire</option>
                            <option value="Other">Other</option>
                          </select>
                          <button
                            class="lm-delete-btn"
                            title="Remove machine"
                            @click="laundryDeleteMachine(child, machine.id)"
                          >
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </div>
                        <div class="lm-machine-row">
                          <div class="lm-model-serial-wrapper">
                            <input
                              :ref="setLaundryInputRef(machine.id)"
                              type="text"
                              class="lm-input-sm"
                              :placeholder="
                                laundryModelSerialToggle[machine.id] ===
                                'serial'
                                  ? 'Serial #'
                                  : 'Model #'
                              "
                              :value="
                                laundryModelSerialToggle[machine.id] ===
                                'serial'
                                  ? machine.serial_number || ''
                                  : machine.model_number || ''
                              "
                              @change="
                                laundrySetField(
                                  machine,
                                  laundryModelSerialToggle[machine.id] ===
                                    'serial'
                                    ? 'serial_number'
                                    : 'model_number',
                                  $event.target.value,
                                )
                              "
                              @keydown.tab="
                                laundryTabToggle(machine.id, $event)
                              "
                            />
                            <button
                              class="lm-model-serial-toggle"
                              :title="
                                laundryModelSerialToggle[machine.id] ===
                                'serial'
                                  ? 'Show Model #'
                                  : 'Show Serial #'
                              "
                              @click="toggleLaundryModelSerial(machine.id)"
                            >
                              {{
                                laundryModelSerialToggle[machine.id] ===
                                'serial'
                                  ? 'S#'
                                  : 'M#'
                              }}
                            </button>
                          </div>
                          <input
                            type="number"
                            class="lm-input-xs"
                            placeholder="Age"
                            min="0"
                            :value="machine.age != null ? machine.age : ''"
                            @change="
                              laundrySetField(
                                machine,
                                'age',
                                $event.target.value
                                  ? parseInt($event.target.value)
                                  : null,
                              )
                            "
                          />
                          <span class="lm-stars">
                            <i
                              v-for="s in 5"
                              :key="s"
                              class="fas fa-star"
                              :class="{
                                'lm-star-active': machine.condition_rating >= s,
                              }"
                              @click="laundrySetRating(machine, s)"
                            ></i>
                          </span>
                        </div>
                        <div class="lm-machine-row">
                          <div class="lm-toggle-group">
                            <button
                              class="lm-toggle-btn"
                              :class="{
                                'lm-toggle-active': machine.fuel_type === 'Gas',
                              }"
                              @click="
                                laundrySetField(machine, 'fuel_type', 'Gas')
                              "
                            >
                              Gas
                            </button>
                            <button
                              class="lm-toggle-btn"
                              :class="{
                                'lm-toggle-active':
                                  machine.fuel_type === 'Electric',
                              }"
                              @click="
                                laundrySetField(
                                  machine,
                                  'fuel_type',
                                  'Electric',
                                )
                              "
                            >
                              Elec
                            </button>
                          </div>
                          <div class="lm-toggle-group">
                            <button
                              class="lm-toggle-btn"
                              :class="{
                                'lm-toggle-active': machine.load_type === 'Top',
                              }"
                              @click="
                                laundrySetField(machine, 'load_type', 'Top')
                              "
                            >
                              Top
                            </button>
                            <button
                              class="lm-toggle-btn"
                              :class="{
                                'lm-toggle-active':
                                  machine.load_type === 'Front',
                              }"
                              @click="
                                laundrySetField(machine, 'load_type', 'Front')
                              "
                            >
                              Front
                            </button>
                          </div>
                          <select
                            class="lm-select-xs"
                            :value="machine.pmt || ''"
                            @change="
                              laundrySetField(
                                machine,
                                'pmt',
                                $event.target.value,
                              )
                            "
                          >
                            <option value="">— Pmt —</option>
                            <option value="Coin">Coin</option>
                            <option value="App">App</option>
                            <option value="Both">Both</option>
                            <option value="None">None</option>
                          </select>
                          <select
                            class="lm-select-xs"
                            :value="machine.voltage || '120V'"
                            @change="
                              laundrySetField(
                                machine,
                                'voltage',
                                $event.target.value,
                              )
                            "
                          >
                            <option value="120V">120V</option>
                            <option value="240V">240V</option>
                          </select>
                        </div>
                        <div class="lm-machine-row">
                          <input
                            type="text"
                            class="lm-input-sm"
                            placeholder="PixieId"
                            :value="machine.pixie_id || ''"
                            @change="
                              laundrySetField(
                                machine,
                                'pixie_id',
                                $event.target.value,
                              )
                            "
                          />
                          <input
                            type="text"
                            class="lm-input-sm"
                            placeholder="ShinePayId"
                            :value="machine.shinepay_id || ''"
                            @change="
                              laundrySetField(
                                machine,
                                'shinepay_id',
                                $event.target.value,
                              )
                            "
                          />
                          <input
                            type="date"
                            class="lm-input-date"
                            :value="machine.coin_pickup_date || ''"
                            @change="
                              laundrySetField(
                                machine,
                                'coin_pickup_date',
                                $event.target.value,
                              )
                            "
                          />
                          <button
                            class="lm-coin-btn"
                            title="Set coin pickup to today"
                            @click="laundryCoinPickup(machine)"
                          >
                            <i class="fas fa-coins"></i>
                          </button>
                        </div>
                        <div class="lm-machine-row">
                          <textarea
                            class="tr-notes"
                            rows="2"
                            placeholder="Notes..."
                            :value="machine.notes || ''"
                            @input="
                              laundrySetField(
                                machine,
                                'notes',
                                $event.target.value,
                              )
                            "
                          ></textarea>
                        </div>
                        <div class="lm-machine-row lm-machine-footer">
                          <button
                            class="tr-alert-btn"
                            @click="laundryOpenAlert(child)"
                          >
                            <i class="fas fa-envelope"></i> Alert
                          </button>
                          <span
                            v-if="laundryModifiedText(machine)"
                            class="tr-modified"
                          >
                            {{ laundryModifiedText(machine) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== CLEANERS VIEW ===== -->
    <div v-if="activeView === 'cleaners'" class="lm-view">
      <!-- Alert Popup -->
      <div
        v-if="cleaningShowAlertPopup"
        class="tr-alert-overlay"
        @click="cleaningCloseAlert"
      ></div>
      <div v-if="cleaningShowAlertPopup" class="tr-alert-popup">
        <h4><i class="fas fa-envelope"></i> Cleaning Alert</h4>
        <div class="tr-alert-context">{{ cleaningAlertAddress }}</div>
        <div v-if="!cleaningAlertResults">
          <textarea
            v-model="cleaningAlertMessage"
            class="tr-alert-textarea"
            placeholder="Enter message to send to tenant(s)..."
            rows="4"
          ></textarea>
          <div class="tr-alert-actions">
            <button class="tr-cancel-btn" @click="cleaningCloseAlert">
              Cancel
            </button>
            <button
              class="tr-send-btn"
              @click="cleaningSendAlert"
              :disabled="cleaningAlertSending || !cleaningAlertMessage.trim()"
            >
              <i v-if="cleaningAlertSending" class="fas fa-spinner fa-spin"></i>
              <span v-else>Send</span>
            </button>
          </div>
        </div>
        <div v-else class="tr-alert-results">
          <div v-if="cleaningAlertFallback" class="tr-alert-fallback">
            <i class="fas fa-info-circle"></i> Message will be sent to
            communications
          </div>
          <div
            v-for="(r, i) in cleaningAlertResults"
            :key="i"
            :class="r.success ? 'tr-result-ok' : 'tr-result-fail'"
          >
            <i :class="r.success ? 'fas fa-check' : 'fas fa-times'"></i>
            {{ r.name || r.email || 'Unknown' }}
            <span v-if="!r.success"> — {{ r.error }}</span>
          </div>
          <button
            class="tr-send-btn"
            @click="cleaningCloseAlert"
            style="margin-top: 10px"
          >
            Close
          </button>
        </div>
      </div>

      <div class="title-bar">
        <button class="back-btn" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span style="flex: 1">Cleaners</span>
        <button class="home" @click="loadCleaningData" title="Refresh">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>

      <div class="tr-content">
        <!-- Filter bar -->
        <div class="tr-filter-bar">
          <input
            ref="cleaningFilterRef"
            v-model="cleaningFilterQuery"
            type="text"
            class="tr-filter-input"
            placeholder="Search properties..."
          />
          <button
            v-if="cleaningFilterQuery"
            class="tr-clear-btn"
            @click="
              cleaningFilterQuery = '';
              cleaningFilterRef?.focus();
            "
          >
            <i class="fas fa-backspace"></i>
          </button>
        </div>

        <!-- Button row -->
        <div class="tr-button-row">
          <button class="tr-action-btn" @click="cleaningCollapseAll">
            <i class="fas fa-compress-alt"></i> Collapse
          </button>
        </div>

        <!-- Loading -->
        <div v-if="cleaningLoading" class="tr-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading...
        </div>

        <!-- Card list -->
        <div v-else class="tr-card-list">
          <div v-if="cleaningGroupedProperties.length === 0" class="tr-empty">
            <i class="fas fa-broom"></i>
            <div>No properties found</div>
          </div>

          <div
            v-for="group in cleaningGroupedProperties"
            :key="group.parent.propertyId"
            class="tr-group"
          >
            <!-- Parent card -->
            <div
              class="tr-card"
              :class="{
                'tr-card-focused': cleaningIsFocused(group.parent.propertyId),
              }"
              @click="cleaningToggleFocus(group.parent.propertyId)"
            >
              <div class="tr-card-header">
                <div class="tr-card-address">
                  {{ group.parent.address }}
                  <span
                    v-if="cleaningCardCount(group)"
                    class="lm-machine-counts"
                  >
                    {{ cleaningCardCount(group) }}
                  </span>
                  <i
                    v-if="cleaningSaving.has(group.parent.propertyId)"
                    class="fas fa-circle-notch fa-spin tr-saving-icon"
                  ></i>
                </div>
                <button
                  v-if="
                    cleaningIsFocused(group.parent.propertyId) &&
                    (group.parent.locations || []).length > 0
                  "
                  class="lm-collapse-machines-btn"
                  title="Collapse locations"
                  @click.stop="
                    cleaningToggleLocationCollapse(group.parent.propertyId)
                  "
                >
                  <i
                    :class="
                      cleaningLocationsCollapsed(group.parent.propertyId)
                        ? 'fas fa-chevron-right'
                        : 'fas fa-chevron-down'
                    "
                  ></i>
                </button>
                <button
                  v-if="cleaningIsFocused(group.parent.propertyId)"
                  class="lm-add-btn"
                  title="Add location"
                  @click.stop="cleaningAddLocation(group.parent)"
                >
                  <i class="fas fa-plus"></i>
                </button>
                <button
                  v-if="group.children.length > 0"
                  class="tr-collapse-btn"
                  @click.stop="cleaningToggleCollapse(group.parent.propertyId)"
                >
                  <i
                    :class="
                      cleaningIsCollapsed(group.parent.propertyId)
                        ? 'fas fa-chevron-right'
                        : 'fas fa-chevron-down'
                    "
                  ></i>
                  <span class="tr-child-count">{{
                    group.children.length
                  }}</span>
                </button>
              </div>

              <!-- Tenants -->
              <div
                v-if="
                  group.parent.isRentable &&
                  group.parent.tenants &&
                  group.parent.tenants.length
                "
                class="tr-tenants"
              >
                <span
                  v-for="(t, ti) in group.parent.tenants"
                  :key="ti"
                  class="tr-tenant-name"
                  :class="{ 'tr-tenant-vacant': t.name === 'Vacant' }"
                  >{{ t.name }}</span
                >
              </div>

              <!-- Locations for this parent (shown when focused and not collapsed) -->
              <div
                v-if="
                  cleaningIsFocused(group.parent.propertyId) &&
                  !cleaningLocationsCollapsed(group.parent.propertyId)
                "
                class="lm-machines-wrapper"
                @click.stop
              >
                <div
                  v-for="loc in group.parent.locations || []"
                  :key="loc.id"
                  class="lm-machine-card"
                >
                  <div class="lm-machine-header">
                    <select
                      class="lm-select-sm"
                      :value="loc.location_type || ''"
                      @change="
                        cleaningSetField(
                          loc,
                          'location_type',
                          $event.target.value,
                        )
                      "
                    >
                      <option value="">— Location —</option>
                      <option value="Hallways">Hallways</option>
                      <option value="Laundry Room">Laundry Room</option>
                      <option value="Basement">Basement</option>
                      <option value="Front Lobby">Front Lobby</option>
                      <option value="Back Lobby">Back Lobby</option>
                      <option value="Yard">Yard</option>
                      <option value="Exterior">Exterior</option>
                      <option value="Driveway">Driveway</option>
                      <option value="Other">Other</option>
                    </select>
                    <button
                      class="lm-delete-btn"
                      title="Remove location"
                      @click="cleaningDeleteLocation(group.parent, loc.id)"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                  <div class="lm-machine-row">
                    <label class="cl-field-label">Every</label>
                    <input
                      type="number"
                      class="lm-input-xs"
                      placeholder="wks"
                      min="1"
                      :value="
                        loc.clean_every_weeks != null
                          ? loc.clean_every_weeks
                          : ''
                      "
                      @change="
                        cleaningSetField(
                          loc,
                          'clean_every_weeks',
                          $event.target.value
                            ? parseInt($event.target.value)
                            : null,
                        )
                      "
                    />
                    <label class="cl-field-label">wks</label>
                    <span class="lm-stars">
                      <i
                        v-for="s in 5"
                        :key="s"
                        class="fas fa-star"
                        :class="{
                          'lm-star-active': loc.condition_rating >= s,
                        }"
                        @click="cleaningSetRating(loc, s)"
                      ></i>
                    </span>
                  </div>
                  <div class="lm-machine-row">
                    <textarea
                      class="tr-notes"
                      rows="2"
                      placeholder="Notes..."
                      :value="loc.notes || ''"
                      @input="
                        cleaningSetField(loc, 'notes', $event.target.value)
                      "
                    ></textarea>
                  </div>
                  <div class="lm-machine-row">
                    <input
                      type="date"
                      class="lm-input-date"
                      :value="loc.last_cleaned_on || ''"
                      @change="
                        cleaningSetField(
                          loc,
                          'last_cleaned_on',
                          $event.target.value,
                        )
                      "
                    />
                    <button
                      class="lm-coin-btn"
                      title="Set to today"
                      @click="cleaningSetCleanedToday(loc)"
                    >
                      <i class="fas fa-calendar-day"></i>
                    </button>
                    <select
                      class="lm-select-sm"
                      :value="
                        loc.last_cleaned_by != null ? loc.last_cleaned_by : ''
                      "
                      @change="cleaningSetContractor(loc, $event.target.value)"
                    >
                      <option value="">— Cleaned by —</option>
                      <option
                        v-for="c in cleaningContractors"
                        :key="c.id"
                        :value="c.id"
                      >
                        {{ c.fullName }}
                      </option>
                    </select>
                  </div>
                  <div class="lm-machine-row lm-machine-footer">
                    <button
                      class="tr-alert-btn"
                      @click="cleaningOpenAlert(group.parent)"
                    >
                      <i class="fas fa-envelope"></i> Alert
                    </button>
                    <span v-if="cleaningModifiedText(loc)" class="tr-modified">
                      {{ cleaningModifiedText(loc) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Children (apartments) -->
            <div
              v-show="!cleaningIsCollapsed(group.parent.propertyId)"
              class="tr-children-wrapper"
            >
              <div
                v-for="child in group.children"
                :key="child.propertyId"
                class="tr-card tr-card-child"
                :class="{
                  'tr-card-focused': cleaningIsFocused(child.propertyId),
                }"
                @click="cleaningToggleFocus(child.propertyId)"
              >
                <div class="tr-card-header">
                  <div class="tr-card-address">
                    {{ child.address }}
                    <span
                      v-if="cleaningLocationCount(child)"
                      class="lm-machine-counts"
                    >
                      {{ cleaningLocationCount(child) }}
                    </span>
                    <i
                      v-if="cleaningSaving.has(child.propertyId)"
                      class="fas fa-circle-notch fa-spin tr-saving-icon"
                    ></i>
                  </div>
                  <button
                    v-if="
                      cleaningIsFocused(child.propertyId) &&
                      (child.locations || []).length > 0
                    "
                    class="lm-collapse-machines-btn"
                    title="Collapse locations"
                    @click.stop="
                      cleaningToggleLocationCollapse(child.propertyId)
                    "
                  >
                    <i
                      :class="
                        cleaningLocationsCollapsed(child.propertyId)
                          ? 'fas fa-chevron-right'
                          : 'fas fa-chevron-down'
                      "
                    ></i>
                  </button>
                  <button
                    v-if="cleaningIsFocused(child.propertyId)"
                    class="lm-add-btn"
                    title="Add location"
                    @click.stop="cleaningAddLocation(child)"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                </div>

                <div
                  v-if="child.tenants && child.tenants.length"
                  class="tr-tenants"
                >
                  <span
                    v-for="(t, ti) in child.tenants"
                    :key="ti"
                    class="tr-tenant-name"
                    :class="{ 'tr-tenant-vacant': t.name === 'Vacant' }"
                    >{{ t.name }}</span
                  >
                </div>

                <div
                  v-if="cleaningIsFocused(child.propertyId)"
                  class="tr-card-body"
                  @click.stop
                >
                  <template
                    v-if="!cleaningLocationsCollapsed(child.propertyId)"
                  >
                    <div class="lm-machines-wrapper">
                      <div
                        v-for="loc in child.locations || []"
                        :key="loc.id"
                        class="lm-machine-card"
                      >
                        <div class="lm-machine-header">
                          <select
                            class="lm-select-sm"
                            :value="loc.location_type || ''"
                            @change="
                              cleaningSetField(
                                loc,
                                'location_type',
                                $event.target.value,
                              )
                            "
                          >
                            <option value="">— Location —</option>
                            <option value="Hallways">Hallways</option>
                            <option value="Laundry Room">Laundry Room</option>
                            <option value="Basement">Basement</option>
                            <option value="Front Lobby">Front Lobby</option>
                            <option value="Back Lobby">Back Lobby</option>
                            <option value="Yard">Yard</option>
                            <option value="Exterior">Exterior</option>
                            <option value="Driveway">Driveway</option>
                            <option value="Other">Other</option>
                          </select>
                          <button
                            class="lm-delete-btn"
                            title="Remove location"
                            @click="cleaningDeleteLocation(child, loc.id)"
                          >
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </div>
                        <div class="lm-machine-row">
                          <label class="cl-field-label">Every</label>
                          <input
                            type="number"
                            class="lm-input-xs"
                            placeholder="wks"
                            min="1"
                            :value="
                              loc.clean_every_weeks != null
                                ? loc.clean_every_weeks
                                : ''
                            "
                            @change="
                              cleaningSetField(
                                loc,
                                'clean_every_weeks',
                                $event.target.value
                                  ? parseInt($event.target.value)
                                  : null,
                              )
                            "
                          />
                          <label class="cl-field-label">wks</label>
                          <span class="lm-stars">
                            <i
                              v-for="s in 5"
                              :key="s"
                              class="fas fa-star"
                              :class="{
                                'lm-star-active': loc.condition_rating >= s,
                              }"
                              @click="cleaningSetRating(loc, s)"
                            ></i>
                          </span>
                        </div>
                        <div class="lm-machine-row">
                          <textarea
                            class="tr-notes"
                            rows="2"
                            placeholder="Notes..."
                            :value="loc.notes || ''"
                            @input="
                              cleaningSetField(
                                loc,
                                'notes',
                                $event.target.value,
                              )
                            "
                          ></textarea>
                        </div>
                        <div class="lm-machine-row">
                          <input
                            type="date"
                            class="lm-input-date"
                            :value="loc.last_cleaned_on || ''"
                            @change="
                              cleaningSetField(
                                loc,
                                'last_cleaned_on',
                                $event.target.value,
                              )
                            "
                          />
                          <button
                            class="lm-coin-btn"
                            title="Set to today"
                            @click="cleaningSetCleanedToday(loc)"
                          >
                            <i class="fas fa-calendar-day"></i>
                          </button>
                          <select
                            class="lm-select-sm"
                            :value="
                              loc.last_cleaned_by != null
                                ? loc.last_cleaned_by
                                : ''
                            "
                            @change="
                              cleaningSetContractor(loc, $event.target.value)
                            "
                          >
                            <option value="">— Cleaned by —</option>
                            <option
                              v-for="c in cleaningContractors"
                              :key="c.id"
                              :value="c.id"
                            >
                              {{ c.fullName }}
                            </option>
                          </select>
                        </div>
                        <div class="lm-machine-row lm-machine-footer">
                          <button
                            class="tr-alert-btn"
                            @click="cleaningOpenAlert(child)"
                          >
                            <i class="fas fa-envelope"></i> Alert
                          </button>
                          <span
                            v-if="cleaningModifiedText(loc)"
                            class="tr-modified"
                          >
                            {{ cleaningModifiedText(loc) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== STORAGE VIEW ===== -->
    <div v-if="activeView === 'storage'" class="tr-view">
      <div class="title-bar">
        <button class="back-btn" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span style="flex: 1">Storage</span>
        <button
          class="home"
          title="Add unassigned storage"
          @click="openStorageAdd"
        >
          <i class="fas fa-plus"></i>
        </button>
        <button class="home" @click="loadStorageLeases" title="Refresh">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>

      <div class="tr-content">
        <div class="st-filter-hint">
          Use + for a locker that is not assigned to a tenant. Assigned storage
          is edited on the lease.
        </div>
        <div v-if="storageAddOpen" ref="storageAddCard" class="tr-card st-add-card">
          <input
            v-model="storageAddQuery"
            type="text"
            class="tr-filter-input"
            placeholder="Search property"
            @input="searchStorageProperties"
          />
          <div
            v-if="storagePropertyMatches.length && !storageAddProperty"
            class="st-property-matches"
          >
            <button
              v-for="property in storagePropertyMatches"
              :key="property.id"
              type="button"
              class="st-property-match"
              @click="selectStorageProperty(property)"
            >
              {{ property.display }}
            </button>
          </div>
          <div v-if="storageAddProperty" class="st-card-address">
            {{ storageAddProperty.display }}
            <button
              type="button"
              class="st-renew-btn"
              @click="storageAddProperty = null"
            >
              Change
            </button>
          </div>
          <input
            v-model="storageAddLocker"
            type="text"
            class="tr-filter-input"
            placeholder="Locker"
          />
          <input
            v-model="storageAddFee"
            type="text"
            inputmode="decimal"
            class="tr-filter-input"
            placeholder="Fee (optional)"
          />
          <div v-if="storageAddError" class="st-renew-error">
            {{ storageAddError }}
          </div>
          <div class="lm-machine-row lm-machine-footer">
            <button
              type="button"
              class="st-renew-btn"
              :disabled="storageAddSaving"
              @click="saveUnassignedStorage"
            >
              {{
                storageAddSaving
                  ? 'Saving...'
                  : storageEditingId
                    ? 'Save'
                    : 'Add'
              }}
            </button>
            <button
              type="button"
              class="st-renew-btn"
              @click="storageAddOpen = false"
            >
              Cancel
            </button>
          </div>
        </div>
        <div class="tr-filter-bar">
          <input
            ref="storageFilterRef"
            v-model="storageFilterQuery"
            type="text"
            class="tr-filter-input"
            placeholder="Search tenant, address, locker..."
          />
          <button
            v-if="storageFilterQuery"
            class="tr-clear-btn"
            @click="
              storageFilterQuery = '';
              storageFilterRef?.focus();
            "
          >
            <i class="fas fa-backspace"></i>
          </button>
        </div>

        <div v-if="storageLoading" class="tr-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading...
        </div>

        <div v-else class="tr-card-list st-card-list">
          <div v-if="filteredStorageLeases.length === 0" class="tr-empty">
            <i class="fas fa-box"></i>
            <div>No storage lockers found</div>
          </div>

          <div
            v-for="row in filteredStorageLeases"
            :key="row.storageId ? 'unit-' + row.storageId : 'lease-' + row.leaseId"
            class="tr-card"
            :class="{ 'st-card-editable': row.unassigned }"
            :title="row.unassigned ? 'Double-click to edit' : ''"
            @dblclick="editUnassignedStorage(row)"
          >
            <div class="st-card-header">
              <div class="tr-card-address">
                {{ row.tenant || 'No tenant' }}
              </div>
              <span
                class="st-status st-card-side"
                :class="'st-status-' + row.status"
                >{{ row.status }}</span
              >
            </div>
            <div class="st-card-address-row">
              <div class="st-card-address">{{ row.address }}</div>
              <div class="st-card-side">
                <button
                  v-if="row.status === 'ended'"
                  class="st-renew-btn"
                  :disabled="storageRenewingId === row.leaseId"
                  @click="renewStorageLease(row)"
                >
                  <i
                    v-if="storageRenewingId === row.leaseId"
                    class="fas fa-spinner fa-spin"
                  ></i>
                  <span v-else>Renew</span>
                </button>
              </div>
            </div>
            <div
              v-if="storageRenewError[row.leaseId]"
              class="st-renew-error"
            >
              {{ storageRenewError[row.leaseId] }}
            </div>
            <div class="st-card-row">
              <span><strong>Locker:</strong> {{ row.locker || 'N/A' }}</span>
              <span><strong>Fee:</strong> {{ row.fee }}</span>
            </div>
            <div v-if="row.term" class="st-card-term">{{ row.term }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== LANDSCAPING VIEW ===== -->
    <div v-if="activeView === 'landscaping'">
      <div class="title-bar">
        <button class="back-btn" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span style="flex: 1">Landscaping</span>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>
      <div class="coming-soon">
        <i class="fas fa-leaf coming-soon-icon"></i>
        <div class="coming-soon-text">Coming Soon</div>
      </div>
    </div>

    <!-- ===== SNOW REMOVAL VIEW ===== -->
    <div v-if="activeView === 'snow-removal'">
      <div class="title-bar">
        <button class="back-btn" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span style="flex: 1">Snow Removal</span>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>
      <div class="coming-soon">
        <i class="fas fa-snowflake coming-soon-icon"></i>
        <div class="coming-soon-text">Coming Soon</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
body {
  background-color: #f0f0f0 !important;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #f3f3f3;
  width: 100%;
  max-width: 440px;
}

.content {
  padding-top: 8px;
  padding-left: 10px;
  padding-right: 10px;
  overflow-y: auto;
  /* Hide scrollbar while keeping scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.content::-webkit-scrollbar {
  display: none;
}

.title-bar {
  background: #6e5875;
  color: #fff;
  font-size: 1.4em;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-bottom: 10px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding-bottom: 16px;
}

.stat-tile {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  min-width: 0; /* Prevent overflow on small screens */
}

.stat-tile:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.stat-header {
  background: #a898b7;
  color: white;
  padding: 8px 6px;
  font-weight: 500;
  font-size: 0.95em;
  text-align: center;
}

.stat-content {
  padding: 8px 8px;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 105px;
}

.stat-row {
  display: flex;
  margin-bottom: 4px;
  align-items: baseline;
  width: 100%;
}

.stat-row:last-child {
  margin-bottom: 0;
}

.stat-row:last-child:has(.stat-label.small) {
  position: absolute;
  bottom: 5px;
  width: calc(100% - 16px);
}

.stat-label {
  flex: 1;
  font-weight: 500;
  color: #666;
  font-size: 0.95em;
  width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-label.small {
  font-size: 0.8em;
  color: #999;
  margin-top: 4px;
  font-style: italic;
}

.stat-value {
  font-weight: bold;
  margin-right: 4px;
  width: 20%;
  min-width: 30px;
  text-align: right;
  font-size: 0.95em;
}

.stat-value-total {
  width: 40%;
  text-align: right;
}

.stat-percent {
  color: #555;
  font-size: 0.75em;
  width: 20%;
  min-width: 40px;
  text-align: right;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  color: #777;
}

.loading i {
  font-size: 2em;
  margin-bottom: 16px;
  color: #a080a8;
}

.home {
  height: 30px;
  width: 30px;
  font-size: 0.6em;
  border-radius: 15px;
  color: #fff;
  background: #907793;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
}

/* Fix for FontAwesome icons in buttons */
.title-bar button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

/* Property Photos Section */
.property-photos-section {
  margin-top: 20px;
  border-top: 1px solid #ddd;
  padding-top: 15px;
  transition: margin-top 0.3s ease;
}

/* When stats are collapsed, reduce top margin to move content up */
.stats-collapsed .property-photos-section {
  margin-top: 10px;
}

/* When there are no stats (role >= 3), adjust the photo section */
.no-stats {
  margin-top: 0;
  border-top: none;
}

.no-stats .carousel-content {
  height: 500px;
}

.no-stats .carousel-item img {
  height: 400px;
}

.section-title {
  font-size: 1.3em;
  color: #a080a8;
  margin-bottom: 10px;
  font-weight: 500;
  margin-top: -10px;
}

.section-title-inline {
  font-size: 1.1em;
  color: #a080a8;
  font-weight: 500;
  margin-right: 4px;
}

.property-coverage-bar {
  background: #f0f0f0;
  padding: 5px 8px;
  border-radius: 4px;
  margin-bottom: 10px;
}

.coverage-label {
  font-size: 0.8em;
  color: #666;
  margin-bottom: 2px;
  text-align: center;
}

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-bar-fill {
  height: 100%;
  background: #a080a8;
  border-radius: 4px;
  transition: width 0.5s ease-in-out;
}

.property-photos-content {
  position: relative;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 10px 0;
}

.search-bar input {
  flex: 1;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 16pt;
}

.clear-search {
  background: #a080a8;
  color: white;
  border: none;
  padding: 5px 7px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.9em;
}

.recent-photos-container {
  margin: 10px 0;
  transition: margin 0.3s ease;
}

/* When stats are collapsed, give more vertical space to the recent photos */
.stats-collapsed .recent-photos-container {
  margin: 20px 0 30px 0;
}

.carousel-container {
  position: relative;
  margin: 15px 0;
}

.carousel-title {
  font-size: 0.9em;
  margin-bottom: 10px;
  color: #333;
  text-align: center;
}

.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.carousel-nav:hover {
  background: rgba(255, 255, 255, 0.95);
  transform: translateY(-50%) scale(1.1);
}

.carousel-prev {
  left: 5px;
}

.carousel-next {
  right: 5px;
}

.carousel-content {
  position: relative;
  height: 250px;
  overflow: hidden;
  transition: height 0.3s ease;
}

/* When stats are collapsed, make the carousel significantly taller to utilize more available height */
.stats-collapsed .carousel-content {
  height: 600px;
}

.carousel-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease;
  display: none;
  display: flex;
  flex-direction: column;
}

.carousel-item.active {
  opacity: 1;
  display: flex;
}

.carousel-item img {
  width: 100%;
  height: 150px;
  object-fit: contain;
  border-radius: 8px;
  transition: height 0.3s ease;
}

/* When stats are collapsed, make the carousel images much larger to fill the available space */
.stats-collapsed .carousel-item img {
  height: 500px;
}

.carousel-item-details {
  padding: 8px 5px;
}

.carousel-item-caption {
  font-size: 0.75em;
  color: #737070;
  margin-bottom: 4px;
  font-weight: 500;
  margin-left: 40px;
  margin-right: 40px;
}

.carousel-item-property {
  font-size: 0.85em;
  color: #666;
  margin-left: 40px;
  margin-right: 40px;
  flex: 1;
  text-align: center;
}

.carousel-nav-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 5px;
  width: 100%;
}

.carousel-nav-bottom {
  background: rgba(160, 128, 168, 0.1);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #a080a8;
  transition: all 0.2s ease;
  position: relative; /* Add position relative */
  z-index: 20; /* Add higher z-index to ensure buttons are above other elements */
}

.carousel-nav-bottom:hover {
  background: rgba(160, 128, 168, 0.3);
  transform: scale(1.1);
}

.carousel-dots {
  text-align: center;
  margin-top: 10px;
}

.carousel-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 4px;
  background: #ddd;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.carousel-dot.active {
  background: #a080a8;
  transform: scale(1.2);
}

.no-recent-photos {
  text-align: center;
  color: #666;
  font-style: italic;
  margin: 30px 0;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
}

ul {
  font-size: 0.78em;
  color: #777;
  list-style-type: none;
  padding: 0;
  margin: 10px 0;
}

li {
  cursor: pointer;
  padding: 10px;
  border-radius: 5px;
  border: 1pt solid #ddd;
  margin-bottom: 8px;
  background: #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.badge {
  background: #a080a8;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px; /* Add explicit height */
  display: flex; /* Add display flex */
  align-items: center;
  justify-content: center;
  font-size: 0.9em;
}

.property-info {
  flex: 1;
}

.match-label {
  color: #666;
  margin-top: 10px;
  margin-bottom: 5px;
}

.no-results,
.no-photos {
  color: #666;
  font-style: italic;
  text-align: center;
  padding: 20px 0;
}

.back-button-2 {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
  color: #907793;
  font-weight: bold;
  cursor: pointer;
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  color: #666;
}

.photo-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
  margin-top: 15px;
}

.gallery-item {
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;
  border-radius: 4px;
  overflow: hidden;
}

.gallery-item:hover {
  transform: translateY(-3px);
}

.gallery-item img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: height 0.3s ease;
}

/* When stats are collapsed, make the gallery images much larger */
.stats-collapsed .gallery-item img {
  height: 420px;
}

.gallery-caption {
  font-size: 0.8em;
  text-align: center;
  margin-top: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #555;
}

.delete-photo-button {
  position: absolute;
  top: 5px;
  right: 5px;
  background: white;
  color: #a898b7;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 0 4px 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  z-index: 6;
}

/* Style the fullscreen delete button similar to the gallery delete button */
.delete-fullscreen-button {
  position: absolute;
  top: 10px; /* Adjust as needed */
  right: 10px; /* Adjust as needed */
  background: white;
  color: #a898b7;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 4px; /* Match gallery button or use 50% for circle */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  z-index: 1001; /* Ensure it's above the image */
}

/* Style the fullscreen share button */
.share-fullscreen-button {
  position: absolute;
  top: 10px;
  left: 10px;
  background: white;
  color: #a898b7;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  z-index: 1001; /* Ensure it's above the image */
}

.add-button {
  background: #a080a8;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 3px;
  margin-top: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

.edit-mode-toggle {
  margin: 15px 0;
  text-align: center;
}

.toggle-button {
  background: #a080a8;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 3px;
}

/* Dialogs */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  overflow: hidden;
}

.dialog-header {
  background: #a080a8;
  color: white;
  padding: 10px 15px;
  font-size: 1.2em;
}

.dialog-body {
  padding: 15px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  padding: 10px 15px;
  background: #f5f5f5;
}

.dialog-actions button {
  margin-left: 10px;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 3px;
}

.confirm-button {
  background: #a080a8;
  color: white;
  border: none;
}

.confirm-dialog-button {
  min-width: 60px;
  padding: 5px 9px;
  cursor: pointer;
}

.cancel-button {
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
}

.delete-confirm-button {
  background: #ff5252;
  color: white;
  border: none;
}

.caption-input {
  width: 96%;
  height: 180px;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin-top: 10px;
  font-size: 16pt;
}

.upload-instructions {
  margin-bottom: 10px;
  color: #666;
}

.upload-options {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin: 15px 0;
}

.upload-option {
  flex: 1;
}

.hidden-input {
  display: none;
}

.option-button {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #a080a8;
  color: white;
  padding: 10px;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.option-button:hover {
  background: #8a6b93;
}

.option-button i {
  font-size: 1.2em;
}

.preview-container {
  margin-top: 15px;
  text-align: center;
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.preview-container::-webkit-scrollbar {
  display: none;
}

.photo-preview-item {
  margin-bottom: 10px;
}

.photo-preview {
  max-width: 100%;
  max-height: 150px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.content-blurred {
  filter: blur(2px);
  pointer-events: none;
}

/* Photo viewer */
.photo-viewer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.photo-viewer-content {
  max-width: 95%;
  max-height: 95%;
  position: relative; /* Make this the positioning context */
  display: flex;
  flex-direction: column;
  align-items: center;
}

.full-size-photo {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  transform-origin: center;
  transition: transform 0.1s ease;
  will-change: transform;
  touch-action: none; /* Prevents browser's default touch actions */
  user-select: none; /* Prevents text selection during zoom */
}

.photo-viewer-caption {
  color: white;
  text-align: center;
  margin-top: 15px;
  font-size: 0.9em;
  background: rgba(0, 0, 0, 0.5);
  padding: 5px 10px;
  border-radius: 4px;
  max-width: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.edit-caption-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1.2em;
  margin-left: 10px;
  padding: 11px;
  border-radius: 50%;
  width: 37px;
  height: 37px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
  transition: all 0.2s ease;
}

/* Popup Styles */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  max-width: 440px;
}

.popup-content {
  background: white;
  border-radius: 8px;
  width: 98%;
  max-width: 440px;
  overflow: hidden;
  height: 89vh;
  display: flex;
  flex-direction: column;
}

.popup-header {
  background: #a080a8;
  color: white;
  padding: 10px 15px;
  font-size: 1.2em;
  flex-shrink: 0;
}

.popup-body {
  padding: 15px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.popup-footer {
  display: flex;
  justify-content: flex-end;
  padding: 10px 15px;
  background: #f5f5f5;
  flex-shrink: 0;
}

.popup-footer button {
  margin-left: 10px;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 3px;
}

.popup-footer .confirm-button {
  background: #a080a8;
  color: white;
  border: none;
}

.property-list {
  flex: 1;
  overflow-y: auto;
  padding: 5px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  min-height: 0; /* Important for flexbox scrolling */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.property-list::-webkit-scrollbar {
  display: none;
}

.property-item {
  display: flex;
  font-size: 0.9em;
  justify-content: space-between;
  align-items: center;
  padding: 2px;
  border-bottom: 1px solid #eee;
  margin-bottom: 5px;
}

/* New style for selected lease rows */
.unpaid-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;
}

.unpaid-sms-overlay {
  z-index: 2100;
}

.unpaid-sms-popup {
  height: auto;
  max-height: 89vh;
}

.unpaid-sms-body {
  overflow-y: auto;
  gap: 6px;
}

.unpaid-sms-meta {
  font-size: 0.95em;
  margin-bottom: 8px;
  line-height: 1.4;
  color: #333;
}

.unpaid-sms-label {
  font-size: 0.85em;
  font-weight: 600;
  color: #555;
  margin-top: 6px;
}

.unpaid-sms-phone {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1em;
}

.unpaid-sms-message {
  width: 100%;
  box-sizing: border-box;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95em;
  line-height: 1.35;
  resize: vertical;
  min-height: 140px;
  font-family: inherit;
}

.unpaid-sms-error {
  color: #b00020;
  font-size: 0.9em;
  margin-top: 6px;
}

.unpaid-sms-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.unpaid-sms-footer-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.ledger-link-button {
  background: #fff;
  color: #5a4a62;
  border: 1px solid #a080a8;
  border-radius: 3px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 0.95em;
}

.ledger-link-button:hover:not(:disabled) {
  background: #f5f0f7;
}

.ledger-link-button:disabled {
  opacity: 0.6;
  cursor: wait;
}

.ledger-link-button i {
  margin-left: 4px;
  font-size: 0.85em;
}

.unpaid-item:hover {
  background-color: #f5f0f7;
}

.unpaid-item.selected {
  background-color: #be98c8;
  color: white;
}

.unpaid-item.selected .payment-property,
.unpaid-item.selected .payment-tenant,
.unpaid-item.selected .payment-date,
.unpaid-item.selected .payment-amount,
.unpaid-item.selected .payment-transaction {
  color: white;
}

/* Popup Search Bar Styles */
.popup-search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
}

.popup-search-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95em;
}

.popup-search-input:focus {
  outline: none;
  border-color: #a080a8;
}

.popup-clear-search {
  background: #a080a8;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.popup-clear-search:hover {
  background: #8a6a92;
}

.popup-clear-search:active {
  background: #725a7a;
}

/* Unpaid tab content wrapper */
.unpaid-tab-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.selected-leases-summary-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end; /* Changed from center to flex-end to align with bottom */
  background-color: #f0f0f0;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.summary-total {
  display: flex;
  flex-direction: column;
  color: #444;
  font-size: 0.8em;
}

.summary-total .balance-label {
  font-size: 0.9em; /* Slightly smaller font for the second line */
  margin-top: 2px;
}

.clear-selection-button {
  background: #a080a8;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  align-self: flex-end; /* Ensures the button aligns with the bottom */
}

.property-address {
  flex: 1;
  font-weight: normal;
  padding-right: 10px;
  font-size: 0.9em;
}

.property-date,
.property-status {
  min-width: 80px;
  text-align: right;
  color: #666;
}
.property-date-label {
  min-width: 80px;
  text-align: right;
  color: #333;
  font-size: 0.8em;
}

/* Stats toggle functionality */
.stats-section {
  position: relative;
  margin-bottom: 20px;
}

.stats-toggle-btn {
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 30px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  color: #a080a8;
  transition:
    transform 0.2s ease,
    background-color 0.2s ease;
}

.stats-toggle-btn:hover {
  background: #f9f5fc;
  transform: translateX(-50%) scale(1.1);
}

.stats-toggle-btn i {
  font-size: 1.1em;
}

.show-stats-bar {
  background: #a080a8;
  color: #f7f5f7;
  padding: 12px;
  text-align: center;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  transition: background-color 0.2s ease;
}

.show-stats-bar:hover {
  background: #ede5f4;
}

/* Animation transitions */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  opacity: 1;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Payment list styles */
.payment-list {
  padding: 0;
}

.payment-item {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 8px 5px;
  border-bottom: 1px solid #eee;
  margin-bottom: 5px;
}

.payment-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.payment-property {
  font-weight: 500;
  color: #444;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
}

.payment-warning-icon {
  color: #e74c3c;
  margin-right: 5px;
  font-size: 0.9em;
}

.payment-tenant {
  font-size: 0.85em;
  color: #666;
}

.payment-details {
  text-align: right;
  margin-right: 10px;
}

.payment-date {
  font-weight: 500;
  color: #a080a8;
  margin-bottom: 2px;
}

.payment-amount {
  font-weight: bold;
  color: #444;
  margin-bottom: 2px;
}

.payment-transaction {
  font-size: 0.8em;
  color: #999;
  font-style: italic;
}

.payment-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.tab-button {
  flex: 1;
  padding: 8px 12px;
  border: none;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s ease;
}

.tab-button.active {
  background: #866e8c !important;
  color: white;
}

.tab-button:hover {
  background: #ddd;
}

.no-unpaid {
  text-align: center;
  color: #666;
  font-style: italic;
  margin-top: 20px;
}

.share-toast {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #a080a8;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  font-size: 0.9em;
}

.share-url {
  background: #f5f5f5;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ddd;
  font-family: monospace;
  word-break: break-all;
  margin: 15px 0;
  line-height: 1.4;
}

.share-tip {
  font-size: 0.85em;
  color: #666;
  font-style: italic;
  margin-top: 15px;
}

.photo-viewer-close {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8em;
  margin-top: 20px;
  text-align: center;
}

.error-details {
  background: #f5f5f5;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ddd;
  font-family: monospace;
  word-break: break-word;
  margin: 15px 0;
  line-height: 1.4;
}

input {
  outline: none;
}

/* Menu styles */
.tools-menu {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.tools-menu > .title-bar {
  flex-shrink: 0;
}

.menu-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px 16px;
  -webkit-overflow-scrolling: touch;
}

.menu-button {
  box-sizing: border-box;
  width: 90%;
  max-width: 360px;
  padding: 10px 14px;
  line-height: 1.2;
  min-height: calc((1.2em + 20px) * 1.3);
  border: none;
  border-radius: 8px;
  background: #6e5875;
  color: #fff;
  font-size: 1.25em;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
}

.menu-button:active {
  transform: scale(0.97);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.menu-icon {
  font-size: 1.05em;
  width: 1.6em;
  flex: 0 0 1.6em;
  text-align: center;
}

.back-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 0.8em;
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
}

/* Coming soon placeholder */
.coming-soon {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.coming-soon-icon {
  font-size: 5em;
  color: #ccc;
  margin-bottom: 20px;
}

.coming-soon-text {
  font-size: 1.6em;
  color: #aaa;
  font-weight: 600;
}

/* ===== CLEANING TRACKING STYLES ===== */
.cl-field-label {
  font-size: 0.8em;
  color: #666;
  white-space: nowrap;
}

/* ===== TRASH TRACKING STYLES ===== */
.tr-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sensor-view {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sensor-view > .title-bar {
  flex-shrink: 0;
}

.tr-content {
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  overflow: hidden;
  flex: 1;
}

.tr-filter-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.tr-filter-input {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.95em;
  outline: none;
}

.tr-filter-input:focus {
  border-color: #a080a8;
}

.tr-clear-btn {
  background: none;
  border: none;
  color: #999;
  font-size: 1.2em;
  cursor: pointer;
  padding: 4px 8px;
}

.tr-button-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.tr-action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 16px;
  background: #a080a8;
  color: #fff;
  font-size: 0.85em;
  cursor: pointer;
}

.tr-action-btn:active {
  opacity: 0.8;
}

.tr-action-btn-active {
  background: #7a5a82;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.3);
}

.tr-print-btn {
  width: 34px;
  min-width: 34px;
  padding: 6px 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

.tr-loading {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 1.1em;
}

.tr-card-list {
  overflow-y: auto;
  flex: 1;
  padding-bottom: 120px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tr-card-list::-webkit-scrollbar {
  display: none;
}

.tr-empty {
  text-align: center;
  padding: 60px 20px;
  color: #ccc;
  font-size: 1.2em;
}

.tr-empty i {
  font-size: 3em;
  margin-bottom: 12px;
  display: block;
}

.tr-group {
  margin-bottom: 2px;
}

.tr-card {
  background: #fff;
  border: 1px solid #d4c4d8;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 2px;
  min-height: 28px;
}

.tr-card-focused {
  outline: 2px solid #8bc34a;
  outline-offset: -1px;
}

.tr-card-child {
  border-left: 3px solid #a080a8;
  margin-left: 0;
  border-radius: 4px 6px 6px 4px;
}

.tr-children-wrapper {
  margin-left: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tr-card-header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tr-card-address {
  flex: 1;
  font-weight: 600;
  font-size: 0.95em;
}

.tr-saving-icon {
  color: #a080a8;
  margin-left: 6px;
  font-size: 0.8em;
}

.tr-collapse-btn {
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 2px 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}

.tr-child-count {
  background: #a080a8;
  color: #fff;
  font-size: 0.7em;
  padding: 1px 6px;
  border-radius: 10px;
  font-weight: 600;
}

.tr-tenants {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.tr-tenant-name {
  background: #ede4f0;
  color: #6e5875;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.8em;
}

.tr-tenant-vacant {
  background: #fff;
  color: #6e5875;
  border: 1px solid #6e5875;
}

.tr-card-body {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eee;
}

.tr-field-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.tr-label {
  font-size: 0.85em;
  color: #666;
  min-width: 55px;
  padding-top: 4px;
}

.tr-select {
  flex: 1;
  padding: 5px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9em;
  outline: none;
}

.tr-select:focus {
  border-color: #a080a8;
}

.tr-fee-input {
  width: 55px;
  padding: 5px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9em;
  outline: none;
}

.tr-fee-input:focus {
  border-color: #a080a8;
}

.tr-qty-input {
  width: 38px;
  padding: 5px 4px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9em;
  outline: none;
  text-align: center;
  margin-left: 6px;
}

.tr-qty-input:focus {
  border-color: #a080a8;
}

.tr-days-row {
  display: flex;
  gap: 2px;
  flex-wrap: nowrap;
  align-items: center;
}

.tr-bin-number {
  width: 80px;
  padding: 5px 6px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.8em;
  outline: none;
  margin-left: 6px;
}

.tr-bin-number:focus {
  border-color: #a080a8;
}

.tr-day-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  cursor: pointer;
}

.tr-day-text {
  font-size: 0.7em;
  color: #666;
}

.tr-day-label input[type='checkbox'] {
  width: 18px;
  height: 18px;
  accent-color: #a080a8;
  cursor: pointer;
}

.tr-notes {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.85em;
  resize: vertical;
  outline: none;
  font-family: inherit;
}

.tr-notes:focus {
  border-color: #a080a8;
}

.tr-alert-btn {
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background: #a080a8;
  color: #fff;
  font-size: 0.85em;
  cursor: pointer;
}

.tr-alert-btn:active {
  opacity: 0.8;
}

.tr-modified {
  font-size: 0.75em;
  color: #bbb;
  margin-top: 6px;
}

.tr-shared-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85em;
  color: #555;
  cursor: pointer;
}

.tr-shared-label input[type='checkbox'] {
  width: 16px;
  height: 16px;
  accent-color: #a080a8;
}

/* Alert popup */
.tr-alert-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
}

.tr-alert-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  width: 90%;
  max-width: 380px;
  z-index: 1001;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.tr-alert-popup h4 {
  margin: 0 0 6px 0;
  color: #6e5875;
}

.tr-alert-context {
  font-size: 0.9em;
  color: #666;
  margin-bottom: 12px;
}

.tr-alert-textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9em;
  resize: vertical;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
}

.tr-alert-textarea:focus {
  border-color: #a080a8;
}

.tr-alert-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.tr-alert-preview {
  margin-top: 10px;
  border: 1px solid #e2d8e5;
  border-radius: 6px;
  background: #faf7fb;
}

.tr-alert-preview-label {
  font-size: 0.75em;
  font-weight: 600;
  color: #6e5875;
  padding: 6px 8px;
  border-bottom: 1px solid #e2d8e5;
}

.tr-alert-preview-body {
  padding: 8px;
  font-size: 0.78em;
  color: #777;
  max-height: 160px;
  overflow-y: auto;
}

.tr-alert-preview-body :deep(p) {
  margin: 0 0 6px 0;
}

.tr-alert-preview-body :deep(p:last-child) {
  margin-bottom: 0;
}

.tr-alert-recipients {
  margin-top: 10px;
  border: 1px solid #e2d8e5;
  border-radius: 6px;
  background: #fff;
}

.tr-alert-recipients-label {
  font-size: 0.75em;
  font-weight: 600;
  color: #6e5875;
  padding: 6px 8px;
  border-bottom: 1px solid #e2d8e5;
}

.tr-alert-recipients-status,
.tr-alert-recipients-error {
  padding: 8px;
  font-size: 0.78em;
  color: #777;
}

.tr-alert-recipients-error {
  color: #b42318;
}

.tr-alert-delivery-note {
  padding: 8px 8px 0;
  font-size: 0.74em;
  color: #7b6b82;
}

.tr-alert-recipient-list {
  margin: 0;
  padding: 8px 8px 8px 24px;
  font-size: 0.78em;
  color: #555;
  max-height: 120px;
  overflow-y: auto;
}

.tr-alert-recipient-list li {
  margin-bottom: 4px;
  word-break: break-word;
}

.tr-alert-recipient-list li:last-child {
  margin-bottom: 0;
}

.tr-cancel-btn {
  padding: 6px 16px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 0.9em;
}

.tr-send-btn {
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  background: #a080a8;
  color: #fff;
  cursor: pointer;
  font-size: 0.9em;
}

.tr-send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tr-alert-results {
  margin-top: 10px;
}

.tr-result-ok {
  color: #4caf50;
  margin-bottom: 4px;
}

.tr-result-fail {
  color: #e53935;
  margin-bottom: 4px;
}

.tr-alert-fallback {
  color: #7b6b82;
  font-size: 0.85em;
  margin-bottom: 8px;
  padding: 4px 0;
}

/* ===== TRASH BIN CARD STYLES ===== */
.tr-bins-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.tr-bins-title {
  font-size: 0.85em;
  color: #6e5875;
  font-weight: 600;
}

.tr-bins-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.tr-bin-card {
  background: #f9f5fa;
  border: 1px solid #e0d4e4;
  border-radius: 8px;
  padding: 8px 10px;
}

.tr-bin-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.tr-bin-label {
  font-size: 0.8em;
  font-weight: 600;
  color: #6e5875;
}

.tr-bin-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.tr-bin-pickup-btn {
  border: 1px solid #d2c2d8;
  border-radius: 4px;
  background: #f0e7f3;
  color: #6e5875;
  cursor: pointer;
  padding: 1px 6px;
  font-size: 0.72em;
  text-transform: lowercase;
}

.tr-bin-pickup-btn:hover {
  background: #e5d8ea;
}

.tr-bin-delete-btn {
  background: none;
  border: none;
  color: #c88;
  cursor: pointer;
  padding: 2px 6px;
  font-size: 0.85em;
}

.tr-bin-delete-btn:active {
  color: #e53935;
}

/* ===== LAUNDRY TRACKING STYLES ===== */
.lm-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.lm-add-btn {
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.85em;
  cursor: pointer;
}

.lm-add-btn:active {
  background: #8a6a90;
}

.lm-collapse-machines-btn {
  background: none;
  border: none;
  color: #a080a8;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 0.8em;
}

.lm-machine-counts {
  font-size: 0.8em;
  color: #a080a8;
  margin-left: 4px;
  font-weight: 500;
}

.lm-machines-wrapper {
  margin-top: 6px;
  margin-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lm-machine-card {
  background: #faf7fb;
  border: 1px solid #d4c4d8;
  border-radius: 6px;
  padding: 8px 10px;
  border-left: 4px solid #ccc;
}

.lm-border-washer {
  border-left-color: #5b9bd5;
}

.lm-border-dryer {
  border-left-color: #e08850;
}

.lm-border-combo {
  border-left-color: #a080a8;
}

.lm-machine-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.lm-machine-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.lm-machine-footer {
  justify-content: space-between;
  margin-top: 6px;
}

.lm-input-cost {
  background: #fff;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 0.82em;
  width: 52px;
  min-width: 52px;
  max-width: 52px;
  outline: none;
  text-align: right;
}

.lm-input-cost:focus {
  border-color: #a080a8;
}

.lm-select-sm {
  background: #fff;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 0.82em;
  flex: 1;
  min-width: 0;
  outline: none;
}

.lm-select-sm:focus {
  border-color: #a080a8;
}

.lm-select-xs {
  background: #fff;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 4px 4px;
  font-size: 0.8em;
  min-width: 55px;
  outline: none;
}

.lm-select-xs:focus {
  border-color: #a080a8;
}

.lm-model-serial-wrapper {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
}

.lm-model-serial-wrapper .lm-input-sm {
  border-radius: 6px 0 0 6px;
  border-right: none;
}

.lm-model-serial-toggle {
  background: #ede4f0;
  color: #a080a8;
  border: 1px solid #ccc;
  border-radius: 0 6px 6px 0;
  padding: 4px 6px;
  font-size: 0.75em;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.lm-input-sm {
  background: #fff;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 0.82em;
  flex: 1;
  min-width: 0;
  outline: none;
}

.lm-input-sm:focus {
  border-color: #a080a8;
}

.lm-input-xs {
  background: #fff;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 0.82em;
  width: 45px;
  outline: none;
}

.lm-input-xs:focus {
  border-color: #a080a8;
}

.lm-input-date {
  background: #fff;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 4px 4px;
  font-size: 0.78em;
  flex: 1;
  min-width: 0;
  outline: none;
}

.lm-input-date:focus {
  border-color: #a080a8;
}

.lm-stars {
  display: flex;
  gap: 2px;
}

.lm-stars .fa-star {
  font-size: 0.85em;
  color: #ccc;
  cursor: pointer;
}

.lm-star-active {
  color: #ffc107 !important;
}

.lm-toggle-group {
  display: flex;
  border: 1px solid #ccc;
  border-radius: 6px;
  overflow: hidden;
}

.lm-toggle-btn {
  background: #f5f5f5;
  color: #999;
  border: none;
  padding: 3px 8px;
  font-size: 0.78em;
  cursor: pointer;
}

.lm-toggle-btn:first-child {
  border-right: 1px solid #ccc;
}

.lm-toggle-active {
  background: #a080a8;
  color: #fff;
}

.lm-coin-btn {
  background: #ede4f0;
  color: #8a6b93;
  border: 1px solid #d4c4d8;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 0.85em;
  cursor: pointer;
}

.lm-coin-btn:active {
  background: #d4c4d8;
}

.lm-delete-btn {
  background: none;
  color: #c88;
  border: none;
  padding: 2px 6px;
  font-size: 0.8em;
  cursor: pointer;
  margin-left: auto;
}

.lm-delete-btn:hover {
  color: #e55;
}

.st-filter-hint {
  font-size: 0.75em;
  color: #888;
  margin: 0 2px 8px;
  line-height: 1.3;
}

.st-card-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.st-card-address-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.st-card-address {
  flex: 1;
  color: #555;
  font-size: 0.9em;
  min-width: 0;
}

.st-card-side {
  width: 72px;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.st-card-list > .tr-card {
  flex-shrink: 0;
  padding: 4px 10px;
  overflow: visible;
}

.st-card-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 2px;
  font-size: 0.9em;
  line-height: 1.25;
  color: #444;
}

.st-card-term {
  display: block;
  margin-top: 2px;
  margin-bottom: 0;
  padding-bottom: 0;
  font-size: 0.72em;
  line-height: 1.25;
  color: #777;
}

.st-status {
  font-size: 0.75em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.st-status-current {
  color: #2e7d32;
}

.st-status-ended {
  color: #888;
}

.st-status-future {
  color: #1565c0;
}

.st-status-available {
  color: #6e5875;
}

.st-card-editable {
  cursor: pointer;
}

.st-add-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 10px 10px;
  padding: 10px;
}

.st-property-matches {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.st-property-match {
  text-align: left;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 6px 8px;
  cursor: pointer;
}

.st-renew-btn {
  background: #fff;
  color: #6e5875;
  border: 1px solid #6e5875;
  border-radius: 16px;
  padding: 2px 10px;
  font-size: 0.75em;
  font-weight: 600;
  cursor: pointer;
  line-height: 1.2;
}

.st-renew-btn:disabled {
  opacity: 0.7;
  cursor: default;
}

.st-renew-error {
  margin-top: 6px;
  color: #b00020;
  font-size: 0.8em;
}
</style>
